import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  useLayoutEffect,
} from "react";
import { toast } from "sonner";
import gsap from "gsap";
import { useNotebookSidebar } from "../context/NotebookSidebarContext";
import { useActiveNote } from "../context/ActiveNoteContext";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  ChevronRight,
  ChevronDown,
  FileText,
  Folder,
  Trash2,
  Edit3,
  Copy,
  Star,
  Search,
  Settings,
  Home,
  Hash,
  List,
  CheckSquare,
  Check,
  Type,
  Heading1,
  Heading2,
  Heading3,
  Quote,
  Code,
  Image,
  Info,
  MoreHorizontal,
  X,
  GripVertical,
  ArrowLeft,
  Moon,
  Sun,
  BookOpen,
  RefreshCw,
  PenTool,
  Menu,
  Sparkles,
  Zap,
  Send,
  Maximize2,
  Minimize2,
  ZoomIn,
  ZoomOut,
  Maximize,
  Tag,
  Play,
  Pause,
  RotateCcw,
  Clock,
  MessageSquare,
  Brain,
  Layers,
  BookMarked,
  FlaskConical,
  NotebookPen,
  Highlighter,
} from "lucide-react";
import styles from "../styles/notebook.module.css";
import "../styles/iconAnimations.css";
import {
  getAllFiles,
  saveFileToStorage,
  base64ToFile,
} from "../utils/fileStorage";
import EnhancedQuizDeck from "../components/EnhancedQuizDeck";
import NotebookAssignPanel from "../components/NotebookAssignPanel";
import SectionPanel from "../components/SectionPanel";
import PdfHighlightReader from "../components/PdfHighlightReader";
import { useTheme } from "../context/ThemeContext";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5001";

const STUDY_TOOLS = [
  {
    id: "notes",
    label: "Study Notes",
    apiType: "notes",
    icon: NotebookPen,
    color: "#818cf8",
  },
  { id: "quiz", label: "Quiz", apiType: "quiz", icon: Brain, color: "#f472b6" },
  {
    id: "flashcards",
    label: "Flashcards",
    apiType: "flashcards",
    icon: Layers,
    color: "#34d399",
  },
  {
    id: "quotes",
    label: "Key Quotes",
    apiType: "quotes",
    icon: BookMarked,
    color: "#fb923c",
  },
  {
    id: "test",
    label: "Practice Test",
    apiType: "test",
    icon: FlaskConical,
    color: "#a78bfa",
  },
];

/** OpenNote-style starter chips (icon colors); actions map to dock AI */
const NOTEBOOK_START_CHIPS = [
  {
    id: "ask",
    label: "Ask Lumi",
    Icon: Sparkles,
    color: "#22c55e",
    mode: "ask",
  },
  {
    id: "summarize",
    label: "Summarize",
    Icon: Zap,
    color: "#f472b6",
    mode: "action",
  },
  {
    id: "flashcards",
    label: "Flashcards",
    Icon: Layers,
    color: "#38bdf8",
    mode: "action",
  },
  {
    id: "quiz",
    label: "Practice quiz",
    Icon: Brain,
    color: "#fb923c",
    mode: "action",
  },
];

const MASTERY_KEY = "bramlo_flashcard_mastery_v1";

const loadMastery = () => {
  try {
    return JSON.parse(localStorage.getItem(MASTERY_KEY) || "{}");
  } catch {
    return {};
  }
};
const saveMastery = (data) => {
  try {
    localStorage.setItem(MASTERY_KEY, JSON.stringify(data));
  } catch {}
};

const cardKey = (card, i) =>
  card.front || card.question || card.term || `card_${i}`;

const NotebookFlashcards = ({ cards, onDone }) => {
  const [idx, setIdx] = React.useState(0);
  const [flipped, setFlipped] = React.useState(false);
  const [mastery, setMastery] = React.useState(() => loadMastery());
  const [finished, setFinished] = React.useState(false);

  if (!cards.length)
    return (
      <div
        style={{
          padding: "24px",
          textAlign: "center",
          color: "rgba(255,255,255,0.5)",
        }}
      >
        No flashcards generated.
      </div>
    );

  const card = cards[idx];
  const key = cardKey(card, idx);
  const known = mastery[key] === "know";
  const learning = mastery[key] === "learning";

  const mark = (status) => {
    const updated = { ...mastery, [key]: status };
    setMastery(updated);
    saveMastery(updated);
    if (idx < cards.length - 1) {
      setIdx((i) => i + 1);
      setFlipped(false);
    } else setFinished(true);
  };

  const knownCount = cards.filter(
    (c, i) => mastery[cardKey(c, i)] === "know",
  ).length;
  const pct = Math.round((knownCount / cards.length) * 100);

  if (finished) {
    const stillLearning = cards.filter(
      (c, i) => mastery[cardKey(c, i)] !== "know",
    );
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "24px",
          gap: "16px",
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: "32px" }}>
          {pct >= 80 ? "🎉" : pct >= 50 ? "💪" : "📚"}
        </div>
        <div
          style={{
            fontSize: "15px",
            fontWeight: 600,
            color: "rgba(255,255,255,0.9)",
          }}
        >
          {pct >= 80
            ? "Excellent work!"
            : pct >= 50
              ? "Good progress!"
              : "Keep studying!"}
        </div>
        <div style={{ fontSize: "12px", color: "rgba(255,255,255,0.5)" }}>
          {knownCount} / {cards.length} cards mastered ({pct}%)
        </div>
        {/* Progress bar */}
        <div
          style={{
            width: "100%",
            height: "6px",
            borderRadius: "3px",
            background: "rgba(255,255,255,0.08)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${pct}%`,
              background:
                pct >= 80 ? "#34d399" : pct >= 50 ? "#fbbf24" : "#f87171",
              borderRadius: "3px",
              transition: "width 0.5s ease",
            }}
          />
        </div>
        <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
          {stillLearning.length > 0 && (
            <button
              onClick={() => {
                setIdx(cards.indexOf(stillLearning[0]));
                setFlipped(false);
                setFinished(false);
              }}
              style={{
                padding: "7px 16px",
                borderRadius: "8px",
                border: "1px solid rgba(251,191,36,0.3)",
                background: "rgba(251,191,36,0.08)",
                color: "#fbbf24",
                cursor: "pointer",
                fontSize: "12px",
              }}
            >
              Review {stillLearning.length} remaining
            </button>
          )}
          <button
            onClick={onDone}
            style={{
              padding: "7px 16px",
              borderRadius: "8px",
              border: "1px solid rgba(139,92,246,0.3)",
              background: "rgba(139,92,246,0.1)",
              color: "#a78bfa",
              cursor: "pointer",
              fontSize: "12px",
            }}
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "16px",
        gap: "10px",
        height: "100%",
      }}
    >
      {/* Progress bar */}
      <div
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <div
          style={{
            flex: 1,
            height: "4px",
            borderRadius: "2px",
            background: "rgba(255,255,255,0.08)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${pct}%`,
              background: "#34d399",
              transition: "width 0.3s",
            }}
          />
        </div>
        <span
          style={{
            fontSize: "11px",
            color: "rgba(255,255,255,0.4)",
            whiteSpace: "nowrap",
          }}
        >
          {knownCount}/{cards.length} known
        </span>
      </div>

      <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.35)" }}>
        Card {idx + 1} of {cards.length}
      </div>

      {/* Card */}
      <div
        onClick={() => setFlipped((f) => !f)}
        style={{
          flex: 1,
          width: "100%",
          maxHeight: "260px",
          cursor: "pointer",
          borderRadius: "12px",
          background: flipped
            ? "rgba(52,211,153,0.08)"
            : known
              ? "rgba(52,211,153,0.05)"
              : learning
                ? "rgba(248,113,113,0.05)"
                : "rgba(255,255,255,0.04)",
          border: `1px solid ${flipped ? "rgba(52,211,153,0.3)" : known ? "rgba(52,211,153,0.2)" : learning ? "rgba(248,113,113,0.2)" : "rgba(255,255,255,0.1)"}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "20px",
          textAlign: "center",
          fontSize: "13.5px",
          lineHeight: "1.6",
          color: "rgba(255,255,255,0.9)",
          transition: "all 0.25s ease",
          userSelect: "none",
          position: "relative",
        }}
      >
        {(known || learning) && !flipped && (
          <span
            style={{
              position: "absolute",
              top: "8px",
              right: "10px",
              fontSize: "10px",
              color: known ? "#34d399" : "#f87171",
              fontWeight: 600,
            }}
          >
            {known ? "✓ Known" : "↻ Review"}
          </span>
        )}
        <div>
          <div
            style={{
              fontSize: "10px",
              textTransform: "uppercase",
              letterSpacing: "0.1em",
              color: flipped
                ? "rgba(52,211,153,0.7)"
                : "rgba(255,255,255,0.35)",
              marginBottom: "10px",
            }}
          >
            {flipped
              ? "Answer — tap to flip back"
              : "Question — tap to reveal answer"}
          </div>
          {flipped
            ? card.back || card.answer || card.definition
            : card.front || card.question || card.term}
        </div>
      </div>

      {/* Actions */}
      {!flipped ? (
        <div style={{ display: "flex", gap: "8px" }}>
          <button
            onClick={() => {
              setIdx((i) => Math.max(0, i - 1));
              setFlipped(false);
            }}
            disabled={idx === 0}
            style={{
              padding: "6px 14px",
              borderRadius: "6px",
              border: "1px solid rgba(255,255,255,0.12)",
              background: "transparent",
              color: "rgba(255,255,255,0.7)",
              cursor: idx === 0 ? "not-allowed" : "pointer",
              opacity: idx === 0 ? 0.4 : 1,
              fontSize: "12px",
            }}
          >
            ← Prev
          </button>
          <button
            onClick={() => setFlipped(true)}
            style={{
              padding: "6px 18px",
              borderRadius: "6px",
              border: "1px solid rgba(99,102,241,0.4)",
              background: "rgba(99,102,241,0.12)",
              color: "#818cf8",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: "12px",
            }}
          >
            Reveal Answer
          </button>
          <button
            onClick={() => {
              setIdx((i) => i + 1);
              setFlipped(false);
            }}
            disabled={idx >= cards.length - 1}
            style={{
              padding: "6px 14px",
              borderRadius: "6px",
              border: "1px solid rgba(255,255,255,0.12)",
              background: "transparent",
              color: "rgba(255,255,255,0.7)",
              cursor: idx >= cards.length - 1 ? "not-allowed" : "pointer",
              opacity: idx >= cards.length - 1 ? 0.4 : 1,
              fontSize: "12px",
            }}
          >
            Skip →
          </button>
        </div>
      ) : (
        <div style={{ display: "flex", gap: "8px" }}>
          <button
            onClick={() => mark("learning")}
            style={{
              padding: "7px 18px",
              borderRadius: "8px",
              border: "1px solid rgba(248,113,113,0.35)",
              background: "rgba(248,113,113,0.1)",
              color: "#f87171",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: "12px",
            }}
          >
            Still Learning
          </button>
          <button
            onClick={() => mark("know")}
            style={{
              padding: "7px 18px",
              borderRadius: "8px",
              border: "1px solid rgba(52,211,153,0.4)",
              background: "rgba(52,211,153,0.12)",
              color: "#34d399",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: "12px",
            }}
          >
            Got It ✓
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Study Pack Panel ─────────────────────────────────────────────────────────
const StudyPackPanel = ({ data, title, onClose }) => {
  const [tab, setTab] = React.useState("summary");
  const [exportMsg, setExportMsg] = React.useState("");

  const exportAnkiCSV = () => {
    const cards = data?.flashcards?.cards || [];
    if (!cards.length) return;
    const rows = cards.map(
      (c) =>
        `"${(c.front || "").replace(/"/g, '""')}","${(c.back || "").replace(/"/g, '""')}"`,
    );
    const csv = "Front,Back\n" + rows.join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(title || "flashcards").replace(/[^a-z0-9]/gi, "_")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setExportMsg('CSV exported! Import into Anki using "Basic" note type.');
    setTimeout(() => setExportMsg(""), 4000);
  };

  const s = data?.summary || {};
  const quiz = data?.quiz || {};
  const fc = data?.flashcards || {};

  const TABS = [
    { id: "summary", label: "Summary" },
    { id: "quiz", label: `Quiz (${(quiz.questions || []).length})` },
    { id: "flashcards", label: `Cards (${(fc.cards || []).length})` },
  ];

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div style={{ padding: "10px 14px 0", flexShrink: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "10px",
          }}
        >
          <span
            style={{
              fontSize: "12px",
              fontWeight: 600,
              color: "rgba(255,255,255,0.85)",
            }}
          >
            ⚡ {title}
          </span>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "rgba(255,255,255,0.4)",
              cursor: "pointer",
              fontSize: "16px",
              padding: "0 2px",
            }}
          >
            ×
          </button>
        </div>
        {/* Tabs */}
        <div style={{ display: "flex", gap: "2px", marginBottom: "10px" }}>
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                padding: "5px 10px",
                borderRadius: "6px",
                border: "none",
                fontSize: "11px",
                fontWeight: 600,
                cursor: "pointer",
                background:
                  tab === t.id ? "rgba(139,92,246,0.2)" : "transparent",
                color: tab === t.id ? "#c084fc" : "rgba(255,255,255,0.45)",
                borderBottom:
                  tab === t.id ? "2px solid #9333ea" : "2px solid transparent",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div style={{ flex: 1, overflow: "auto", padding: "0 14px 14px" }}>
        {tab === "summary" && (
          <div>
            <p
              style={{
                fontSize: "13px",
                lineHeight: 1.6,
                color: "rgba(255,255,255,0.75)",
                margin: "0 0 12px",
              }}
            >
              {s.overview}
            </p>
            {(s.key_points || []).length > 0 && (
              <div style={{ marginBottom: "14px" }}>
                <div
                  style={{
                    fontSize: "11px",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    color: "rgba(255,255,255,0.35)",
                    marginBottom: "6px",
                  }}
                >
                  Key Points
                </div>
                {s.key_points.map((pt, i) => (
                  <div
                    key={i}
                    style={{ display: "flex", gap: "8px", marginBottom: "6px" }}
                  >
                    <span
                      style={{
                        color: "#c084fc",
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      •
                    </span>
                    <span
                      style={{
                        fontSize: "12.5px",
                        lineHeight: 1.55,
                        color: "rgba(255,255,255,0.8)",
                      }}
                    >
                      {pt}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {(s.definitions || []).length > 0 && (
              <div style={{ marginBottom: "14px" }}>
                <div
                  style={{
                    fontSize: "11px",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    color: "rgba(255,255,255,0.35)",
                    marginBottom: "6px",
                  }}
                >
                  Definitions
                </div>
                {s.definitions.map((d, i) => (
                  <div
                    key={i}
                    style={{
                      marginBottom: "8px",
                      padding: "8px 10px",
                      background: "rgba(255,255,255,0.04)",
                      borderRadius: "8px",
                      borderLeft: "3px solid rgba(139,92,246,0.5)",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "12px",
                        fontWeight: 700,
                        color: "#c084fc",
                        marginBottom: "3px",
                      }}
                    >
                      {d.term}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "rgba(255,255,255,0.7)",
                        lineHeight: 1.5,
                      }}
                    >
                      {d.definition}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {(s.exam_tips || []).length > 0 && (
              <div>
                <div
                  style={{
                    fontSize: "11px",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    color: "rgba(255,255,255,0.35)",
                    marginBottom: "6px",
                  }}
                >
                  Exam Tips
                </div>
                {s.exam_tips.map((tip, i) => (
                  <div
                    key={i}
                    style={{ display: "flex", gap: "8px", marginBottom: "6px" }}
                  >
                    <span style={{ color: "#fbbf24", flexShrink: 0 }}>💡</span>
                    <span
                      style={{
                        fontSize: "12px",
                        lineHeight: 1.5,
                        color: "rgba(255,255,255,0.75)",
                      }}
                    >
                      {tip}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "quiz" && (
          <div>
            {(quiz.questions || []).length > 0 ? (
              <EnhancedQuizDeck quiz={quiz} onExit={() => setTab("summary")} />
            ) : (
              <p style={{ color: "rgba(255,255,255,0.4)", fontSize: "12px" }}>
                No quiz questions generated.
              </p>
            )}
          </div>
        )}

        {tab === "flashcards" && (
          <div>
            {(fc.cards || []).length > 0 ? (
              <>
                <NotebookFlashcards
                  cards={fc.cards}
                  onDone={() => setTab("summary")}
                />
                <div style={{ padding: "8px 0 0" }}>
                  <button
                    onClick={exportAnkiCSV}
                    style={{
                      width: "100%",
                      padding: "7px",
                      borderRadius: "7px",
                      border: "1px solid rgba(251,191,36,0.3)",
                      background: "rgba(251,191,36,0.08)",
                      color: "#fbbf24",
                      cursor: "pointer",
                      fontSize: "11.5px",
                      fontWeight: 600,
                    }}
                  >
                    Export to Anki (CSV)
                  </button>
                  {exportMsg && (
                    <p
                      style={{
                        fontSize: "11px",
                        color: "#34d399",
                        textAlign: "center",
                        marginTop: "6px",
                      }}
                    >
                      {exportMsg}
                    </p>
                  )}
                </div>
              </>
            ) : (
              <p style={{ color: "rgba(255,255,255,0.4)", fontSize: "12px" }}>
                No flashcards generated.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Block types
const BLOCK_TYPES = {
  TEXT: "text",
  HEADING1: "heading1",
  HEADING2: "heading2",
  HEADING3: "heading3",
  BULLET: "bullet",
  NUMBERED: "numbered",
  TODO: "todo",
  QUOTE: "quote",
  CODE: "code",
  DIVIDER: "divider",
  CALLOUT: "callout",
  TOGGLE: "toggle",
  IMAGE: "image",
  FILE: "file",
  COLUMN_GROUP: "column_group",
  COLUMN: "column",
};

const BLOCK_COLORS = [
  { name: "Default", value: "inherit" },
  { name: "Gray", value: "#9b9a97" },
  { name: "Brown", value: "#64473a" },
  { name: "Orange", value: "#d9730d" },
  { name: "Yellow", value: "#dfab01" },
  { name: "Green", value: "#0f7b6c" },
  { name: "Blue", value: "#0b6e99" },
  { name: "Purple", value: "#6940a5" },
  { name: "Pink", value: "#ad1a72" },
  { name: "Red", value: "#e03e3e" },
];

const BLOCK_BG_COLORS = [
  { name: "Default", value: "transparent" },
  { name: "Gray background", value: "rgba(155, 154, 151, 0.15)" },
  { name: "Brown background", value: "rgba(100, 71, 58, 0.15)" },
  { name: "Orange background", value: "rgba(217, 115, 13, 0.15)" },
  { name: "Yellow background", value: "rgba(223, 171, 1, 0.15)" },
  { name: "Green background", value: "rgba(15, 123, 108, 0.15)" },
  { name: "Blue background", value: "rgba(11, 110, 153, 0.15)" },
  { name: "Purple background", value: "rgba(105, 64, 165, 0.15)" },
  { name: "Pink background", value: "rgba(173, 26, 114, 0.15)" },
  { name: "Red background", value: "rgba(224, 62, 62, 0.15)" },
];

// Color themes for notebooks (Notion-style palette)
const NOTEBOOK_COLORS = [
  { name: "Default", primary: "var(--text-primary)", bg: "transparent" },
  { name: "Gray", primary: "#9b9a97", bg: "rgba(155, 154, 151, 0.1)" },
  { name: "Brown", primary: "#64473a", bg: "rgba(100, 71, 58, 0.1)" },
  { name: "Orange", primary: "#d9730d", bg: "rgba(217, 115, 13, 0.1)" },
  { name: "Yellow", primary: "#dfab01", bg: "rgba(223, 171, 1, 0.1)" },
  { name: "Green", primary: "#0f7b6c", bg: "rgba(15, 123, 108, 0.1)" },
  { name: "Blue", primary: "#0b6e99", bg: "rgba(11, 110, 153, 0.1)" },
  { name: "Purple", primary: "#6940a5", bg: "rgba(105, 64, 165, 0.1)" },
  { name: "Pink", primary: "#ad1a72", bg: "rgba(173, 26, 114, 0.1)" },
  { name: "Red", primary: "#e03e3e", bg: "rgba(224, 62, 62, 0.1)" },
];

// Generate unique IDs
const generateId = () =>
  `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

/** Last N turns for /api/chat `context` (Groq message array) */
const MAX_NOTEBOOK_CHAT_CONTEXT = 24;

function trimChatContext(messages) {
  if (!messages?.length) return [];
  return messages
    .slice(-MAX_NOTEBOOK_CHAT_CONTEXT)
    .map(({ role, content }) => ({ role, content: String(content ?? "") }));
}

function extractChatReply(data) {
  if (!data || typeof data !== "object") return "";
  const r = data.reply;
  if (r && typeof r === "object" && typeof r.text === "string") return r.text;
  if (typeof r === "string") return r;
  if (typeof data.response === "string") return data.response;
  if (typeof data.message === "string") return data.message;
  return "";
}

// Default block
const createBlock = (type = BLOCK_TYPES.TEXT, content = "") => ({
  id: generateId(),
  type,
  content,
  checked: false,
  children: [],
  collapsed: false,
  color: "default",
  backgroundColor: "default",
});

function blockHasMeaningfulContent(block) {
  if (!block) return false;
  if ((block.content || "").trim()) return true;
  if (block.children?.length)
    return block.children.some(blockHasMeaningfulContent);
  return false;
}

// Default page with icon instead of emoji
const createPage = (title = "Untitled", parentId = null, color = 0) => ({
  id: generateId(),
  title,
  iconType: "file",
  emoji: "📄",
  color: color,
  parentId,
  blocks: [createBlock()],
  properties: {
    status: "Not started",
    tags: [],
    date: new Date().toISOString().split("T")[0],
  },
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  isFavorite: false,
});

// Folder types for student organization
const FOLDER_TYPES = {
  SUBJECT: "subject",
  CLASS: "class",
  UNIT: "unit",
  FOLDER: "folder",
};

const FOLDER_ICONS = {
  subject: "📚",
  class: "🎓",
  unit: "📖",
  folder: "📁",
};

// Default folder
const createFolder = (
  name = "New Folder",
  parentId = null,
  folderType = FOLDER_TYPES.FOLDER,
) => ({
  id: generateId(),
  name,
  parentId,
  isFolder: true,
  folderType,
  createdAt: new Date().toISOString(),
  collapsed: false,
});

const NotebookPage = ({
  onBackToChat,
  hideSidebar = false,
  onGenerateKnowledgeGraph,
  onOpenLumiWithMessage,
  onQueueLumiDraft,
} = {}) => {
  const navigate = useNavigate();
  const { setContent: setNotebookSidebarContent } = useNotebookSidebar();
  const {
    setActiveNote: publishActiveNote,
    clearActiveNote: clearPublishedActiveNote,
    registerInserter: registerNoteInserter,
  } = useActiveNote();
  const { isDarkMode: darkMode, toggleTheme } = useTheme();

  // Notebook structure
  const [notebooks, setNotebooks] = useState(() => {
    const saved = localStorage.getItem("notebook_data");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return [];
      }
    }
    // Default notebook structure
    return [
      {
        ...createFolder("My Notebooks"),
        id: "root",
        children: [createPage("Welcome to Notebooks", "root", 0)],
      },
    ];
  });

  const [activePage, setActivePage] = useState(null);
  const activePageRef = useRef(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSlashMenu, setShowSlashMenu] = useState(false);
  const [slashMenuPosition, setSlashMenuPosition] = useState({ x: 0, y: 0 });
  const [activeBlockId, setActiveBlockId] = useState(null);
  const [selectedBlockIds, setSelectedBlockIds] = useState([]);
  const [lastSelectedId, setLastSelectedId] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [editingTitle, setEditingTitle] = useState(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [addingMarginNoteId, setAddingMarginNoteId] = useState(null);
  const [marginNoteInput, setMarginNoteInput] = useState("");
  const [draggedBlockId, setDraggedBlockId] = useState(null);
  const [dragOverBlockId, setDragOverBlockId] = useState(null);
  const [dropPosition, setDropPosition] = useState(null); // 'before', 'after', or 'child'
  const [saveStatus, setSaveStatus] = useState("saved"); // 'saved', 'saving'
  const [recentlyVisitedIds, setRecentlyVisitedIds] = useState(() => {
    const saved = localStorage.getItem("recently_visited_notebooks");
    return saved ? JSON.parse(saved) : [];
  });
  const [slashFilter, setSlashFilter] = useState("");
  const [trash, setTrash] = useState(() => {
    const saved = localStorage.getItem("notebook_trash");
    return saved ? JSON.parse(saved) : [];
  });
  const [showTrash, setShowTrash] = useState(false);
  // null | { type: 'handle'|'convert'|'blockColor', pos: {x,y}, blockId: string }
  const [activeMenu, setActiveMenu] = useState(null);
  const [showQuickSwitcher, setShowQuickSwitcher] = useState(false);
  const [switcherSearchQuery, setSwitcherSearchQuery] = useState("");
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [showAskBar, setShowAskBar] = useState(false);
  const [dockTab, setDockTab] = useState("docs"); // 'docs' or 'study'
  const [dockWidth, setDockWidth] = useState(() => {
    const saved = localStorage.getItem("notebook_dockWidth");
    const parsed = saved ? JSON.parse(saved) : 420;
    const vw = window.innerWidth;
    const maxW = vw <= 768 ? vw : vw <= 1024 ? 340 : vw <= 1200 ? 400 : 600;
    return Math.min(Math.max(parsed, 300), maxW);
  });
  const isDraggingDock = useRef(false);
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(0);
  const [dockDocuments, setDockDocuments] = useState([]);
  const [activeDockDocId, setActiveDockDocId] = useState(null);
  const [activeDockDocUrl, setActiveDockDocUrl] = useState("");
  const [showProperties, setShowProperties] = useState(false);
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [aiInput, setAiInput] = useState("");
  const [aiPreview, setAiPreview] = useState(null); // { prompt, response } for accept/reject
  const [aiBarExpanded, setAiBarExpanded] = useState(false);
  const [showPageSettings, setShowPageSettings] = useState(false);
  // Contextual AI panel state (non-chat, card-based)
  const [aiContextLoading, setAiContextLoading] = useState(false);
  const [aiContextQuery, setAiContextQuery] = useState("");
  const [aiDockMessages, setAiDockMessages] = useState([]);
  const [recentSearches, setRecentSearches] = useState(() => {
    const saved = localStorage.getItem("notebook_recentSearches");
    return saved ? JSON.parse(saved) : [];
  });
  const [sidebarSections, setSidebarSections] = useState(() => {
    const saved = localStorage.getItem("notebook_sidebarSections");
    return saved
      ? JSON.parse(saved)
      : {
          favorites: true,
          private: true,
        };
  });
  const [studyMode, setStudyMode] = useState(false);
  const [studyTimer, setStudyTimer] = useState(25 * 60);
  const [studyTimerRunning, setStudyTimerRunning] = useState(false);
  const [pomodoroMode, setPomodoroMode] = useState("work"); // 'work' | 'break' | 'longBreak'
  const [pomodoroSession, setPomodoroSession] = useState(1);
  const [showPomodoro, setShowPomodoro] = useState(true);
  const [pomodoroSettings] = useState({
    workDuration: 25,
    breakDuration: 5,
    longBreakDuration: 15,
    sessionsBeforeLongBreak: 4,
  });
  const [docZoom, setDocZoom] = useState(100);
  const [showAnnotations, setShowAnnotations] = useState(false);
  const [annotations, setAnnotations] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("bramlo_annotations_v1") || "{}");
    } catch {
      return {};
    }
  });
  const [newAnnotation, setNewAnnotation] = useState({ quote: "", note: "" });
  const [addingAnnotation, setAddingAnnotation] = useState(false);
  const [docSearchOpen, setDocSearchOpen] = useState(false);
  const [docSearchQuery, setDocSearchQuery] = useState("");
  const docSearchInputRef = useRef(null);
  const [docSortMode, setDocSortMode] = useState(() => {
    try {
      return localStorage.getItem("notebook_docSortMode") || "recent";
    } catch {
      return "recent";
    }
  });
  const [docLastOpened, setDocLastOpened] = useState(() => {
    try {
      const saved = localStorage.getItem("notebook_docLastOpened");
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });
  const [showTagInput, setShowTagInput] = useState(false);
  const [tagInputValue, setTagInputValue] = useState("");
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [commandQuery, setCommandQuery] = useState("");
  const [selectionTagMenu, setSelectionTagMenu] = useState(null);
  // { text, x, y, start, end, blockId } - includes selection positions for actual text manipulation

  // Study tools state
  const [studyToolsExpanded, setStudyToolsExpanded] = useState(false);
  const [expandedStudyTool, setExpandedStudyTool] = useState(null);
  const [studyContent, setStudyContent] = useState(null); // { type, data, title }
  const [isGeneratingStudy, setIsGeneratingStudy] = useState(false);
  const [studyDocs, setStudyDocs] = useState([]);

  const sidebarHoverTimeoutRef = useRef(null);
  const activeTextareaRef = useRef(null);

  useEffect(() => {
    try {
      localStorage.setItem("notebook_docSortMode", docSortMode);
    } catch {}
  }, [docSortMode]);

  useEffect(() => {
    try {
      localStorage.setItem(
        "notebook_docLastOpened",
        JSON.stringify(docLastOpened || {}),
      );
    } catch {}
  }, [docLastOpened]);

  // ── PDF quick-annotate (from PdfHighlightReader selection bubble) ──────────
  const handlePdfAnnotate = useCallback(
    (quote) => {
      if (!activeDockDocId || !quote) return;
      const ann = {
        id: `ann_${Date.now()}`,
        quote: quote.slice(0, 800),
        note: "",
        createdAt: new Date().toISOString(),
      };
      const updated = {
        ...annotations,
        [activeDockDocId]: [...(annotations[activeDockDocId] || []), ann],
      };
      setAnnotations(updated);
      setShowAnnotations(true);
      try {
        localStorage.setItem("bramlo_annotations_v1", JSON.stringify(updated));
      } catch {}
    },
    [activeDockDocId, annotations],
  );

  // ── Annotation helpers ─────────────────────────────────────────────────────
  const currentDocAnnotations = useMemo(
    () => (activeDockDocId ? annotations[activeDockDocId] || [] : []),
    [annotations, activeDockDocId],
  );

  const saveAnnotation = useCallback(() => {
    if (!activeDockDocId || !newAnnotation.note.trim()) return;
    const ann = {
      id: `ann_${Date.now()}`,
      quote: newAnnotation.quote.trim(),
      note: newAnnotation.note.trim(),
      createdAt: new Date().toISOString(),
    };
    const updated = {
      ...annotations,
      [activeDockDocId]: [...(annotations[activeDockDocId] || []), ann],
    };
    setAnnotations(updated);
    try {
      localStorage.setItem("bramlo_annotations_v1", JSON.stringify(updated));
    } catch {}
    setNewAnnotation({ quote: "", note: "" });
    setAddingAnnotation(false);
  }, [activeDockDocId, annotations, newAnnotation]);

  const deleteAnnotation = useCallback(
    (annId) => {
      if (!activeDockDocId) return;
      const updated = {
        ...annotations,
        [activeDockDocId]: (annotations[activeDockDocId] || []).filter(
          (a) => a.id !== annId,
        ),
      };
      setAnnotations(updated);
      try {
        localStorage.setItem("bramlo_annotations_v1", JSON.stringify(updated));
      } catch {}
    },
    [activeDockDocId, annotations],
  );

  useEffect(() => {
    if (!activeDockDocId) return;
    const now = Date.now();
    setDocLastOpened((prev) => ({
      ...(prev || {}),
      [activeDockDocId]: now,
    }));
  }, [activeDockDocId]);

  const filteredDockDocuments = useMemo(() => {
    const q = (docSearchQuery || "").trim().toLowerCase();
    if (!q) return dockDocuments;
    return dockDocuments.filter((d) =>
      (d.name || "").toLowerCase().includes(q),
    );
  }, [dockDocuments, docSearchQuery]);

  const sortedDockDocuments = useMemo(() => {
    const docs = [...(filteredDockDocuments || [])];
    if (docSortMode === "az") {
      docs.sort((a, b) =>
        (a?.name || "").localeCompare(b?.name || "", undefined, {
          sensitivity: "base",
        }),
      );
      return docs;
    }
    docs.sort((a, b) => {
      const at = docLastOpened?.[a?.id] || 0;
      const bt = docLastOpened?.[b?.id] || 0;
      if (bt !== at) return bt - at;
      return (a?.name || "").localeCompare(b?.name || "", undefined, {
        sensitivity: "base",
      });
    });
    return docs;
  }, [filteredDockDocuments, docSortMode, docLastOpened]);

  useEffect(() => {
    if (!docSearchOpen) return;
    const t = setTimeout(() => docSearchInputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [docSearchOpen]);
  const commandInputRef = useRef(null);
  const docUploadInputRef = useRef(null);
  const studyUploadRef = useRef(null);
  const docIframeRef = useRef(null);
  const dockRef = useRef(null);
  const currentDragWidth = useRef(dockWidth);
  const aiDockThreadRef = useRef(null);
  const aiDockInputRef = useRef(null);
  const askBarInputRef = useRef(null);
  const aiInlineSurfaceRef = useRef(null);
  const aiInlineDropRef = useRef(null);
  const aiInlineDropInnerRef = useRef(null);
  const topBarAiHostRef = useRef(null);
  const topBarAskPillRef = useRef(null);
  /** Snapshot of ask-pill (or host) when opening AI inline */
  const lastAiOpenOriginRef = useRef(null);
  const closeAiModalAnimRef = useRef(() => {});

  // Pomodoro timer effect with work/break cycles
  useEffect(() => {
    if (!studyTimerRunning || studyTimer <= 0) return;
    const interval = setInterval(() => {
      setStudyTimer((prev) => {
        if (prev <= 1) {
          setStudyTimerRunning(false);
          // Play notification sound
          const audio = new Audio(
            "data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleR0Kk9PEtopWEwBiprRyCCVvs6/WkVw3GWesu9LTjz0DBZ3I0MhyOgcJSK3RyI1RMAI=",
          );
          audio.volume = 0.5;
          audio.play().catch(() => {});

          // Auto-switch modes
          if (pomodoroMode === "work") {
            const newSession = pomodoroSession + 1;
            setPomodoroSession(newSession);
            if (newSession % pomodoroSettings.sessionsBeforeLongBreak === 0) {
              setPomodoroMode("longBreak");
              return pomodoroSettings.longBreakDuration * 60;
            } else {
              setPomodoroMode("break");
              return pomodoroSettings.breakDuration * 60;
            }
          } else {
            setPomodoroMode("work");
            return pomodoroSettings.workDuration * 60;
          }
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [
    studyTimerRunning,
    studyTimer,
    pomodoroMode,
    pomodoroSession,
    pomodoroSettings,
  ]);

  useEffect(() => {
    localStorage.setItem("notebook_dockWidth", JSON.stringify(dockWidth));
  }, [dockWidth]);

  // Auto-scroll AI dock thread to bottom on new messages
  useEffect(() => {
    if (aiDockThreadRef.current) {
      aiDockThreadRef.current.scrollTop = aiDockThreadRef.current.scrollHeight;
    }
  }, [aiDockMessages, aiContextLoading]);

  const handleDockResizeStart = useCallback(
    (e) => {
      e.preventDefault();
      isDraggingDock.current = true;
      dragStartX.current = e.clientX;
      dragStartWidth.current = dockWidth;
      currentDragWidth.current = dockWidth;
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      // Disable CSS transition during drag for zero-lag response
      if (dockRef.current) dockRef.current.style.transition = "none";
      const onMove = (ev) => {
        if (!isDraggingDock.current) return;
        const delta = dragStartX.current - ev.clientX;
        const vw = window.innerWidth;
        const maxW =
          vw <= 1024
            ? Math.min(340, vw * 0.5)
            : vw <= 1200
              ? Math.min(400, vw * 0.45)
              : Math.min(600, vw * 0.5);
        const next = Math.min(
          Math.max(dragStartWidth.current + delta, 280),
          maxW,
        );
        currentDragWidth.current = next;
        // Direct DOM update — no React re-render during drag
        if (dockRef.current) dockRef.current.style.width = next + "px";
      };
      const onUp = () => {
        isDraggingDock.current = false;
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        if (dockRef.current) dockRef.current.style.transition = "";
        // Commit final width to React state (single re-render on release)
        setDockWidth(currentDragWidth.current);
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [dockWidth],
  );

  // Load docs from storage for study tools
  useEffect(() => {
    const load = () => setStudyDocs(getAllFiles());
    load();
    window.addEventListener("filesUpdated", load);
    return () => window.removeEventListener("filesUpdated", load);
  }, []);

  // One-click: generate a full study pack from the current notebook page
  const handleStudyThisPage = useCallback(async () => {
    if (!activePage) return;
    const pageText = [
      activePage.title,
      ...activePage.blocks.map((b) => b.content),
    ]
      .filter(Boolean)
      .join("\n\n");
    if (pageText.trim().split(/\s+/).length < 20) {
      alert(
        "Write more content on this page first (at least a few sentences) before generating a study pack.",
      );
      return;
    }

    setStudyContent({
      type: "loading",
      title: `Building study pack for "${activePage.title}"…`,
    });
    setShowAiPanel(true);
    setDockTab("study");

    try {
      const res = await fetch(`${API_BASE}/api/study_pack`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text: pageText,
          title: activePage.title || "Study Material",
          num_questions: 6,
          num_cards: 8,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setStudyContent({
          type: "error",
          title: "Generation failed",
          message:
            err.error || "Server error. Make sure the backend is running.",
        });
        return;
      }
      const data = await res.json();
      // Store the full pack — the study tab will render tabs for each section
      setStudyContent({
        type: "studypack",
        data,
        title: `Study Pack: ${activePage.title}`,
      });

      // Track this in study analytics
      try {
        const sessions = JSON.parse(
          localStorage.getItem("bramlo_study_sessions") || "[]",
        );
        sessions.push({
          date: new Date().toISOString(),
          type: "study_pack",
          topic: activePage.title,
          duration: 0,
        });
        if (sessions.length > 500) sessions.splice(0, sessions.length - 500);
        localStorage.setItem("bramlo_study_sessions", JSON.stringify(sessions));
      } catch {}
    } catch (e) {
      setStudyContent({
        type: "error",
        title: "Error",
        message: e.message || "Network error. Is the backend running?",
      });
    }
  }, [activePage]);

  const handleStudyGenerate = useCallback(async (tool, doc) => {
    setIsGeneratingStudy(true);
    setStudyContent({ type: "loading", title: `Generating ${tool.label}…` });
    setShowAiPanel(true);
    setDockTab("study");

    try {
      const docFile = base64ToFile(doc.content, doc.name, doc.type);
      const formData = new FormData();
      formData.append("document", docFile);
      formData.append("type", tool.apiType);

      const response = await fetch(`${API_BASE}/api/enhanced_generate`, {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        setStudyContent({
          type: tool.id,
          data: data.result,
          title: `${tool.label}: ${doc.name}`,
        });
      } else {
        setStudyContent({
          type: "error",
          title: "Generation failed",
          message: "The server returned an error. Try again.",
        });
      }
    } catch (e) {
      setStudyContent({ type: "error", title: "Error", message: e.message });
    } finally {
      setIsGeneratingStudy(false);
    }
  }, []);

  // Keyboard shortcuts (ESC, ⌘+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // ESC to exit focus mode or close command palette
      if (e.key === "Escape") {
        if (showCommandPalette) {
          setShowCommandPalette(false);
          setCommandQuery("");
        } else if (showAskBar) {
          e.preventDefault();
          setShowAskBar(false);
        } else if (studyMode) {
          setStudyMode(false);
          setStudyTimerRunning(false);
          if (document.fullscreenElement) {
            document.exitFullscreen();
          }
        }
      }
      // ⌘+K or Ctrl+K to open command palette
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setShowCommandPalette((prev) => !prev);
        setCommandQuery("");
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [studyMode, showCommandPalette, showAiPanel, dockTab, showAskBar]);

  useEffect(() => {
    if (!showAiPanel) return;
    const files = getAllFiles();
    setDockDocuments(files);
  }, [showAiPanel]);

  useEffect(() => {
    if (!activeDockDocId) {
      setActiveDockDocUrl("");
      return;
    }

    const doc = dockDocuments.find((d) => d.id === activeDockDocId);
    if (!doc) {
      setActiveDockDocUrl("");
      return;
    }

    const file = base64ToFile(doc.content, doc.name, doc.type);
    const url = URL.createObjectURL(file);
    setActiveDockDocUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [activeDockDocId, dockDocuments]);

  const blockRefs = useRef({});
  const slashMenuRef = useRef(null);

  const slashMenuOptions = useMemo(
    () => [
      {
        category: "AI Tools",
        type: "ask_ai",
        icon: (
          <Sparkles size={18} style={{ color: "var(--accent-secondary)" }} />
        ),
        label: "Ask AI",
        shortcut: "ai",
        description: "Generate content with AI",
      },
      {
        category: "AI Tools",
        type: "ai_brainstorm",
        icon: <Zap size={18} style={{ color: "var(--accent-secondary)" }} />,
        label: "Brainstorm",
        shortcut: "brain",
        description: "Generate ideas",
      },
      {
        category: "AI Tools",
        type: "ai_summarize",
        icon: (
          <FileText size={18} style={{ color: "var(--accent-secondary)" }} />
        ),
        label: "Summarize Page",
        shortcut: "sum",
        description: "Summarize whole page",
      },
      {
        category: "AI Tools",
        type: "ai_quiz",
        icon: (
          <CheckSquare size={18} style={{ color: "var(--accent-secondary)" }} />
        ),
        label: "Generate Quiz",
        shortcut: "quiz",
        description: "Create quiz from content",
      },
      {
        category: "AI Tools",
        type: "ai_flashcards",
        icon: <Copy size={18} style={{ color: "var(--accent-secondary)" }} />,
        label: "Generate Flashcards",
        shortcut: "flash",
        description: "Create flashcards",
      },
      {
        category: "AI Tools",
        type: "ai_test",
        icon: (
          <FileText size={18} style={{ color: "var(--accent-secondary)" }} />
        ),
        label: "Generate Test",
        shortcut: "test",
        description: "Create practice test",
      },
      {
        category: "AI Tools",
        type: "ai_quote",
        icon: <Quote size={18} style={{ color: "var(--accent-secondary)" }} />,
        label: "Generate Quote",
        shortcut: "gquote",
        description: "Inspirational quote",
      },

      {
        category: "Text",
        type: BLOCK_TYPES.TEXT,
        icon: <Type size={18} />,
        label: "Text",
        shortcut: "text",
        description: "Plain text",
      },
      {
        category: "Text",
        type: BLOCK_TYPES.HEADING1,
        icon: <Heading1 size={18} />,
        label: "Heading 1",
        shortcut: "h1",
        description: "Large section heading",
      },
      {
        category: "Text",
        type: BLOCK_TYPES.HEADING2,
        icon: <Heading2 size={18} />,
        label: "Heading 2",
        shortcut: "h2",
        description: "Medium section heading",
      },
      {
        category: "Text",
        type: BLOCK_TYPES.HEADING3,
        icon: <Heading3 size={18} />,
        label: "Heading 3",
        shortcut: "h3",
        description: "Small section heading",
      },

      {
        category: "Lists",
        type: BLOCK_TYPES.BULLET,
        icon: <List size={18} />,
        label: "Bulleted list",
        shortcut: "bullet",
        description: "Simple bullet list",
      },
      {
        category: "Lists",
        type: BLOCK_TYPES.NUMBERED,
        icon: <List size={18} />,
        label: "Numbered list",
        shortcut: "num",
        description: "List with numbers",
      },
      {
        category: "Lists",
        type: BLOCK_TYPES.TODO,
        icon: <CheckSquare size={18} />,
        label: "To-do list",
        shortcut: "todo",
        description: "Track tasks",
      },
      {
        category: "Lists",
        type: BLOCK_TYPES.TOGGLE,
        icon: <ChevronRight size={18} />,
        label: "Toggle list",
        shortcut: "toggle",
        description: "Expandable content",
      },

      {
        category: "Media",
        type: BLOCK_TYPES.IMAGE,
        icon: <Image size={18} />,
        label: "Image",
        shortcut: "img",
        description: "Upload or embed",
      },
      {
        category: "Media",
        type: BLOCK_TYPES.FILE,
        icon: <FileText size={18} />,
        label: "File",
        shortcut: "file",
        description: "Upload a file",
      },

      {
        category: "Blocks",
        type: BLOCK_TYPES.QUOTE,
        icon: <Quote size={18} />,
        label: "Quote",
        shortcut: "quote",
        description: "Capture a quote",
      },
      {
        category: "Blocks",
        type: BLOCK_TYPES.CODE,
        icon: <Code size={18} />,
        label: "Code",
        shortcut: "code",
        description: "Code snippet",
      },
      {
        category: "Blocks",
        type: BLOCK_TYPES.CALLOUT,
        icon: <Info size={18} />,
        label: "Callout",
        shortcut: "call",
        description: "Highlight text",
      },
      {
        category: "Blocks",
        type: BLOCK_TYPES.DIVIDER,
        icon: <MoreHorizontal size={18} />,
        label: "Divider",
        shortcut: "div",
        description: "Visual separation",
      },

      {
        category: "Colors",
        type: "color_red",
        icon: (
          <div
            style={{
              width: 16,
              height: 16,
              borderRadius: 4,
              background: "#ef4444",
            }}
          />
        ),
        label: "Red",
        shortcut: "red",
        description: "Red text color",
      },
      {
        category: "Colors",
        type: "color_orange",
        icon: (
          <div
            style={{
              width: 16,
              height: 16,
              borderRadius: 4,
              background: "#f97316",
            }}
          />
        ),
        label: "Orange",
        shortcut: "orange",
        description: "Orange text color",
      },
      {
        category: "Colors",
        type: "color_yellow",
        icon: (
          <div
            style={{
              width: 16,
              height: 16,
              borderRadius: 4,
              background: "#eab308",
            }}
          />
        ),
        label: "Yellow",
        shortcut: "yellow",
        description: "Yellow text color",
      },
      {
        category: "Colors",
        type: "color_green",
        icon: (
          <div
            style={{
              width: 16,
              height: 16,
              borderRadius: 4,
              background: "#22c55e",
            }}
          />
        ),
        label: "Green",
        shortcut: "green",
        description: "Green text color",
      },
      {
        category: "Colors",
        type: "color_blue",
        icon: (
          <div
            style={{
              width: 16,
              height: 16,
              borderRadius: 4,
              background: "#3b82f6",
            }}
          />
        ),
        label: "Blue",
        shortcut: "blue",
        description: "Blue text color",
      },
      {
        category: "Colors",
        type: "color_purple",
        icon: (
          <div
            style={{
              width: 16,
              height: 16,
              borderRadius: 4,
              background: "#a855f7",
            }}
          />
        ),
        label: "Purple",
        shortcut: "purple",
        description: "Purple text color",
      },

      {
        category: "Highlights",
        type: "highlight_red",
        icon: (
          <div
            style={{
              width: 16,
              height: 16,
              borderRadius: 4,
              background: "rgba(239, 68, 68, 0.3)",
              border: "1px solid #ef4444",
            }}
          />
        ),
        label: "Red highlight",
        shortcut: "hred",
        description: "Red background",
      },
      {
        category: "Highlights",
        type: "highlight_yellow",
        icon: (
          <div
            style={{
              width: 16,
              height: 16,
              borderRadius: 4,
              background: "rgba(234, 179, 8, 0.3)",
              border: "1px solid #eab308",
            }}
          />
        ),
        label: "Yellow highlight",
        shortcut: "hyellow",
        description: "Yellow background",
      },
      {
        category: "Highlights",
        type: "highlight_green",
        icon: (
          <div
            style={{
              width: 16,
              height: 16,
              borderRadius: 4,
              background: "rgba(34, 197, 94, 0.3)",
              border: "1px solid #22c55e",
            }}
          />
        ),
        label: "Green highlight",
        shortcut: "hgreen",
        description: "Green background",
      },
      {
        category: "Highlights",
        type: "highlight_blue",
        icon: (
          <div
            style={{
              width: 16,
              height: 16,
              borderRadius: 4,
              background: "rgba(59, 130, 246, 0.3)",
              border: "1px solid #3b82f6",
            }}
          />
        ),
        label: "Blue highlight",
        shortcut: "hblue",
        description: "Blue background",
      },
      {
        category: "Highlights",
        type: "highlight_purple",
        icon: (
          <div
            style={{
              width: 16,
              height: 16,
              borderRadius: 4,
              background: "rgba(168, 85, 247, 0.3)",
              border: "1px solid #a855f7",
            }}
          />
        ),
        label: "Purple highlight",
        shortcut: "hpurple",
        description: "Purple background",
      },
    ],
    [],
  );

  const [slashMenuSelectedIndex, setSlashMenuSelectedIndex] = useState(0);

  // --- RECURSIVE HELPERS ---

  const getFlatBlockList = useCallback((blocks, depth = 0, result = []) => {
    if (!blocks) return result;
    blocks.forEach((block) => {
      result.push({ ...block, depth });
      if (block.children && !block.collapsed) {
        getFlatBlockList(block.children, depth + 1, result);
      }
    });
    return result;
  }, []);

  const findPageById = useCallback((items, id) => {
    for (const item of items) {
      if (item.id === id && !item.isFolder) return item;
      if (item.children) {
        const found = findPageById(item.children, id);
        if (found) return found;
      }
    }
    return null;
  }, []);

  const findItemById = useCallback((items, id) => {
    for (const item of items) {
      if (item.id === id) return item;
      if (item.children) {
        const found = findItemById(item.children, id);
        if (found) return found;
      }
    }
    return null;
  }, []);

  const updateItemInTree = useCallback((items, id, updates) => {
    return items.map((item) => {
      if (item.id === id) {
        return { ...item, ...updates };
      }
      if (item.children) {
        return {
          ...item,
          children: updateItemInTree(item.children, id, updates),
        };
      }
      return item;
    });
  }, []);

  const appendBlocksToActivePage = useCallback(
    (blocksToAdd) => {
      if (!activePage || !blocksToAdd?.length) return;
      const updatedBlocks = [...(activePage.blocks || []), ...blocksToAdd];
      const updatedPage = { ...activePage, blocks: updatedBlocks };
      setActivePage(updatedPage);
      setNotebooks((prev) =>
        updateItemInTree(prev, activePage.id, updatedPage),
      );
    },
    [activePage, updateItemInTree],
  );

  const handleAnnotationInsertToNote = useCallback(
    (ann) => {
      if (!ann || !activePage) return;
      const ts = Date.now();
      const parts = [];
      if (ann.quote?.trim()) {
        parts.push({
          id: `b-${ts}-q`,
          type: "quote",
          content: ann.quote.trim(),
          children: [],
          collapsed: false,
          color: null,
          backgroundColor: null,
        });
      }
      if (ann.note?.trim()) {
        parts.push(createBlock(BLOCK_TYPES.TEXT, ann.note.trim()));
      }
      if (!parts.length) return;
      appendBlocksToActivePage(parts);
    },
    [activePage, appendBlocksToActivePage],
  );

  const deleteItemFromTree = useCallback((items, id) => {
    return items.filter((item) => {
      if (item.id === id) return false;
      if (item.children) {
        item.children = deleteItemFromTree(item.children, id);
      }
      return true;
    });
  }, []);

  const getNotebookItemLabel = useCallback((item) => {
    return (item.isFolder ? item.name : item.title || "Untitled").toLowerCase();
  }, []);

  const sortNotebookItems = useCallback(
    (items = []) => {
      return [...items].sort((a, b) => {
        if (a.isFolder && !b.isFolder) return -1;
        if (!a.isFolder && b.isFolder) return 1;
        return getNotebookItemLabel(a).localeCompare(getNotebookItemLabel(b));
      });
    },
    [getNotebookItemLabel],
  );

  const addItemToFolder = useCallback(
    (items, parentId, newItem) => {
      // If parentId is 'root', add to the root folder (first item with id 'root')
      // or if no root exists, add directly to items array
      let found = false;
      const result = items.map((item) => {
        if (item.id === parentId) {
          found = true;
          return {
            ...item,
            children: sortNotebookItems([...(item.children || []), newItem]),
            collapsed: false,
          };
        }
        if (item.children) {
          const updatedChildren = addItemToFolder(
            item.children,
            parentId,
            newItem,
          );
          if (updatedChildren !== item.children) {
            found = true;
            return { ...item, children: updatedChildren };
          }
        }
        return item;
      });

      // If parentId wasn't found and it's 'root', add to first folder or create structure
      if (!found && parentId === "root") {
        // Find first folder and add there
        for (let i = 0; i < result.length; i++) {
          if (result[i].isFolder) {
            result[i] = {
              ...result[i],
              children: sortNotebookItems([
                ...(result[i].children || []),
                newItem,
              ]),
              collapsed: false,
            };
            return result;
          }
        }
        // No folder found, add item directly to root level
        return sortNotebookItems([...result, newItem]);
      }

      return result;
    },
    [sortNotebookItems],
  );

  const updateBlockInList = useCallback((blocks, id, updates) => {
    return blocks.map((block) => {
      if (block.id === id) {
        return { ...block, ...updates, updatedAt: new Date().toISOString() };
      }
      if (block.children && block.children.length > 0) {
        return {
          ...block,
          children: updateBlockInList(block.children, id, updates),
        };
      }
      return block;
    });
  }, []);

  const deleteBlockFromList = useCallback((blocks, id) => {
    return blocks
      .filter((block) => block.id !== id)
      .map((block) => {
        if (block.children && block.children.length > 0) {
          return {
            ...block,
            children: deleteBlockFromList(block.children, id),
          };
        }
        return block;
      });
  }, []);

  const addBlockToList = useCallback(
    (blocks, targetId, newBlock, asChild = false) => {
      return blocks.reduce((acc, block) => {
        if (block.id === targetId) {
          if (asChild) {
            acc.push({
              ...block,
              children: [...(block.children || []), newBlock],
              collapsed: false,
            });
          } else {
            acc.push(block);
            acc.push(newBlock);
          }
        } else if (block.children && block.children.length > 0) {
          acc.push({
            ...block,
            children: addBlockToList(
              block.children,
              targetId,
              newBlock,
              asChild,
            ),
          });
        } else {
          acc.push(block);
        }
        return acc;
      }, []);
    },
    [],
  );

  const findPreviousBlock = useCallback(
    (blocks, id) => {
      const flatList = getFlatBlockList(blocks);
      const index = flatList.findIndex((b) => b.id === id);
      return index > 0 ? flatList[index - 1] : null;
    },
    [getFlatBlockList],
  );

  const findNextBlock = useCallback(
    (blocks, id) => {
      const flatList = getFlatBlockList(blocks);
      const index = flatList.findIndex((b) => b.id === id);
      return index < flatList.length - 1 ? flatList[index + 1] : null;
    },
    [getFlatBlockList],
  );

  const filterNotebooks = useCallback((items, query) => {
    if (!query) return items;
    const lowerQuery = query.toLowerCase();

    return items.reduce((acc, item) => {
      const matchesTitle = (item.isFolder ? item.name : item.title)
        .toLowerCase()
        .includes(lowerQuery);

      let filteredChildren = [];
      if (item.children) {
        filteredChildren = filterNotebooks(item.children, query);
      }

      if (matchesTitle || filteredChildren.length > 0) {
        acc.push({
          ...item,
          children: filteredChildren,
          collapsed: query ? false : item.collapsed,
        });
      }

      return acc;
    }, []);
  }, []);

  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
  }, []);

  const filteredNotebooks = useMemo(
    () => filterNotebooks(notebooks, searchQuery),
    [notebooks, searchQuery, filterNotebooks],
  );

  const clearSelection = useCallback(() => {
    setSelectedBlockIds([]);
    setLastSelectedId(null);
  }, []);

  const toggleBlockSelection = useCallback(
    (blockId, isShift, isCmd) => {
      if (isShift && lastSelectedId) {
        const flatList = getFlatBlockList(activePage.blocks);
        const lastIdx = flatList.findIndex((b) => b.id === lastSelectedId);
        const currIdx = flatList.findIndex((b) => b.id === blockId);

        if (lastIdx !== -1 && currIdx !== -1) {
          const start = Math.min(lastIdx, currIdx);
          const end = Math.max(lastIdx, currIdx);
          const rangeIds = flatList.slice(start, end + 1).map((b) => b.id);
          setSelectedBlockIds((prev) =>
            Array.from(new Set([...prev, ...rangeIds])),
          );
        }
      } else if (isCmd) {
        setSelectedBlockIds((prev) =>
          prev.includes(blockId)
            ? prev.filter((id) => id !== blockId)
            : [...prev, blockId],
        );
      } else {
        setSelectedBlockIds([blockId]);
      }
      setLastSelectedId(blockId);
    },
    [activePage, lastSelectedId, getFlatBlockList],
  );

  // --- EFFECTS ---

  useEffect(() => {
    localStorage.setItem(
      "notebook_sidebarSections",
      JSON.stringify(sidebarSections),
    );
  }, [sidebarSections]);

  useEffect(() => {
    localStorage.setItem("notebook_trash", JSON.stringify(trash));
  }, [trash]);

  useEffect(() => {
    setSaveStatus("saving");
    localStorage.setItem("notebook_data", JSON.stringify(notebooks));
    const timeout = setTimeout(() => setSaveStatus("saved"), 1000);
    return () => clearTimeout(timeout);
  }, [notebooks]);

  useEffect(() => {
    activePageRef.current = activePage;
  }, [activePage]);

  const collectBlockText = useCallback((blocks, acc = []) => {
    blocks.forEach((block) => {
      if (block.content) acc.push(block.content);
      if (block.children && block.children.length > 0) {
        collectBlockText(block.children, acc);
      }
    });
    return acc;
  }, []);

  const getNoteContext = useCallback(
    (blocks, maxChars = 800) => {
      const text = collectBlockText(blocks).join("\n").trim();
      if (!text) return "";
      return text.length > maxChars ? `${text.slice(0, maxChars)}…` : text;
    },
    [collectBlockText],
  );

  const buildAiBlocks = useCallback((text) => {
    return text
      .split(/\n{2,}/)
      .map((part) => part.trim())
      .filter(Boolean)
      .map((part) => {
        if (part.startsWith("```")) {
          return {
            ...createBlock(BLOCK_TYPES.CODE, part.replace(/```/g, "").trim()),
            aiGenerated: true,
          };
        }
        if (part.startsWith(">")) {
          return {
            ...createBlock(
              BLOCK_TYPES.QUOTE,
              part.replace(/^>\s?/gm, "").trim(),
            ),
            aiGenerated: true,
          };
        }
        return {
          ...createBlock(BLOCK_TYPES.TEXT, part),
          aiGenerated: true,
        };
      });
  }, []);

  const insertBlocksAfterTarget = useCallback(
    (blocks, targetId, newBlocks) => {
      if (!newBlocks.length) return blocks;
      if (!targetId) return [...blocks, ...newBlocks];
      let updated = blocks;
      let insertAfterId = targetId;
      newBlocks.forEach((block) => {
        updated = addBlockToList(updated, insertAfterId, block, false);
        insertAfterId = block.id;
      });
      return updated;
    },
    [addBlockToList],
  );

  const insertAssistantMarkdownIntoNotes = useCallback(
    (markdownText) => {
      if (!activePage || !markdownText?.trim()) return;
      const stamp = new Date().toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      });
      const header = createBlock(BLOCK_TYPES.HEADING3, `AI · ${stamp}`);
      const bodyBlocks = buildAiBlocks(markdownText.trim());
      const combined = [header, ...bodyBlocks];
      const updatedBlocks = insertBlocksAfterTarget(
        activePage.blocks,
        activeBlockId,
        combined,
      );
      const updatedPage = { ...activePage, blocks: updatedBlocks };
      setActivePage(updatedPage);
      setNotebooks((prev) =>
        updateItemInTree(prev, activePage.id, updatedPage),
      );
      const focusBlock = combined[combined.length - 1];
      setTimeout(() => {
        const el = blockRefs.current[focusBlock?.id];
        if (el) el.focus();
      }, 50);
    },
    [
      activePage,
      activeBlockId,
      buildAiBlocks,
      insertBlocksAfterTarget,
      updateItemInTree,
    ],
  );

  /**
   * Direct insertion path used by the chat panel (HomePage) via
   * ActiveNoteContext. Mirrors insertAssistantMarkdownIntoNotes but accepts a
   * custom label so the heading reads "AI · <label> · <stamp>".
   */
  const insertLabeledMarkdown = useCallback(
    (markdownText, label = "Insert") => {
      const page = activePageRef.current;
      if (!page || !markdownText?.trim()) return;
      const stamp = new Date().toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      });
      const header = {
        ...createBlock(BLOCK_TYPES.HEADING3, `AI · ${label} · ${stamp}`),
        aiGenerated: true,
      };
      const bodyBlocks = buildAiBlocks(markdownText.trim());
      const combined = [header, ...bodyBlocks];
      const targetId =
        activeBlockId ||
        (page.blocks.length > 0 ? page.blocks[page.blocks.length - 1].id : null);
      const updatedBlocks = insertBlocksAfterTarget(
        page.blocks,
        targetId,
        combined,
      );
      const updatedPage = { ...page, blocks: updatedBlocks };
      setActivePage(updatedPage);
      setNotebooks((prev) => updateItemInTree(prev, page.id, updatedPage));
      toast.success(`"${label}" inserted into ${page.title || "this note"}`);
    },
    [
      activeBlockId,
      buildAiBlocks,
      insertBlocksAfterTarget,
      updateItemInTree,
    ],
  );

  // ── Publish the active note + register the inserter so chat can use them ──
  useEffect(() => {
    if (!activePage) {
      clearPublishedActiveNote();
      return;
    }
    publishActiveNote({
      id: activePage.id,
      title: activePage.title || "Untitled note",
      // Lazy: chat pulls fresh context only when it needs it
      getContext: (maxChars = 2000) => {
        const page = activePageRef.current;
        if (!page) return "";
        return getNoteContext(page.blocks, maxChars);
      },
    });
  }, [activePage, publishActiveNote, clearPublishedActiveNote, getNoteContext]);

  useEffect(() => {
    const unregister = registerNoteInserter(insertLabeledMarkdown);
    return () => {
      if (typeof unregister === "function") unregister();
    };
  }, [registerNoteInserter, insertLabeledMarkdown]);

  const handleAiAction = useCallback(
    async (action, rawText) => {
      if (!activePage || rawText == null) return;
      const text = String(rawText);
      if (!text.trim()) return;
      setIsAiLoading(true);

      const noteContext = getNoteContext(activePage.blocks, 4000);
      const excerpt = text.length > 12000 ? `${text.slice(0, 12000)}\n…` : text;

      let message;
      if (action === "Summarize Page") {
        message = `Summarize the following student notes clearly. Use ## headings and bullet lists where helpful.\n\n---\nNOTES\n---\n${excerpt}`;
      } else if (action === "Explain") {
        message = `Explain the following excerpt clearly for a student. Define jargon, give intuition, and connect ideas.\n\n---\nEXCERPT\n---\n${excerpt}${noteContext ? `\n\n---\nOther text on this page (context)\n---\n${noteContext}` : ""}`;
      } else if (action === "Improve") {
        message = `Rewrite the following excerpt for clarity and academic tone. Preserve the meaning. Return only the improved text.\n\n---\nEXCERPT\n---\n${excerpt}${noteContext ? `\n\n---\nPage context\n---\n${noteContext}` : ""}`;
      } else if (action === "Summarize") {
        message = `Summarize the following excerpt in short bullets.\n\n---\nEXCERPT\n---\n${excerpt}`;
      } else {
        message = `Help a student with the following. Be concise and clear.\n\n---\nCONTENT\n---\n${excerpt}${noteContext ? `\n\n---\nPage notes\n---\n${noteContext}` : ""}`;
      }

      try {
        const response = await fetch(`${API_BASE}/api/chat`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ message, context: [], stream: false }),
        });
        const data = await response.json();
        let reply = extractChatReply(data);
        if (!response.ok) {
          reply =
            typeof data?.error === "string"
              ? data.error
              : "Could not generate a response.";
        }
        if (!reply?.trim()) {
          reply = "No content returned. Check the API or try again.";
        }
        const aiBlocks = buildAiBlocks(reply.trim());
        const updatedBlocks = insertBlocksAfterTarget(
          activePage.blocks,
          activeBlockId,
          aiBlocks,
        );
        const updatedPage = { ...activePage, blocks: updatedBlocks };
        setActivePage(updatedPage);
        setNotebooks((prev) =>
          updateItemInTree(prev, activePage.id, updatedPage),
        );
        const focusBlock = aiBlocks[aiBlocks.length - 1];
        setTimeout(() => {
          const el = blockRefs.current[focusBlock?.id];
          if (el) el.focus();
        }, 50);
      } catch {
        const errBlocks = buildAiBlocks(
          "**AI error**\n\nCould not reach the server. Is the backend running?",
        );
        const updatedBlocks = insertBlocksAfterTarget(
          activePage.blocks,
          activeBlockId,
          errBlocks,
        );
        setActivePage({ ...activePage, blocks: updatedBlocks });
        setNotebooks((prev) =>
          updateItemInTree(prev, activePage.id, {
            ...activePage,
            blocks: updatedBlocks,
          }),
        );
      } finally {
        setIsAiLoading(false);
      }
    },
    [
      activePage,
      activeBlockId,
      getNoteContext,
      buildAiBlocks,
      insertBlocksAfterTarget,
      updateItemInTree,
    ],
  );

  const handleAskAiSubmit = async (blockId, prompt) => {
    if (!prompt.trim() || !activePage) return;
    setIsAiLoading(true);

    const noteContext = getNoteContext(activePage.blocks, 6000);
    const message = `The student asked about their notes: "${prompt.trim()}"\n\nAnswer helpfully. If needed, use this page content:\n\n---\n${noteContext || "(No other text on this page)"}\n---`;

    try {
      const response = await fetch(`${API_BASE}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message, context: [], stream: false }),
      });
      const data = await response.json();
      let reply = extractChatReply(data);
      if (!response.ok) {
        reply =
          typeof data?.error === "string"
            ? data.error
            : "Could not generate a response.";
      }
      if (!reply?.trim()) {
        reply = "No content returned.";
      }
      const aiBlocks = buildAiBlocks(reply.trim());
      const previous = findPreviousBlock(activePage.blocks, blockId);
      const blocksWithoutPrompt = deleteBlockFromList(
        activePage.blocks,
        blockId,
      );
      const updatedBlocks = insertBlocksAfterTarget(
        blocksWithoutPrompt,
        previous?.id || null,
        aiBlocks,
      );
      const updatedPage = { ...activePage, blocks: updatedBlocks };
      setActivePage(updatedPage);
      setNotebooks((prev) =>
        updateItemInTree(prev, activePage.id, updatedPage),
      );
      const focusBlock = aiBlocks[aiBlocks.length - 1];
      setTimeout(() => {
        const el = blockRefs.current[focusBlock?.id];
        if (el) el.focus();
      }, 50);
    } catch {
      const previous = findPreviousBlock(activePage.blocks, blockId);
      const blocksWithoutPrompt = deleteBlockFromList(
        activePage.blocks,
        blockId,
      );
      const aiBlocks = buildAiBlocks("Could not reach the AI server.");
      const updatedBlocks = insertBlocksAfterTarget(
        blocksWithoutPrompt,
        previous?.id || null,
        aiBlocks,
      );
      setActivePage({ ...activePage, blocks: updatedBlocks });
      setNotebooks((prev) =>
        updateItemInTree(prev, activePage.id, {
          ...activePage,
          blocks: updatedBlocks,
        }),
      );
    } finally {
      setIsAiLoading(false);
    }
  };

  const handleOpenDock = useCallback(() => {
    setShowAiPanel(true);
    setDockTab("docs");
  }, []);

  const openNotebookAskBar = useCallback(() => {
    setShowAskBar((prev) => {
      if (!prev) setTimeout(() => askBarInputRef.current?.focus(), 60);
      return !prev;
    });
  }, []);

  useEffect(() => {
    window.__notebookToast = (msg) => toast.success(msg);
    return () => {
      delete window.__notebookToast;
    };
  }, []);

  useEffect(() => {
    const checkPendingInsert = () => {
      try {
        const raw = localStorage.getItem("notebook_pending_insert");
        if (!raw) return;
        const payload = JSON.parse(raw);
        if (!payload?.content || !activePage) return;
        localStorage.removeItem("notebook_pending_insert");
        const stamp = new Date().toLocaleString(undefined, {
          dateStyle: "medium",
          timeStyle: "short",
        });
        const header = {
          ...createBlock(BLOCK_TYPES.HEADING3, `AI · ${payload.label || "Insert"} · ${stamp}`),
          aiGenerated: true,
        };
        const bodyBlocks = buildAiBlocks(payload.content.trim());
        const combined = [header, ...bodyBlocks];
        const targetId =
          activeBlockId ||
          (activePage.blocks.length > 0
            ? activePage.blocks[activePage.blocks.length - 1].id
            : null);
        const updatedBlocks = insertBlocksAfterTarget(
          activePage.blocks,
          targetId,
          combined,
        );
        const updatedPage = { ...activePage, blocks: updatedBlocks };
        setActivePage(updatedPage);
        setNotebooks((prev) =>
          updateItemInTree(prev, activePage.id, updatedPage),
        );
        toast.success(`"${payload.label || "AI content"}" inserted into this note`);
      } catch (e) {
        console.error("Failed to insert pending content:", e);
      }
    };

    checkPendingInsert();
    window.addEventListener("focus", checkPendingInsert);
    return () => window.removeEventListener("focus", checkPendingInsert);
  }, [activePage, activeBlockId, buildAiBlocks, insertBlocksAfterTarget, updateItemInTree]);

  const closeAiModalAnimated = useCallback(() => {
    const surface = aiInlineSurfaceRef.current;
    const drop = aiInlineDropRef.current;
    const orig = lastAiOpenOriginRef.current;
    const pw = orig?.width ?? 280;

    if (!surface) {
      setShowAiPanel(false);
      return;
    }

    gsap.killTweensOf([surface, drop].filter(Boolean));

    const tl = gsap.timeline({
      onComplete: () => setShowAiPanel(false),
    });
    if (drop) {
      tl.to(
        drop,
        {
          height: 0,
          duration: 0.28,
          ease: "power2.in",
        },
        0,
      );
    }
    tl.to(
      surface,
      {
        width: pw,
        duration: 0.32,
        ease: "power2.inOut",
      },
      drop ? "-=0.12" : 0,
    );
  }, []);

  closeAiModalAnimRef.current = closeAiModalAnimated;

  const isAiFloatingModal = false;
  const dockSideOpen = showAiPanel;

  useLayoutEffect(() => {
    if (!isAiFloatingModal) return;
    const surface = aiInlineSurfaceRef.current;
    const drop = aiInlineDropRef.current;
    const innerEl = aiInlineDropInnerRef.current;
    const orig = lastAiOpenOriginRef.current;
    if (!surface || !drop || !orig) return;

    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const pw = orig.width;
    const finalW = Math.min(480, vw - 24);

    gsap.killTweensOf([surface, drop]);

    gsap.set(surface, {
      width: pw,
      overflow: "hidden",
      borderRadius: 12,
    });

    gsap.set(drop, { height: 0, overflow: "hidden" });

    const runOpen = () => {
      const maxInner = Math.min(480, vh - 96);
      const innerH = innerEl
        ? Math.min(innerEl.scrollHeight || 0, maxInner)
        : 320;

      // One continuous gesture: widen and unfold together (not sequential).
      const duration = 0.5;
      const ease = "power3.out";

      gsap
        .timeline({ defaults: { duration, ease } })
        .to(surface, { width: finalW }, 0)
        .to(drop, { height: innerH }, 0);
    };

    requestAnimationFrame(runOpen);
  }, [isAiFloatingModal]);

  useEffect(() => {
    if (!isAiFloatingModal) return;
    const onMouseDown = (e) => {
      if (aiInlineSurfaceRef.current?.contains(e.target)) return;
      closeAiModalAnimRef.current();
    };
    document.addEventListener("mousedown", onMouseDown, true);
    return () => document.removeEventListener("mousedown", onMouseDown, true);
  }, [isAiFloatingModal]);

  const isNotebookPageSparse = useMemo(() => {
    if (!activePage?.blocks?.length) return true;
    return !activePage.blocks.some(blockHasMeaningfulContent);
  }, [activePage]);

  // Contextual AI: chat-thread in dock; sends prior turns as `context` for /api/chat
  const handleContextualAI = useCallback(
    async (action, customQuery = "") => {
      if (!activePage) return;
      setAiContextLoading(true);

      const pageTrunc = activePage.blocks
        .map((b) => b.content)
        .join("\n")
        .slice(0, 8000);

      const labelMap = {
        summarize: "Summary",
        quiz: "Quiz",
        outline: "Outline",
        improve: "Improved Notes",
        gaps: "Knowledge Gaps",
        flashcards: "Flashcards",
        explain: "Explanation",
      };

      const promptMap = {
        summarize: `Summarize the following notes clearly and concisely. Use markdown with ## headings and bullet points.\n\n${pageTrunc}`,
        explain: `Explain the key concepts in these notes simply and clearly, as if teaching a student for the first time. Use ## headings and short paragraphs.\n\n${pageTrunc}`,
        quiz: `Create 5 multiple-choice quiz questions from these notes. Format with Q1, Q2, etc. with options A-D and mark the correct answer.\n\n${pageTrunc}`,
        outline: `Create a structured study outline based on these notes. Use ## for main sections and ### for subsections.\n\n${pageTrunc}`,
        improve: `Improve the clarity and completeness of these notes. Return the improved version with markdown formatting.\n\n${pageTrunc}`,
        gaps: `Identify knowledge gaps or missing topics in these notes that a student should review. Use ## for main points.\n\n${pageTrunc}`,
        flashcards: `Create 6 flashcard pairs from these notes. Format each as: **Term**: Definition\n\n${pageTrunc}`,
      };

      let prompt;

      if (action === "custom") {
        const q = customQuery.trim();
        if (!q) { setAiContextLoading(false); return; }
        prompt = pageTrunc ? `${q}\n\n---\nPage notes:\n${pageTrunc}` : q;
      } else {
        prompt = promptMap[action];
      }

      if (!prompt) {
        setAiContextLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_BASE}/api/chat`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            message: prompt,
            stream: false,
          }),
        });
        const data = await response.json();
        const text = extractChatReply(data);
        if (response.ok && text.trim()) {
          // Insert directly into notes instead of showing in chat
          const stamp = new Date().toLocaleString(undefined, {
            dateStyle: "medium",
            timeStyle: "short",
          });
          const actionLabel = labelMap[action] || action;
          const header = {
            ...createBlock(BLOCK_TYPES.HEADING3, `AI · ${actionLabel} · ${stamp}`),
            aiGenerated: true,
          };
          const bodyBlocks = buildAiBlocks(text.trim());
          const combined = [header, ...bodyBlocks];
          const targetId = activeBlockId || (activePage.blocks.length > 0 ? activePage.blocks[activePage.blocks.length - 1].id : null);
          const updatedBlocks = insertBlocksAfterTarget(
            activePage.blocks,
            targetId,
            combined,
          );
          const updatedPage = { ...activePage, blocks: updatedBlocks };
          setActivePage(updatedPage);
          setNotebooks((prev) =>
            updateItemInTree(prev, activePage.id, updatedPage),
          );
          const focusBlock = combined[combined.length - 1];
          setTimeout(() => {
            const el = blockRefs.current[focusBlock?.id];
            if (el) el.focus();
          }, 50);
        } else {
          const err =
            typeof data?.error === "string"
              ? data.error
              : text || "Could not generate a response. Try again.";
          // Insert error into notes
          const stamp = new Date().toLocaleString(undefined, {
            dateStyle: "medium",
            timeStyle: "short",
          });
          const actionLabel = labelMap[action] || action;
          const header = {
            ...createBlock(BLOCK_TYPES.HEADING3, `AI · ${actionLabel} · Error · ${stamp}`),
            aiGenerated: true,
          };
          const errBlock = {
            ...createBlock(BLOCK_TYPES.TEXT, err),
            aiGenerated: true,
          };
          const combined = [header, errBlock];
          const updatedBlocks = insertBlocksAfterTarget(
            activePage.blocks,
            activeBlockId,
            combined,
          );
          const updatedPage = { ...activePage, blocks: updatedBlocks };
          setActivePage(updatedPage);
          setNotebooks((prev) =>
            updateItemInTree(prev, activePage.id, updatedPage),
          );
        }
      } catch {
        // Insert network error into notes
        const stamp = new Date().toLocaleString(undefined, {
          dateStyle: "medium",
          timeStyle: "short",
        });
        const actionLabel = labelMap[action] || action;
        const header = {
          ...createBlock(BLOCK_TYPES.HEADING3, `AI · ${actionLabel} · Network Error · ${stamp}`),
          aiGenerated: true,
        };
        const errBlock = {
          ...createBlock(BLOCK_TYPES.TEXT, "Network error. Make sure the server is running."),
          aiGenerated: true,
        };
        const combined = [header, errBlock];
        const updatedBlocks = insertBlocksAfterTarget(
          activePage.blocks,
          activeBlockId,
          combined,
        );
        const updatedPage = { ...activePage, blocks: updatedBlocks };
        setActivePage(updatedPage);
        setNotebooks((prev) =>
          updateItemInTree(prev, activePage.id, updatedPage),
        );
      } finally {
        setAiContextLoading(false);
      }
    },
    [activePage, activeBlockId, buildAiBlocks, insertBlocksAfterTarget, updateItemInTree],
  );

  // Focus bar + selection toolbar: same thread as dock AI; opens AI tab
  const handleAiChat = useCallback(
    async (prompt) => {
      if (!prompt.trim()) return;
      setIsAiLoading(true);
      setShowAiPanel(true);
      setDockTab("ai");

      const pageText = activePage
        ? [activePage.title, ...activePage.blocks.map((b) => b.content)]
            .filter(Boolean)
            .join("\n")
        : "";
      const pageTrunc =
        pageText.length > 14000
          ? `${pageText.slice(0, 14000)}\n…[truncated]`
          : pageText;

      const enrichedPrompt = pageTrunc
        ? `${prompt}\n\n---\nPage notes:\n${pageTrunc}`
        : prompt;

      const prior = trimChatContext(aiDockMessages);
      setAiDockMessages((prev) => [
        ...prev,
        { role: "user", content: prompt.trim() },
      ]);

      try {
        const response = await fetch(`${API_BASE}/api/chat`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            message: enrichedPrompt,
            context: prior,
            stream: false,
          }),
        });
        const data = await response.json();
        const text = extractChatReply(data);
        if (response.ok && text.trim()) {
          setAiDockMessages((prev) => [
            ...prev,
            { role: "assistant", content: text },
          ]);
        } else {
          const err =
            typeof data?.error === "string"
              ? data.error
              : text ||
                "Could not generate a response. Make sure the server is running.";
          setAiDockMessages((prev) => [
            ...prev,
            { role: "assistant", content: err, error: true },
          ]);
        }
      } catch {
        setAiDockMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content:
              "Network error. Make sure the backend server is running on port 5001.",
            error: true,
          },
        ]);
      } finally {
        setIsAiLoading(false);
      }
    },
    [activePage, aiDockMessages],
  );

  const handleDocUpload = useCallback(
    async (file) => {
      if (!file) return;
      try {
        await saveFileToStorage(file);
        const files = getAllFiles();
        setDockDocuments(files);
        const justSaved = files.find((f) => f.name === file.name);
        if (justSaved) setActiveDockDocId(justSaved.id);
      } catch {
        // ignore
      }
    },
    [setDockDocuments, setActiveDockDocId],
  );

  useEffect(() => {
    localStorage.setItem(
      "recently_visited_notebooks",
      JSON.stringify(recentlyVisitedIds),
    );
  }, [recentlyVisitedIds]);

  useEffect(() => {
    if (!activePage && notebooks.length > 0) {
      const findFirstPage = (items) => {
        for (const item of items) {
          if (!item.isFolder) return item;
          if (item.children) {
            const found = findFirstPage(item.children);
            if (found) return found;
          }
        }
        return null;
      };
      const firstPage = findFirstPage(notebooks);
      if (firstPage) setActivePage(firstPage);
    }
  }, [notebooks, activePage]);

  // --- HANDLERS ---

  const toggleSidebarSection = useCallback((section) => {
    setSidebarSections((prev) => ({ ...prev, [section]: !prev[section] }));
  }, []);

  const handleDuplicateBlock = useCallback(
    (blockId) => {
      if (!activePage) return;
      const blockToDuplicate = (function findBlock(blocks, id) {
        for (const b of blocks) {
          if (b.id === id) return b;
          if (b.children) {
            const found = findBlock(b.children, id);
            if (found) return found;
          }
        }
        return null;
      })(activePage.blocks, blockId);

      if (blockToDuplicate) {
        const duplicate = JSON.parse(JSON.stringify(blockToDuplicate));
        // Re-generate IDs recursively
        const reassignIds = (b) => {
          b.id = generateId();
          if (b.children) b.children.forEach(reassignIds);
        };
        reassignIds(duplicate);

        const updatedBlocks = addBlockToList(
          activePage.blocks,
          blockId,
          duplicate,
          false,
        );
        const updatedPage = { ...activePage, blocks: updatedBlocks };
        setActivePage(updatedPage);
        setNotebooks((prev) =>
          updateItemInTree(prev, activePage.id, updatedPage),
        );
      }
      setActiveMenu(null);
    },
    [activePage, addBlockToList, updateItemInTree],
  );

  const handlePageSelect = useCallback(
    (page) => {
      if (!page) {
        setActivePage(null);
        clearSelection();
        return;
      }
      if (page.id === activePage?.id) return;
      setActivePage(page);
      clearSelection();

      // Update recently visited
      setRecentlyVisitedIds((prev) => {
        const filtered = prev.filter((id) => id !== page.id);
        return [page.id, ...filtered].slice(0, 6);
      });
    },
    [activePage, clearSelection],
  );

  /** PDF reader → notebook: works even when no page is open (creates one). */
  const handlePdfAddToNotes = useCallback(
    (selectedText, meta = {}) => {
      const text = (selectedText || "").trim();
      if (!text) return;
      const currentPage = activePageRef.current;

      const fromAi = meta.kind === "ai_summary";
      const referenceLabel = `PDF Ref: ${meta.docName || "Document"}${meta.page != null ? ` · page ${meta.page}` : ""}`;
      const blocks = [];
      if (fromAi) {
        const label = `PDF · ${meta.docName || "Document"}${meta.page != null ? ` · p.${meta.page}` : ""}`;
        blocks.push(createBlock(BLOCK_TYPES.HEADING3, label));
        blocks.push(createBlock(BLOCK_TYPES.TEXT, text));
        blocks.push(
          createBlock(
            BLOCK_TYPES.CALLOUT,
            `${referenceLabel}\nUse this as a linked study reference.`,
          ),
        );
      } else {
        blocks.push(createBlock(BLOCK_TYPES.QUOTE, text));
        blocks.push(
          createBlock(
            BLOCK_TYPES.CALLOUT,
            `${referenceLabel}\nActions: Explain · Quiz · Analyze`,
          ),
        );
        blocks.push({
          ...createBlock(BLOCK_TYPES.TODO, "Explain this reference"),
          checked: false,
        });
        blocks.push({
          ...createBlock(BLOCK_TYPES.TODO, "Generate quiz from this reference"),
          checked: false,
        });
        blocks.push({
          ...createBlock(
            BLOCK_TYPES.TODO,
            "Analyze key terms from this reference",
          ),
          checked: false,
        });
      }

      if (currentPage) {
        const updatedBlocks = [...(currentPage.blocks || []), ...blocks];
        const updatedPage = {
          ...currentPage,
          blocks: updatedBlocks,
          updatedAt: new Date().toISOString(),
        };
        setActivePage(updatedPage);
        setNotebooks((prev) =>
          updateItemInTree(prev, currentPage.id, updatedPage),
        );
        toast.success(
          fromAi ? "Added AI summary to your note" : "Added quote to your note",
        );
        return;
      }

      const newPage = createPage(fromAi ? "AI summary" : "PDF quote", "root");
      newPage.blocks = [...blocks, createBlock(BLOCK_TYPES.TEXT, "")];
      setNotebooks((prev) => addItemToFolder(prev, "root", newPage));
      setTimeout(() => handlePageSelect(newPage), 0);
      toast.success("Created a note with your PDF capture");
    },
    [updateItemInTree, addItemToFolder, handlePageSelect],
  );

  const handleBlockChange = useCallback(
    (blockId, updates) => {
      if (!activePage) return;
      const blockUpdates =
        typeof updates === "string" ? { content: updates } : updates;
      const updatedBlocks = updateBlockInList(
        activePage.blocks,
        blockId,
        blockUpdates,
      );
      const updatedPage = {
        ...activePage,
        blocks: updatedBlocks,
        updatedAt: new Date().toISOString(),
      };
      setActivePage(updatedPage);
      setNotebooks((prev) =>
        updateItemInTree(prev, activePage.id, updatedPage),
      );
    },
    [activePage, updateBlockInList, updateItemInTree],
  );

  // Apply formatting to selected text in textarea
  const applyTextFormat = useCallback(
    (formatType) => {
      if (!selectionTagMenu || !selectionTagMenu.blockId) return;

      const { blockId, start, end } = selectionTagMenu;
      const textarea = blockRefs.current[blockId];
      if (!textarea) return;

      const text = textarea.value;
      const selectedText = text.substring(start, end);

      if (!selectedText || !selectedText.trim()) return;

      let wrappedText = selectedText;

      switch (formatType) {
        case "bold":
          wrappedText = `**${selectedText}**`;
          break;
        case "italic":
          wrappedText = `*${selectedText}*`;
          break;
        case "strikethrough":
          wrappedText = `~~${selectedText}~~`;
          break;
        case "code":
          wrappedText = `\`${selectedText}\``;
          break;
        case "highlight-yellow":
          wrappedText = `==yellow:${selectedText}==`;
          break;
        case "highlight-green":
          wrappedText = `==green:${selectedText}==`;
          break;
        case "highlight-blue":
          wrappedText = `==blue:${selectedText}==`;
          break;
        case "highlight-pink":
          wrappedText = `==pink:${selectedText}==`;
          break;
        case "highlight-purple":
          wrappedText = `==purple:${selectedText}==`;
          break;
        case "highlight-red":
          wrappedText = `==red:${selectedText}==`;
          break;
        case "quote":
          wrappedText = `> ${selectedText}`;
          break;
        // Text colors
        case "tc-red":
          wrappedText = `{tc:red:${selectedText}}`;
          break;
        case "tc-orange":
          wrappedText = `{tc:orange:${selectedText}}`;
          break;
        case "tc-amber":
          wrappedText = `{tc:amber:${selectedText}}`;
          break;
        case "tc-green":
          wrappedText = `{tc:green:${selectedText}}`;
          break;
        case "tc-blue":
          wrappedText = `{tc:blue:${selectedText}}`;
          break;
        case "tc-purple":
          wrappedText = `{tc:purple:${selectedText}}`;
          break;
        case "tc-pink":
          wrappedText = `{tc:pink:${selectedText}}`;
          break;
        case "tc-gray":
          wrappedText = `{tc:gray:${selectedText}}`;
          break;
        // Font sizes
        case "fs-sm":
          wrappedText = `{fs:sm:${selectedText}}`;
          break;
        case "fs-lg":
          wrappedText = `{fs:lg:${selectedText}}`;
          break;
        case "fs-xl":
          wrappedText = `{fs:xl:${selectedText}}`;
          break;
        // Font families
        case "ff-serif":
          wrappedText = `{ff:serif:${selectedText}}`;
          break;
        case "ff-mono":
          wrappedText = `{ff:mono:${selectedText}}`;
          break;
        case "ff-cursive":
          wrappedText = `{ff:cursive:${selectedText}}`;
          break;
        case "ff-display":
          wrappedText = `{ff:display:${selectedText}}`;
          break;
        case "ff-narrow":
          wrappedText = `{ff:narrow:${selectedText}}`;
          break;
        case "ff-rounded":
          wrappedText = `{ff:rounded:${selectedText}}`;
          break;
        // Clear all inline formatting
        case "clear":
          wrappedText = selectedText
            .replace(/\*\*(.+?)\*\*/g, "$1")
            .replace(/\*(.+?)\*/g, "$1")
            .replace(/~~(.+?)~~/g, "$1")
            .replace(/`(.+?)`/g, "$1")
            .replace(/==[a-z-]+:(.+?)==/g, "$1")
            .replace(/==(.+?)==/g, "$1")
            .replace(/\{tc:[a-z]+:(.+?)\}/g, "$1")
            .replace(/\{fs:[a-z]+:(.+?)\}/g, "$1")
            .replace(/\{ff:[a-z]+:(.+?)\}/g, "$1");
          break;
        default:
          break;
      }

      const newText =
        text.substring(0, start) + wrappedText + text.substring(end);
      handleBlockChange(blockId, newText);
      setSelectionTagMenu(null);
      // Deactivate block so the formatted preview renders immediately
      setActiveBlockId(null);
    },
    [selectionTagMenu, handleBlockChange],
  );

  const handleAddMarginNote = useCallback(
    (blockId, note) => {
      if (!activePage) return;
      const updatedBlocks = updateBlockInList(activePage.blocks, blockId, {
        marginNote: note,
        hasMarginNote: !!note,
      });
      const updatedPage = {
        ...activePage,
        blocks: updatedBlocks,
        updatedAt: new Date().toISOString(),
      };
      setActivePage(updatedPage);
      setNotebooks((prev) =>
        updateItemInTree(prev, activePage.id, updatedPage),
      );
    },
    [activePage, updateBlockInList, updateItemInTree],
  );

  const handleDelete = useCallback(
    (id) => {
      const item = findItemById(notebooks, id);
      if (item) {
        setTrash((prev) => [
          { ...item, deletedAt: new Date().toISOString() },
          ...prev,
        ]);
        if (activePage?.id === id) setActivePage(null);
        setNotebooks((prev) => deleteItemFromTree(prev, id));
      }
      setContextMenu(null);
    },
    [activePage, notebooks, findItemById, deleteItemFromTree],
  );

  const handleRestore = useCallback(
    (id) => {
      const item = trash.find((i) => i.id === id);
      if (item) {
        const { deletedAt, ...restoredItem } = item;
        setNotebooks((prev) =>
          addItemToFolder(prev, item.parentId || "root", restoredItem),
        );
        setTrash((prev) => prev.filter((i) => i.id !== id));
      }
    },
    [trash, addItemToFolder],
  );

  const handlePermanentDelete = useCallback((id) => {
    setTrash((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const handleEmptyTrash = useCallback(() => {
    setTrash([]);
  }, []);

  const calculateMenuPosition = useCallback((rect, menuWidth, menuHeight) => {
    let x = rect.left;
    let y = rect.bottom + 5;

    // Adjust x if menu goes off screen right
    if (x + menuWidth > window.innerWidth) {
      x = window.innerWidth - menuWidth - 10;
    }
    // Adjust y if menu goes off screen bottom
    if (y + menuHeight > window.innerHeight) {
      y = rect.top - menuHeight - 5;
    }
    // Ensure x/y are not negative
    x = Math.max(10, x);
    y = Math.max(10, y);

    return { x, y };
  }, []);

  const handleRename = useCallback(
    (id, newName) => {
      const item = findItemById(notebooks, id);
      if (item) {
        if (item.isFolder) {
          setNotebooks((prev) => updateItemInTree(prev, id, { name: newName }));
        } else {
          setNotebooks((prev) =>
            updateItemInTree(prev, id, { title: newName }),
          );
          if (activePage?.id === id)
            setActivePage((prev) => ({ ...prev, title: newName }));
        }
      }
      setEditingTitle(null);
    },
    [activePage, notebooks, findItemById, updateItemInTree],
  );

  const handleToggleFavorite = useCallback(
    (id) => {
      const item = findItemById(notebooks, id);
      if (item && !item.isFolder) {
        setNotebooks((prev) =>
          updateItemInTree(prev, id, { isFavorite: !item.isFavorite }),
        );
        if (activePage?.id === id)
          setActivePage((prev) => ({ ...prev, isFavorite: !prev.isFavorite }));
      }
      setContextMenu(null);
    },
    [activePage, notebooks, findItemById, updateItemInTree],
  );

  const handleCreatePage = useCallback(
    (parentId = "root") => {
      const newPage = createPage("Untitled", parentId);
      setSearchQuery("");
      setSidebarSections((prev) => ({ ...prev, private: true }));
      setNotebooks((prev) => {
        const targetId = findItemById(prev, parentId) ? parentId : "root";
        return addItemToFolder(prev, targetId, newPage);
      });
      setTimeout(() => handlePageSelect(newPage), 50);
    },
    [handlePageSelect, addItemToFolder, findItemById],
  );

  const handleCreateFolder = useCallback(
    (parentId = "root") => {
      const newFolder = createFolder("New Folder", parentId);
      newFolder.children = [];
      setSearchQuery("");
      setSidebarSections((prev) => ({ ...prev, private: true }));
      setNotebooks((prev) => {
        const targetId = findItemById(prev, parentId) ? parentId : "root";
        return addItemToFolder(prev, targetId, newFolder);
      });
    },
    [addItemToFolder, findItemById],
  );

  const findParentAndIndex = useCallback((list, id, parent = null) => {
    for (let i = 0; i < list.length; i++) {
      if (list[i].id === id) return { parent, list, index: i };
      if (list[i].children) {
        const found = findParentAndIndex(list[i].children, id, list[i]);
        if (found) return found;
      }
    }
    return null;
  }, []);

  const handleBlockDrop = useCallback(
    (targetId) => {
      if (!draggedBlockId || draggedBlockId === targetId) {
        setDraggedBlockId(null);
        setDragOverBlockId(null);
        setDropPosition(null);
        return;
      }

      const blockToMove = (function findBlock(blocks, id) {
        for (const b of blocks) {
          if (b.id === id) return b;
          if (b.children) {
            const found = findBlock(b.children, id);
            if (found) return found;
          }
        }
        return null;
      })(activePage.blocks, draggedBlockId);

      if (!blockToMove) return;

      let blocksAfterDelete = deleteBlockFromList(
        activePage.blocks,
        draggedBlockId,
      );
      let updatedBlocks;

      if (dropPosition === "child") {
        updatedBlocks = addBlockToList(
          blocksAfterDelete,
          targetId,
          blockToMove,
          true,
        );
      } else if (dropPosition === "left" || dropPosition === "right") {
        const res = findParentAndIndex(blocksAfterDelete, targetId);
        if (res) {
          const updateNestedList = (list, tid, moveBlock, pos) => {
            return list.map((b, i) => {
              if (b.id === tid) {
                const leftCol = createBlock(BLOCK_TYPES.COLUMN);
                const rightCol = createBlock(BLOCK_TYPES.COLUMN);
                const colGroup = createBlock(BLOCK_TYPES.COLUMN_GROUP);

                if (pos === "left") {
                  leftCol.children = [moveBlock];
                  rightCol.children = [b];
                } else {
                  leftCol.children = [b];
                  rightCol.children = [moveBlock];
                }
                colGroup.children = [leftCol, rightCol];
                return colGroup;
              }
              if (b.children && b.children.length > 0) {
                // Special case: if dropping onto a column inside a column group
                if (b.type === BLOCK_TYPES.COLUMN_GROUP) {
                  const targetIdx = b.children.findIndex((c) => c.id === tid);
                  if (targetIdx !== -1) {
                    const newCol = createBlock(BLOCK_TYPES.COLUMN);
                    newCol.children = [moveBlock];
                    const newChildren = [...b.children];
                    newChildren.splice(
                      pos === "left" ? targetIdx : targetIdx + 1,
                      0,
                      newCol,
                    );
                    return { ...b, children: newChildren };
                  }
                }
                return {
                  ...b,
                  children: updateNestedList(b.children, tid, moveBlock, pos),
                };
              }
              return b;
            });
          };
          updatedBlocks = updateNestedList(
            blocksAfterDelete,
            targetId,
            blockToMove,
            dropPosition,
          );
        } else {
          updatedBlocks = blocksAfterDelete;
        }
      } else {
        const insertInList = (list) => {
          const result = [];
          for (const b of list) {
            if (b.id === targetId) {
              if (dropPosition === "before") result.push(blockToMove);
              result.push(b);
              if (dropPosition === "after") result.push(blockToMove);
            } else {
              if (b.children && b.children.length > 0) {
                result.push({ ...b, children: insertInList(b.children) });
              } else {
                result.push(b);
              }
            }
          }
          return result;
        };
        updatedBlocks = insertInList(blocksAfterDelete);
      }

      if (updatedBlocks) {
        const updatedPage = { ...activePage, blocks: updatedBlocks };
        setActivePage(updatedPage);
        setNotebooks((prev) =>
          updateItemInTree(prev, activePage.id, updatedPage),
        );
      }
      setDraggedBlockId(null);
      setDragOverBlockId(null);
      setDropPosition(null);
    },
    [
      draggedBlockId,
      dropPosition,
      activePage,
      deleteBlockFromList,
      addBlockToList,
      updateItemInTree,
      findParentAndIndex,
    ],
  );

  const handleBulkDelete = useCallback(() => {
    if (selectedBlockIds.length === 0) return;
    let updatedBlocks = activePage.blocks;
    selectedBlockIds.forEach((id) => {
      updatedBlocks = deleteBlockFromList(updatedBlocks, id);
    });
    const updatedPage = { ...activePage, blocks: updatedBlocks };
    setActivePage(updatedPage);
    setNotebooks((prev) => updateItemInTree(prev, activePage.id, updatedPage));
    clearSelection();
  }, [
    activePage,
    selectedBlockIds,
    deleteBlockFromList,
    updateItemInTree,
    clearSelection,
  ]);

  const handleBulkTransform = useCallback(
    (type) => {
      if (selectedBlockIds.length === 0) return;
      let updatedBlocks = activePage.blocks;
      selectedBlockIds.forEach((id) => {
        updatedBlocks = updateBlockInList(updatedBlocks, id, { type });
      });
      const updatedPage = { ...activePage, blocks: updatedBlocks };
      setActivePage(updatedPage);
      setNotebooks((prev) =>
        updateItemInTree(prev, activePage.id, updatedPage),
      );
      clearSelection();
    },
    [
      activePage,
      selectedBlockIds,
      updateBlockInList,
      updateItemInTree,
      clearSelection,
    ],
  );

  const handleBlockColorSelect = useCallback(
    (colorType, colorValue, isBackground = false) => {
      if (!activePage || !activeMenu?.blockId) return;
      const updates = isBackground
        ? { backgroundColor: colorValue }
        : { color: colorValue };
      const updatedBlocks = updateBlockInList(
        activePage.blocks,
        activeMenu.blockId,
        updates,
      );
      const updatedPage = { ...activePage, blocks: updatedBlocks };
      setActivePage(updatedPage);
      setNotebooks((prev) =>
        updateItemInTree(prev, activePage.id, updatedPage),
      );
      setActiveMenu(null);
    },
    [activePage, activeMenu, updateBlockInList, updateItemInTree],
  );

  const handleConvertMenuSelect = useCallback(
    (type) => {
      if (!activePage || !activeMenu?.blockId) return;
      const updatedBlocks = updateBlockInList(
        activePage.blocks,
        activeMenu.blockId,
        { type },
      );
      const updatedPage = { ...activePage, blocks: updatedBlocks };
      setActivePage(updatedPage);
      setNotebooks((prev) =>
        updateItemInTree(prev, activePage.id, updatedPage),
      );
      setActiveMenu(null);
    },
    [activePage, activeMenu, updateBlockInList, updateItemInTree],
  );

  const handleSlashMenuSelect = useCallback(
    (type) => {
      if (!activePage || !activeBlockId) return;

      if (type === "ask_ai" || type === "ai_brainstorm") {
        const updatedBlocks = updateBlockInList(
          activePage.blocks,
          activeBlockId,
          {
            type: "ask_ai",
            content: "",
            aiMode: type === "ai_brainstorm" ? "brainstorm" : "general",
          },
        );
        const updatedPage = { ...activePage, blocks: updatedBlocks };
        setActivePage(updatedPage);
        setNotebooks((prev) =>
          updateItemInTree(prev, activePage.id, updatedPage),
        );
        setShowSlashMenu(false);
        return;
      }

      if (type === "ai_summarize") {
        setShowAiPanel(true);
        handleAiAction(
          "Summarize Page",
          activePage.blocks.map((b) => b.content).join("\n"),
        );
        setShowSlashMenu(false);
        return;
      }

      // AI-powered content generation
      if (
        type === "ai_quiz" ||
        type === "ai_flashcards" ||
        type === "ai_test" ||
        type === "ai_quote"
      ) {
        setShowSlashMenu(false);
        setIsAiLoading(true);

        setTimeout(() => {
          let response = "";
          if (type === "ai_quiz") {
            response = `**Quiz: Test Your Knowledge**\n\n1. What is the main concept discussed?\n   a) Option A\n   b) Option B\n   c) Option C\n   d) Option D\n\n2. Which statement is true?\n   a) Statement 1\n   b) Statement 2\n   c) Statement 3\n\n3. Fill in the blank: _____ is important because...\n\n*Answers: 1-b, 2-c, 3-varies*`;
          } else if (type === "ai_flashcards") {
            response = `**Flashcards**\n\n**Card 1**\nQ: What is the key concept?\nA: The key concept is...\n\n**Card 2**\nQ: Define the main term\nA: It refers to...\n\n**Card 3**\nQ: What are the benefits?\nA: The main benefits include...`;
          } else if (type === "ai_test") {
            response = `**Practice Test**\n\n**Section A: Multiple Choice (10 pts)**\n1. Question about main topic...\n2. Question about secondary concept...\n\n**Section B: Short Answer (15 pts)**\n3. Explain the relationship between...\n4. Describe how...\n\n**Section C: Essay (25 pts)**\n5. Analyze and discuss...`;
          } else if (type === "ai_quote") {
            response = `> "The only way to do great work is to love what you do."\n> — Steve Jobs\n\n*Related to your notes on productivity and passion.*`;
          }

          setAiPreview({
            prompt: type.replace("ai_", "").replace("_", " "),
            response,
          });
          setIsAiLoading(false);
          setAiBarExpanded(true);
        }, 1200);
        return;
      }

      // Handle color options
      if (type.startsWith("color_")) {
        const colorName = type.replace("color_", "");
        const colorMap = {
          red: "#ef4444",
          orange: "#f97316",
          yellow: "#eab308",
          green: "#22c55e",
          blue: "#3b82f6",
          purple: "#a855f7",
        };
        const updatedBlocks = updateBlockInList(
          activePage.blocks,
          activeBlockId,
          { color: colorMap[colorName] },
        );
        const updatedPage = { ...activePage, blocks: updatedBlocks };
        setActivePage(updatedPage);
        setNotebooks((prev) =>
          updateItemInTree(prev, activePage.id, updatedPage),
        );
        setShowSlashMenu(false);
        return;
      }

      // Handle highlight options
      if (type.startsWith("highlight_")) {
        const colorName = type.replace("highlight_", "");
        const bgMap = {
          red: "rgba(239, 68, 68, 0.2)",
          yellow: "rgba(234, 179, 8, 0.2)",
          green: "rgba(34, 197, 94, 0.2)",
          blue: "rgba(59, 130, 246, 0.2)",
          purple: "rgba(168, 85, 247, 0.2)",
        };
        const updatedBlocks = updateBlockInList(
          activePage.blocks,
          activeBlockId,
          { backgroundColor: bgMap[colorName] },
        );
        const updatedPage = { ...activePage, blocks: updatedBlocks };
        setActivePage(updatedPage);
        setNotebooks((prev) =>
          updateItemInTree(prev, activePage.id, updatedPage),
        );
        setShowSlashMenu(false);
        return;
      }

      const updatedBlocks = updateBlockInList(
        activePage.blocks,
        activeBlockId,
        { type },
      );
      const updatedPage = { ...activePage, blocks: updatedBlocks };
      setActivePage(updatedPage);
      setNotebooks((prev) =>
        updateItemInTree(prev, activePage.id, updatedPage),
      );
      setShowSlashMenu(false);
      setTimeout(() => {
        const blockEl = blockRefs.current[activeBlockId];
        if (blockEl) blockEl.focus();
      }, 10);
    },
    [
      activePage,
      activeBlockId,
      updateBlockInList,
      updateItemInTree,
      handleAiAction,
    ],
  );

  const handleBlockKeyDown = useCallback(
    (e, block, index) => {
      const content = block.content;

      if (e.key === "/" && content === "") {
        const rect = e.target.getBoundingClientRect();
        setSlashMenuPosition({ x: rect.left, y: rect.bottom + 5 });
        setShowSlashMenu(true);
        setSlashFilter("");
        setActiveBlockId(block.id);
      }

      if (content.startsWith("/")) {
        const filter = content.slice(1).toLowerCase();
        setSlashFilter(filter);

        // Get filtered options for keyboard nav
        const filtered = slashMenuOptions.filter(
          (opt) =>
            opt.label.toLowerCase().includes(filter) ||
            opt.shortcut.includes(filter) ||
            opt.description.toLowerCase().includes(filter),
        );

        // Tab or Enter to apply first match
        if ((e.key === "Tab" || e.key === "Enter") && filtered.length > 0) {
          e.preventDefault();
          const selected = filtered[slashMenuSelectedIndex] || filtered[0];
          handleSlashMenuSelect(selected.type);
          handleBlockChange(block.id, "");
          setSlashMenuSelectedIndex(0);
          return;
        }

        // Arrow keys to navigate
        if (e.key === "ArrowDown" && showSlashMenu) {
          e.preventDefault();
          setSlashMenuSelectedIndex((prev) =>
            Math.min(prev + 1, filtered.length - 1),
          );
          return;
        }
        if (e.key === "ArrowUp" && showSlashMenu) {
          e.preventDefault();
          setSlashMenuSelectedIndex((prev) => Math.max(prev - 1, 0));
          return;
        }

        if (!showSlashMenu) {
          const rect = e.target.getBoundingClientRect();
          setSlashMenuPosition({ x: rect.left, y: rect.bottom + 5 });
          setShowSlashMenu(true);
          setActiveBlockId(block.id);
          setSlashMenuSelectedIndex(0);
        }
      } else if (showSlashMenu && content === "") {
        setShowSlashMenu(false);
        setSlashFilter("");
        setSlashMenuSelectedIndex(0);
      }

      if (e.key === "Escape") {
        if (showSlashMenu) {
          setShowSlashMenu(false);
          setSlashFilter("");
          return;
        }
        clearSelection();
      }

      if ((e.metaKey || e.ctrlKey) && e.key === "j") {
        e.preventDefault();
        setShowAiPanel((prev) => !prev);
        return;
      }

      if (e.key === " " && !e.repeat) {
        if (content === "#") {
          e.preventDefault();
          handleSlashMenuSelect(BLOCK_TYPES.HEADING1);
          handleBlockChange(block.id, "");
          return;
        }
        if (content === "##") {
          e.preventDefault();
          handleSlashMenuSelect(BLOCK_TYPES.HEADING2);
          handleBlockChange(block.id, "");
          return;
        }
        if (content === "###") {
          e.preventDefault();
          handleSlashMenuSelect(BLOCK_TYPES.HEADING3);
          handleBlockChange(block.id, "");
          return;
        }
        if (content === "-" || content === "*") {
          e.preventDefault();
          handleSlashMenuSelect(BLOCK_TYPES.BULLET);
          handleBlockChange(block.id, "");
          return;
        }
        if (content === "1.") {
          e.preventDefault();
          handleSlashMenuSelect(BLOCK_TYPES.NUMBERED);
          handleBlockChange(block.id, "");
          return;
        }
        if (content === "[]") {
          e.preventDefault();
          handleSlashMenuSelect(BLOCK_TYPES.TODO);
          handleBlockChange(block.id, "");
          return;
        }
        if (content === ">") {
          e.preventDefault();
          handleSlashMenuSelect(BLOCK_TYPES.QUOTE);
          handleBlockChange(block.id, "");
          return;
        }
        if (content === '""' || content === "```") {
          e.preventDefault();
          handleSlashMenuSelect(BLOCK_TYPES.CODE);
          handleBlockChange(block.id, "");
          return;
        }
      }

      if (e.key === " " && block.content === "" && !e.repeat) {
        return;
      }

      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        const newBlock = createBlock();
        const asChild = block.type === BLOCK_TYPES.TOGGLE && !block.collapsed;
        const updatedBlocks = addBlockToList(
          activePage.blocks,
          block.id,
          newBlock,
          asChild,
        );
        const updatedPage = { ...activePage, blocks: updatedBlocks };
        setActivePage(updatedPage);
        setNotebooks((prev) =>
          updateItemInTree(prev, activePage.id, updatedPage),
        );
        setTimeout(() => {
          const newBlockEl = blockRefs.current[newBlock.id];
          if (newBlockEl) newBlockEl.focus();
        }, 10);
        return;
      }

      if (
        e.key === "Backspace" &&
        content === "" &&
        (activePage.blocks.length > 1 || block.parentId)
      ) {
        e.preventDefault();
        const prevBlock = findPreviousBlock(activePage.blocks, block.id);
        const updatedBlocks = deleteBlockFromList(activePage.blocks, block.id);
        const updatedPage = { ...activePage, blocks: updatedBlocks };
        setActivePage(updatedPage);
        setNotebooks((prev) =>
          updateItemInTree(prev, activePage.id, updatedPage),
        );
        if (prevBlock) {
          setTimeout(() => {
            const el = blockRefs.current[prevBlock.id];
            if (el) {
              el.focus();
              el.setSelectionRange(el.value.length, el.value.length);
            }
          }, 10);
        }
        return;
      }

      if (e.key === "ArrowUp") {
        const prev = findPreviousBlock(activePage.blocks, block.id);
        if (prev) {
          e.preventDefault();
          const el = blockRefs.current[prev.id];
          if (el) el.focus();
        }
      }

      if (e.key === "ArrowDown") {
        const next = findNextBlock(activePage.blocks, block.id);
        if (next) {
          e.preventDefault();
          const el = blockRefs.current[next.id];
          if (el) el.focus();
        }
      }

      if (e.key === "Tab") {
        e.preventDefault();
        // Implementation of indent/outdent logic
        const blocks = activePage.blocks;
        const res = findParentAndIndex(blocks, block.id);
        if (!res) return;
        const { parent, list, index: idx } = res;

        if (!e.shiftKey) {
          if (idx > 0) {
            const prevSibling = list[idx - 1];
            const blockToMove = { ...block };
            let updatedBlocks = deleteBlockFromList(
              activePage.blocks,
              block.id,
            );
            updatedBlocks = addBlockToList(
              updatedBlocks,
              prevSibling.id,
              blockToMove,
              true,
            );
            const updatedPage = { ...activePage, blocks: updatedBlocks };
            setActivePage(updatedPage);
            setNotebooks((prev) =>
              updateItemInTree(prev, activePage.id, updatedPage),
            );
            setTimeout(() => {
              const el = blockRefs.current[block.id];
              if (el) el.focus();
            }, 10);
          }
        } else {
          if (parent) {
            const blockToMove = { ...block };
            let updatedBlocks = deleteBlockFromList(
              activePage.blocks,
              block.id,
            );
            updatedBlocks = addBlockToList(
              updatedBlocks,
              parent.id,
              blockToMove,
              false,
            );
            const updatedPage = { ...activePage, blocks: updatedBlocks };
            setActivePage(updatedPage);
            setNotebooks((prev) =>
              updateItemInTree(prev, activePage.id, updatedPage),
            );
            setTimeout(() => {
              const el = blockRefs.current[block.id];
              if (el) el.focus();
            }, 10);
          }
        }
      }
    },
    [
      activePage,
      handleSlashMenuSelect,
      handleBlockChange,
      showSlashMenu,
      clearSelection,
      addBlockToList,
      updateItemInTree,
      findPreviousBlock,
      findNextBlock,
      findParentAndIndex,
      deleteBlockFromList,
      slashMenuOptions,
      slashMenuSelectedIndex,
    ],
  );

  const getRelativeTime = (isoString) => {
    if (!isoString) return "Never";
    const date = new Date(isoString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    if (diffInSeconds < 60) return "Just now";
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400)
      return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return date.toLocaleDateString();
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const renderSidebarItem = (item, depth = 0) => {
    const isFolder = item.isFolder;
    const isEditing = editingTitle === item.id;
    const isActive = activePage?.id === item.id;

    return (
      <div
        key={item.id}
        className={`${styles.sidebarItemWrapper} ${isActive ? styles.sidebarItemActive : ""}`}
        style={{ paddingLeft: `${depth * 12 + 8}px` }}
        onContextMenu={(e) => {
          e.preventDefault();
          setContextMenu({ x: e.clientX, y: e.clientY, item });
        }}
      >
        <div className={styles.sidebarItemHeader}>
          {isFolder ? (
            <>
              <button
                className={styles.sidebarCollapseBtn}
                onClick={() => {
                  setNotebooks((prev) =>
                    updateItemInTree(prev, item.id, {
                      collapsed: !item.collapsed,
                    }),
                  );
                }}
              >
                {item.collapsed ? (
                  <ChevronRight size={14} />
                ) : (
                  <ChevronDown size={14} />
                )}
              </button>
              <span className={styles.folderTypeIcon}>
                {FOLDER_ICONS[item.folderType] || FOLDER_ICONS.folder}
              </span>
            </>
          ) : (
            <div className={styles.sidebarItemIcon}>
              <FileText size={14} />
            </div>
          )}

          <div
            className={styles.sidebarItemTitle}
            onClick={() => {
              if (isEditing) return;
              if (isFolder) {
                setSelectedFolder(item);
                setActivePage(null);
              } else {
                handlePageSelect(item);
                setSelectedFolder(null);
              }
            }}
          >
            {isEditing ? (
              <input
                autoFocus
                className={styles.sidebarTitleInput}
                defaultValue={isFolder ? item.name : item.title}
                onBlur={(e) => handleRename(item.id, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleRename(item.id, e.target.value);
                  if (e.key === "Escape") setEditingTitle(null);
                }}
              />
            ) : (
              <span>{isFolder ? item.name : item.title}</span>
            )}
          </div>

          <div className={styles.sidebarItemActions}>
            {isFolder && (
              <button
                className={styles.sidebarItemActionBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCreatePage(item.id);
                }}
                title="Add page"
              >
                <Plus size={14} />
              </button>
            )}
            <button
              className={styles.sidebarItemActionBtn}
              onClick={(e) => {
                e.stopPropagation();
                setContextMenu({ x: e.clientX, y: e.clientY, item });
              }}
              title="More options"
            >
              <MoreHorizontal size={14} />
            </button>
          </div>
        </div>

        {isFolder && !item.collapsed && item.children && (
          <div className={styles.sidebarItemChildren}>
            {item.children.map((child) => renderSidebarItem(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const renderInlineMarkup = (text) => {
    //         8=tc-color 9=tc-text  10=fs-size 11=fs-text  12=ff-family 13=ff-text
    const combined =
      /\*\*(.+?)\*\*|\*(.+?)\*|~~(.+?)~~|`(.+?)`|==(yellow|green|blue|pink|purple|red):(.+?)==|==(.+?)==|\{tc:(red|orange|amber|green|blue|purple|pink|gray):(.+?)\}|\{fs:(sm|lg|xl):(.+?)\}|\{ff:(serif|mono|cursive|display|narrow|rounded):(.+?)\}/g;
    const elements = [];
    let lastIndex = 0;
    let match;
    let key = 0;
    combined.lastIndex = 0;
    while ((match = combined.exec(text)) !== null) {
      if (match.index > lastIndex)
        elements.push(text.slice(lastIndex, match.index));
      if (match[1] !== undefined)
        elements.push(<strong key={key++}>{match[1]}</strong>);
      else if (match[2] !== undefined)
        elements.push(<em key={key++}>{match[2]}</em>);
      else if (match[3] !== undefined)
        elements.push(<s key={key++}>{match[3]}</s>);
      else if (match[4] !== undefined)
        elements.push(
          <code key={key++} className={styles.inlineCode}>
            {match[4]}
          </code>,
        );
      else if (match[5] !== undefined)
        elements.push(
          <mark
            key={key++}
            className={`${styles.hl} ${styles[`hl_${match[5]}`]}`}
          >
            {match[6]}
          </mark>,
        );
      else if (match[7] !== undefined)
        elements.push(
          <mark key={key++} className={`${styles.hl} ${styles.hl_yellow}`}>
            {match[7]}
          </mark>,
        );
      else if (match[8] !== undefined)
        elements.push(
          <span key={key++} className={`${styles[`tc_${match[8]}`]}`}>
            {match[9]}
          </span>,
        );
      else if (match[10] !== undefined)
        elements.push(
          <span key={key++} className={`${styles[`fs_${match[10]}`]}`}>
            {match[11]}
          </span>,
        );
      else if (match[12] !== undefined)
        elements.push(
          <span key={key++} className={`${styles[`ff_${match[12]}`]}`}>
            {match[13]}
          </span>,
        );
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < text.length) elements.push(text.slice(lastIndex));
    return elements.length ? elements : text;
  };

  const renderFormattedText = renderInlineMarkup;

  // When embedded in HomePage (hideSidebar=true), push sidebar content into shared context
  // so the homepage sidebar renders it instead of the notebook's own sidebar.
  useEffect(() => {
    if (!hideSidebar) {
      setNotebookSidebarContent(null);
      return;
    }
    setNotebookSidebarContent(
      <div className={styles.sidebarContent}>
        <div className={styles.sidebarSearch}>
          <Search size={16} />
          <input
            type="text"
            placeholder="Search pages..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
          />
          {searchQuery && (
            <button
              className={styles.clearSearch}
              onClick={() => setSearchQuery("")}
            >
              <X size={14} />
            </button>
          )}
        </div>

        <div className={styles.sidebarNav}>
          <button
            className={`${styles.navItem} ${!activePage ? styles.active : ""}`}
            onClick={() => handlePageSelect(null)}
          >
            <Home size={16} />
            <span>Home</span>
          </button>
          <button
            className={styles.navItem}
            onClick={() => {
              setShowQuickSwitcher(true);
              setSwitcherSearchQuery("");
            }}
          >
            <Search size={16} />
            <span>Search</span>
          </button>
          <button className={styles.navItem} onClick={() => setShowTrash(true)}>
            <Trash2 size={16} />
            <span>Trash</span>
          </button>
          <button className={styles.navItem} onClick={toggleTheme}>
            {darkMode ? <Sun size={16} /> : <Moon size={16} />}
            <span>{darkMode ? "Light mode" : "Dark mode"}</span>
          </button>
        </div>

        {notebooks.some((item) =>
          (function hasFav(n) {
            if (n.isFavorite) return true;
            if (n.children) return n.children.some(hasFav);
            return false;
          })(item),
        ) && (
          <div className={styles.sidebarSection}>
            <div
              className={styles.sectionHeader}
              onClick={() => toggleSidebarSection("favorites")}
            >
              <div className={styles.sectionHeaderLeft}>
                {sidebarSections.favorites ? (
                  <ChevronDown size={14} />
                ) : (
                  <ChevronRight size={14} />
                )}
                <span>Favorites</span>
              </div>
            </div>
            {sidebarSections.favorites && (
              <div className={styles.sidebarList}>
                {(function getFavorites(items) {
                  let favs = [];
                  items.forEach((item) => {
                    if (item.isFavorite) favs.push(item);
                    if (item.children)
                      favs = [...favs, ...getFavorites(item.children)];
                  });
                  return favs;
                })(notebooks).map((item) => renderSidebarItem(item))}
              </div>
            )}
          </div>
        )}

        <div className={styles.sidebarDivider} />

        <div className={styles.sidebarSection}>
          <div
            className={styles.sectionHeader}
            onClick={() => toggleSidebarSection("private")}
          >
            <div className={styles.sectionHeaderLeft}>
              {sidebarSections.private ? (
                <ChevronDown size={14} />
              ) : (
                <ChevronRight size={14} />
              )}
              <span>My Notes</span>
            </div>
            <div className={styles.sectionActions}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCreateFolder("root");
                }}
                title="New subject"
              >
                <Folder size={14} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCreatePage("root");
                }}
                title="New page"
              >
                <Plus size={14} />
              </button>
            </div>
          </div>
          {sidebarSections.private && (
            <>
              <div className={styles.sidebarList}>
                {filteredNotebooks.length > 0 ? (
                  filteredNotebooks.map((item) => renderSidebarItem(item))
                ) : (
                  <div className={styles.noResults}>No matches found</div>
                )}
              </div>
              <button
                className={styles.addSidebarPageBtn}
                onClick={() => handleCreatePage("root")}
              >
                <Plus size={14} />
                <span>Add a page</span>
              </button>
            </>
          )}
        </div>

        <div className={styles.sidebarDivider} />

        <div className={styles.sidebarBottom}>
          <button
            className={styles.navItem}
            onClick={() => handleCreatePage("root")}
          >
            <Plus size={16} />
            <span>New page</span>
          </button>
        </div>
      </div>,
    );
    return () => setNotebookSidebarContent(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hideSidebar,
    notebooks,
    activePage,
    searchQuery,
    filteredNotebooks,
    sidebarSections,
    darkMode,
    editingTitle,
  ]);

  // Render block - using textarea to fix typing backwards issue
  // eslint-disable-next-line no-unused-vars
  const renderBlock = (block, index) => {
    const handleTextareaChange = (e) => {
      handleBlockChange(block.id, e.target.value);
      // Auto-resize textarea
      e.target.style.height = "auto";
      e.target.style.height = e.target.scrollHeight + "px";
    };

    const handleTextareaKeyDown = (e) => {
      handleBlockKeyDown(e, block, index);
    };

    const getBlockClassName = () => {
      let className = styles.blockTextarea;
      if (selectedBlockIds.includes(block.id)) {
        className += ` ${styles.selectedTextarea}`;
      }
      switch (block.type) {
        case BLOCK_TYPES.HEADING1:
          return `${className} ${styles.heading1}`;
        case BLOCK_TYPES.HEADING2:
          return `${className} ${styles.heading2}`;
        case BLOCK_TYPES.HEADING3:
          return `${className} ${styles.heading3}`;
        case BLOCK_TYPES.CODE:
          return `${className} ${styles.codeText}`;
        default:
          return className;
      }
    };

    const getBlockStyle = () => {
      const style = {};
      if (block.color && block.color !== "default") {
        const colorObj = BLOCK_COLORS.find(
          (c) => c.name.toLowerCase() === block.color.toLowerCase(),
        );
        if (colorObj) style.color = colorObj.value;
      }
      if (block.backgroundColor && block.backgroundColor !== "default") {
        const bgObj = BLOCK_BG_COLORS.find(
          (c) => c.name.toLowerCase() === block.backgroundColor.toLowerCase(),
        );
        if (bgObj) style.backgroundColor = bgObj.value;
      }
      return style;
    };

    const textareaProps = {
      ref: (el) => {
        blockRefs.current[block.id] = el;
        if (el) {
          el.style.height = "auto";
          el.style.height = el.scrollHeight + "px";
        }
      },
      className: getBlockClassName(),
      style: getBlockStyle(),
      value: block.content,
      onChange: handleTextareaChange,
      onKeyDown: handleTextareaKeyDown,
      onFocus: () => setActiveBlockId(block.id),
      onMouseUp: (e) => {
        const ta = e.target;
        const start = ta.selectionStart;
        const end = ta.selectionEnd;
        const selectedText = ta.value.substring(start, end);
        if (selectedText && selectedText.trim().length > 0) {
          setSelectionTagMenu({
            text: selectedText,
            x: Math.min(e.clientX, window.innerWidth - 420),
            y: Math.max(e.clientY - 60, 10),
            start,
            end,
            blockId: block.id,
          });
        } else {
          setSelectionTagMenu(null);
        }
      },
      placeholder:
        block.content === "" && activeBlockId === block.id
          ? "Type '/' for commands..."
          : "",
      rows: 1,
      ...(block.aiGenerated && { "data-ai-generated": "true" }),
    };

    const renderMarginNote = () => {
      if (addingMarginNoteId === block.id) {
        return (
          <div className={styles.marginNote}>
            <div className={styles.marginNoteEdit}>
              <textarea
                autoFocus
                className={styles.marginNoteTextarea}
                placeholder="Write a note..."
                value={marginNoteInput}
                onChange={(e) => setMarginNoteInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleAddMarginNote(block.id, marginNoteInput);
                    setAddingMarginNoteId(null);
                    setMarginNoteInput("");
                  }
                  if (e.key === "Escape") {
                    setAddingMarginNoteId(null);
                    setMarginNoteInput("");
                  }
                }}
                onBlur={() => {
                  if (marginNoteInput.trim()) {
                    handleAddMarginNote(block.id, marginNoteInput);
                  }
                  setAddingMarginNoteId(null);
                  setMarginNoteInput("");
                }}
              />
            </div>
          </div>
        );
      }
      if (!block.hasMarginNote) return null;
      return (
        <div className={styles.marginNote}>
          <div className={styles.marginNoteContent}>
            <div className={styles.marginNoteText}>{block.marginNote}</div>
            <button
              className={styles.deleteMarginNote}
              onClick={() => handleAddMarginNote(block.id, null)}
            >
              <X size={10} />
            </button>
          </div>
        </div>
      );
    };

    const renderChildren = () => {
      if (!block.children || block.children.length === 0 || block.collapsed)
        return null;
      return (
        <div className={styles.blockChildren}>
          {block.children.map((child, childIndex) =>
            renderBlock(child, childIndex),
          )}
        </div>
      );
    };

    if (block.type === "ask_ai") {
      return (
        <div className={styles.askAiBlock} key={block.id}>
          <div className={styles.askAiHeader}>
            <Sparkles size={14} />
            <span>Ask AI</span>
          </div>
          <input
            autoFocus
            className={styles.askAiInput}
            placeholder="Ask anything..."
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.target.value.trim()) {
                handleAskAiSubmit(block.id, e.target.value);
              }
            }}
          />
        </div>
      );
    }

    const handleBlockDropInternal = (e) => {
      e.preventDefault();
      handleBlockDrop(block.id);
    };

    const handleDragOver = (e) => {
      e.preventDefault();
      if (draggedBlockId === block.id) return;

      const rect = e.currentTarget.getBoundingClientRect();
      const relativeY = e.clientY - rect.top;
      const relativeX = e.clientX - rect.left;
      const height = rect.height;
      const width = rect.width;

      setDragOverBlockId(block.id);

      // Notion-style drop zones: top 20% (before), bottom 20% (after),
      // left 20% (left), right 20% (right), middle (child)
      if (relativeY < height * 0.2) {
        setDropPosition("before");
      } else if (relativeY > height * 0.8) {
        setDropPosition("after");
      } else if (relativeX < width * 0.2) {
        setDropPosition("left");
      } else if (relativeX > width * 0.8) {
        setDropPosition("right");
      } else {
        setDropPosition("child");
      }
    };

    const handleDragLeave = () => {
      setDragOverBlockId(null);
      setDropPosition(null);
    };

    const handleBlockClick = (e) => {
      // Don't trigger if clicking inside textarea or buttons
      if (e.target.tagName === "TEXTAREA" || e.target.closest("button")) return;

      e.stopPropagation();
      if (e.shiftKey || e.metaKey || e.ctrlKey) {
        toggleBlockSelection(block.id, true, e.metaKey || e.ctrlKey);
        return;
      }

      const el = blockRefs.current[block.id];
      if (el) {
        el.focus();
        const pos = el.value.length;
        el.setSelectionRange(pos, pos);
      }
    };

    const getDragClassName = () => {
      let classes = [];
      if (dragOverBlockId === block.id) {
        if (dropPosition === "before") classes.push(styles.dropBefore);
        if (dropPosition === "after") classes.push(styles.dropAfter);
        if (dropPosition === "left") classes.push(styles.dropLeft);
        if (dropPosition === "right") classes.push(styles.dropRight);
        if (dropPosition === "child") classes.push(styles.dropChild);
      }
      if (selectedBlockIds.includes(block.id)) {
        classes.push(styles.selectedBlock);
      }
      if (activeBlockId === block.id) {
        classes.push(styles.blockFocused);
      }
      return classes.join(" ");
    };

    // Dual-render: formatted preview when not editing, textarea when editing
    const blockField = (extraClass = "", extraStyle = {}) => {
      const isActive = activeBlockId === block.id;
      const previewCls = [
        styles.blockPreview,
        block.type === BLOCK_TYPES.HEADING1 ? styles.heading1 : "",
        block.type === BLOCK_TYPES.HEADING2 ? styles.heading2 : "",
        block.type === BLOCK_TYPES.HEADING3 ? styles.heading3 : "",
        block.type === BLOCK_TYPES.CODE ? styles.codeText : "",
        extraClass,
      ]
        .filter(Boolean)
        .join(" ");

      return (
        <div className={styles.blockEditorWrap}>
          <textarea
            {...textareaProps}
            className={`${textareaProps.className} ${extraClass} ${isActive ? "" : styles.textareaHidden}`}
          />
          {!isActive && (
            <div
              className={previewCls}
              style={{ ...getBlockStyle(), ...extraStyle }}
              onClick={() => {
                const el = blockRefs.current[block.id];
                if (el) {
                  el.focus();
                  setTimeout(
                    () =>
                      el.setSelectionRange(el.value.length, el.value.length),
                    0,
                  );
                }
              }}
            >
              {block.content ? (
                renderFormattedText(block.content)
              ) : (
                <span className={styles.blockPlaceholder} />
              )}
            </div>
          )}
        </div>
      );
    };

    const renderBlockActions = () => (
      <div className={styles.blockHandle}>
        <button
          className={styles.blockPlusBtn}
          onClick={() => {
            const newBlock = createBlock();
            const updatedBlocks = addBlockToList(
              activePage.blocks,
              block.id,
              newBlock,
              false,
            );
            const updatedPage = { ...activePage, blocks: updatedBlocks };
            setActivePage(updatedPage);
            setNotebooks((prev) =>
              updateItemInTree(prev, activePage.id, updatedPage),
            );
            setTimeout(() => {
              const el = blockRefs.current[newBlock.id];
              if (el) el.focus();
            }, 10);
          }}
          title="Click to add a block below"
        >
          <Plus size={14} />
        </button>
        <div
          className={styles.gripWrapper}
          draggable
          onDragStart={(e) => {
            setDraggedBlockId(block.id);
            // Set drag image/effect
            e.dataTransfer.setData("text/plain", block.id);
            e.dataTransfer.effectAllowed = "move";
          }}
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const pos = calculateMenuPosition(rect, 180, 200);
            setActiveMenu({ type: "handle", pos, blockId: block.id });
          }}
          title="Drag to move or click for actions"
        >
          <GripVertical size={14} />
        </div>
        <button
          className={styles.blockActionBtn}
          onClick={() => {
            setAddingMarginNoteId(block.id);
            setMarginNoteInput(block.marginNote || "");
          }}
          title="Add margin note"
        >
          <PenTool size={12} />
        </button>
      </div>
    );

    switch (block.type) {
      case BLOCK_TYPES.HEADING1:
      case BLOCK_TYPES.HEADING2:
      case BLOCK_TYPES.HEADING3:
        return (
          <div
            className={`${styles.block} ${getDragClassName()}`}
            key={block.id}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleBlockDropInternal}
            onClick={handleBlockClick}
          >
            {renderBlockActions()}
            <div className={styles.blockContentWrapper}>
              {blockField()}
              {renderChildren()}
            </div>
            {renderMarginNote()}
          </div>
        );
      case BLOCK_TYPES.BULLET:
        return (
          <div
            className={`${styles.block} ${getDragClassName()}`}
            key={block.id}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleBlockDropInternal}
            onClick={handleBlockClick}
          >
            {renderBlockActions()}
            <div className={styles.blockContentWrapper}>
              <div className={styles.bulletItem}>
                <span className={styles.bullet}>•</span>
                {blockField()}
              </div>
              {renderChildren()}
            </div>
            {renderMarginNote()}
          </div>
        );
      case BLOCK_TYPES.NUMBERED:
        return (
          <div
            className={`${styles.block} ${getDragClassName()}`}
            key={block.id}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleBlockDropInternal}
            onClick={handleBlockClick}
          >
            {renderBlockActions()}
            <div className={styles.blockContentWrapper}>
              <div className={styles.numberedItem}>
                <span className={styles.number}>{index + 1}.</span>
                {blockField()}
              </div>
              {renderChildren()}
            </div>
            {renderMarginNote()}
          </div>
        );
      case BLOCK_TYPES.TODO:
        return (
          <div
            className={`${styles.block} ${getDragClassName()}`}
            key={block.id}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleBlockDropInternal}
            onClick={handleBlockClick}
          >
            {renderBlockActions()}
            <div className={styles.blockContentWrapper}>
              <div className={styles.todoItem}>
                <input
                  type="checkbox"
                  checked={block.checked}
                  onChange={() => {
                    const updatedBlocks = updateBlockInList(
                      activePage.blocks,
                      block.id,
                      { checked: !block.checked },
                    );
                    const updatedPage = {
                      ...activePage,
                      blocks: updatedBlocks,
                    };
                    setActivePage(updatedPage);
                    setNotebooks((prev) =>
                      updateItemInTree(prev, activePage.id, updatedPage),
                    );
                  }}
                  className={styles.todoCheckbox}
                />
                {blockField(`${block.checked ? styles.todoChecked : ""}`)}
              </div>
              {renderChildren()}
            </div>
            {renderMarginNote()}
          </div>
        );
      case BLOCK_TYPES.TOGGLE:
        return (
          <div
            className={`${styles.block} ${getDragClassName()}`}
            key={block.id}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleBlockDropInternal}
            onClick={handleBlockClick}
          >
            {renderBlockActions()}
            <div className={styles.blockContentWrapper}>
              <div className={styles.toggleBlock}>
                <button
                  className={`${styles.toggleIcon} ${block.collapsed ? "" : styles.expanded}`}
                  onClick={() => {
                    const updatedBlocks = updateBlockInList(
                      activePage.blocks,
                      block.id,
                      { collapsed: !block.collapsed },
                    );
                    const updatedPage = {
                      ...activePage,
                      blocks: updatedBlocks,
                    };
                    setActivePage(updatedPage);
                    setNotebooks((prev) =>
                      updateItemInTree(prev, activePage.id, updatedPage),
                    );
                  }}
                >
                  <ChevronRight size={16} />
                </button>
                <div className={styles.toggleHeader}>
                  <textarea {...textareaProps} />
                </div>
              </div>
              {renderChildren()}
            </div>
            {renderMarginNote()}
          </div>
        );
      case BLOCK_TYPES.QUOTE:
        return (
          <div
            className={`${styles.block} ${getDragClassName()}`}
            key={block.id}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleBlockDropInternal}
            onClick={handleBlockClick}
          >
            {renderBlockActions()}
            <div className={styles.blockContentWrapper}>
              <blockquote className={styles.quoteBlock}>
                <textarea
                  {...textareaProps}
                  className={`${styles.blockTextarea} ${styles.quoteText}`}
                />
              </blockquote>
              {renderChildren()}
            </div>
            {renderMarginNote()}
          </div>
        );
      case BLOCK_TYPES.CODE:
        return (
          <div
            className={`${styles.block} ${getDragClassName()}`}
            key={block.id}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleBlockDropInternal}
            onClick={handleBlockClick}
          >
            {renderBlockActions()}
            <div className={styles.blockContentWrapper}>
              <div className={styles.codeBlock}>
                <textarea
                  {...textareaProps}
                  className={`${styles.blockTextarea} ${styles.codeText}`}
                />
              </div>
              {renderChildren()}
            </div>
            {renderMarginNote()}
          </div>
        );
      case BLOCK_TYPES.CALLOUT:
        return (
          <div
            className={`${styles.block} ${getDragClassName()}`}
            key={block.id}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleBlockDropInternal}
            onClick={handleBlockClick}
          >
            {renderBlockActions()}
            <div className={styles.blockContentWrapper}>
              <div className={styles.calloutBlock}>
                <Info size={18} className={styles.calloutIcon} />
                <textarea {...textareaProps} />
              </div>
              {renderChildren()}
            </div>
            {renderMarginNote()}
          </div>
        );
      case BLOCK_TYPES.FILE:
        return (
          <div
            className={`${styles.block} ${getDragClassName()}`}
            key={block.id}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleBlockDropInternal}
            onClick={handleBlockClick}
          >
            {renderBlockActions()}
            <div className={styles.blockContentWrapper}>
              <div className={styles.fileBlock}>
                <FileText size={18} className={styles.fileIcon} />
                {block.fileData ? (
                  <div className={styles.fileInfo}>
                    <span className={styles.fileName}>{block.fileName}</span>
                    <button
                      className={styles.fileDelete}
                      onClick={() =>
                        handleBlockChange(block.id, {
                          content: "",
                          fileData: null,
                          fileName: null,
                        })
                      }
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <div className={styles.fileUpload}>
                    <input
                      type="file"
                      id={`file-${block.id}`}
                      className={styles.hiddenInput}
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onload = (event) => {
                            const updatedBlocks = updateBlockInList(
                              activePage.blocks,
                              block.id,
                              {
                                fileData: event.target.result,
                                fileName: file.name,
                              },
                            );
                            const updatedPage = {
                              ...activePage,
                              blocks: updatedBlocks,
                            };
                            setActivePage(updatedPage);
                            setNotebooks((prev) =>
                              updateItemInTree(
                                prev,
                                activePage.id,
                                updatedPage,
                              ),
                            );
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                    />
                    <label
                      htmlFor={`file-${block.id}`}
                      className={styles.fileLabel}
                    >
                      Upload a file
                    </label>
                  </div>
                )}
              </div>
              {renderChildren()}
            </div>
            {renderMarginNote()}
          </div>
        );
      case BLOCK_TYPES.IMAGE:
        return (
          <div
            className={`${styles.block} ${getDragClassName()}`}
            key={block.id}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleBlockDropInternal}
            onClick={handleBlockClick}
          >
            {renderBlockActions()}
            <div className={styles.blockContentWrapper}>
              <div className={styles.imageBlock}>
                {block.imageData ? (
                  <div className={styles.imageContainer}>
                    <img
                      src={block.imageData}
                      alt="Uploaded content"
                      className={styles.uploadedImage}
                    />
                    <button
                      className={styles.imageDelete}
                      onClick={() =>
                        handleBlockChange(block.id, {
                          content: "",
                          imageData: null,
                        })
                      }
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <div className={styles.imageUpload}>
                    <input
                      type="file"
                      accept="image/*"
                      id={`image-${block.id}`}
                      className={styles.hiddenInput}
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onload = (event) => {
                            const updatedBlocks = updateBlockInList(
                              activePage.blocks,
                              block.id,
                              {
                                imageData: event.target.result,
                              },
                            );
                            const updatedPage = {
                              ...activePage,
                              blocks: updatedBlocks,
                            };
                            setActivePage(updatedPage);
                            setNotebooks((prev) =>
                              updateItemInTree(
                                prev,
                                activePage.id,
                                updatedPage,
                              ),
                            );
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                    />
                    <label
                      htmlFor={`image-${block.id}`}
                      className={styles.imageLabel}
                    >
                      <Plus size={18} />
                      <span>Add an image</span>
                    </label>
                  </div>
                )}
              </div>
              {renderChildren()}
            </div>
            {renderMarginNote()}
          </div>
        );
      case BLOCK_TYPES.COLUMN_GROUP:
        return (
          <div
            className={`${styles.block} ${styles.columnGroup} ${getDragClassName()}`}
            key={block.id}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleBlockDropInternal}
            onClick={handleBlockClick}
          >
            {renderBlockActions()}
            <div className={styles.columnGroupContent}>
              {block.children.map((child, childIndex) =>
                renderBlock(child, childIndex),
              )}
            </div>
          </div>
        );
      case BLOCK_TYPES.COLUMN:
        return (
          <div
            className={`${styles.block} ${styles.column} ${getDragClassName()}`}
            key={block.id}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleBlockDropInternal}
            onClick={handleBlockClick}
          >
            <div className={styles.columnContent}>{renderChildren()}</div>
          </div>
        );
      case BLOCK_TYPES.DIVIDER:
        return (
          <div
            className={`${styles.block} ${getDragClassName()}`}
            key={block.id}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleBlockDropInternal}
            onClick={handleBlockClick}
          >
            {renderBlockActions()}
            <div className={styles.dividerBlock}>
              <hr />
            </div>
            {renderChildren()}
          </div>
        );
      default:
        return (
          <div
            className={`${styles.block} ${getDragClassName()}`}
            key={block.id}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleBlockDropInternal}
            onClick={handleBlockClick}
          >
            {renderBlockActions()}
            <div className={styles.blockContentWrapper}>
              {blockField()}
              {renderChildren()}
            </div>
            {renderMarginNote()}
          </div>
        );
    }
  };

  // Filter slash menu options based on input
  const filteredSlashOptions = slashMenuOptions.filter(
    (opt) =>
      slashFilter === "" ||
      opt.label.toLowerCase().includes(slashFilter) ||
      opt.shortcut.includes(slashFilter) ||
      opt.description.toLowerCase().includes(slashFilter),
  );

  // Global key handlers for Escape and Cmd+K
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (e.key === "Escape") {
        setShowSlashMenu(false);
        setShowColorPicker(false);
        setActiveMenu(null);
        setShowTrash(false);
        setShowQuickSwitcher(false);
        clearSelection();
      }
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setShowQuickSwitcher(true);
        setSwitcherSearchQuery("");
      }
    };
    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => window.removeEventListener("keydown", handleGlobalKeyDown);
  }, [clearSelection]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (slashMenuRef.current && !slashMenuRef.current.contains(e.target)) {
        setShowSlashMenu(false);
      }
      if (contextMenu && !e.target.closest(`.${styles.contextMenu}`)) {
        setContextMenu(null);
      }
      if (
        activeMenu?.type === "convert" &&
        !e.target.closest(`.${styles.slashMenu}`)
      ) {
        setActiveMenu(null);
      }
      if (
        activeMenu?.type === "handle" &&
        !e.target.closest(`.${styles.handleMenu}`)
      ) {
        setActiveMenu(null);
      }
      if (
        activeMenu?.type === "blockColor" &&
        !e.target.closest(`.${styles.blockColorMenu}`)
      ) {
        setActiveMenu(null);
      }
      if (
        showColorPicker &&
        !e.target.closest(`.${styles.colorPickerDropdown}`) &&
        !e.target.closest(`.${styles.pageTitleIcon}`)
      ) {
        setShowColorPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [contextMenu, activeMenu, showColorPicker]);

  const handleEditorBackgroundMouseDown = useCallback(
    (e) => {
      if (!activePage) return;

      if (
        e.target.closest("textarea") ||
        e.target.closest("input") ||
        e.target.closest("button")
      )
        return;
      if (
        e.target.closest(`.${styles.slashMenu}`) ||
        e.target.closest(`.${styles.contextMenu}`) ||
        e.target.closest(`.${styles.handleMenu}`)
      )
        return;
      if (
        e.target.closest(`.${styles.block}`) ||
        e.target.closest(`.${styles.blockHandle}`) ||
        e.target.closest(`.${styles.selectionToolbar}`)
      )
        return;

      e.preventDefault();

      const flatBlocks = getFlatBlockList(activePage.blocks);
      const preferredId =
        activeBlockId ||
        (flatBlocks.length ? flatBlocks[flatBlocks.length - 1].id : null);

      if (preferredId && blockRefs.current[preferredId]) {
        blockRefs.current[preferredId].focus();
        return;
      }

      const newBlock = createBlock();
      const updatedPage = {
        ...activePage,
        blocks: [...activePage.blocks, newBlock],
      };
      setActivePage(updatedPage);
      setNotebooks((prev) =>
        updateItemInTree(prev, activePage.id, updatedPage),
      );
      setTimeout(() => {
        const el = blockRefs.current[newBlock.id];
        if (el) el.focus();
      }, 10);
    },
    [
      activeBlockId,
      activePage,
      getFlatBlockList,
      setNotebooks,
      updateItemInTree,
    ],
  );

  return (
    <div
      className={`${styles.container} ${darkMode ? styles.dark : styles.light} ${isAiFloatingModal ? styles.containerAiDropdownOpen : ""}`}
      style={hideSidebar ? { width: "100%", minWidth: 0 } : undefined}
    >
      {/* Ambient Animated Background */}
      <div className={styles.ambientBackground}>
        <div className={styles.ambientOrb} />
        <div className={styles.ambientOrb} />
        <div className={styles.ambientOrb} />
      </div>

      {/* Sidebar — hidden when embedded in HomePage (sidebar is shown there instead) */}
      <div
        style={hideSidebar ? { display: "none" } : undefined}
        className={`${styles.sidebar} ${sidebarCollapsed && !sidebarHovered ? styles.collapsed : ""} ${sidebarHovered && sidebarCollapsed ? styles.hoveredOpen : ""}`}
        onMouseEnter={() => {
          if (sidebarCollapsed) {
            clearTimeout(sidebarHoverTimeoutRef.current);
            sidebarHoverTimeoutRef.current = setTimeout(() => {
              setSidebarHovered(true);
            }, 150);
          }
        }}
        onMouseLeave={() => {
          clearTimeout(sidebarHoverTimeoutRef.current);
          setSidebarHovered(false);
        }}
      >
        <div className={styles.sidebarHeader}>
          <button
            className={styles.menuBtn}
            onClick={() => {
              setSidebarCollapsed(!sidebarCollapsed);
              setSidebarHovered(false);
            }}
            title={sidebarCollapsed ? "Open sidebar" : "Close sidebar"}
          >
            <Menu size={18} />
          </button>
          <div
            className={`${styles.workspaceSelector} ${sidebarCollapsed && !sidebarHovered ? styles.hidden : ""}`}
          >
            <BookOpen size={18} />
            <span className={styles.workspaceName}>Notebooks</span>
          </div>
        </div>

        <div
          className={`${styles.sidebarContent} ${sidebarCollapsed && !sidebarHovered ? styles.hidden : ""}`}
        >
          <div className={styles.sidebarSearch}>
            <Search size={16} />
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
            />
            {searchQuery && (
              <button
                className={styles.clearSearch}
                onClick={() => setSearchQuery("")}
              >
                <X size={14} />
              </button>
            )}
          </div>

          <div className={styles.sidebarNav}>
            <button
              className={`${styles.navItem} ${!activePage ? styles.active : ""}`}
              onClick={() => handlePageSelect(null)}
            >
              <Home size={16} />
              <span>Home</span>
            </button>
            <button
              className={styles.navItem}
              onClick={() => {
                setShowQuickSwitcher(true);
                setSwitcherSearchQuery("");
              }}
            >
              <Search size={16} />
              <span>Search</span>
            </button>
            <button
              className={styles.navItem}
              onClick={() => setShowTrash(true)}
            >
              <Trash2 size={16} />
              <span>Trash</span>
            </button>
            <button className={styles.navItem} onClick={toggleTheme}>
              {darkMode ? <Sun size={16} /> : <Moon size={16} />}
              <span>{darkMode ? "Light mode" : "Dark mode"}</span>
            </button>
            <button
              className={styles.navItem}
              onClick={() => {
                setStudyMode(true);
                // Auto-enter fullscreen for immersive experience
                setTimeout(() => {
                  document.documentElement
                    .requestFullscreen?.()
                    .catch(() => {});
                }, 100);
              }}
            >
              <BookOpen size={16} />
              <span>Focus Mode</span>
            </button>
          </div>

          {notebooks.some((item) =>
            (function hasFavorite(n) {
              if (n.isFavorite) return true;
              if (n.children) return n.children.some(hasFavorite);
              return false;
            })(item),
          ) && (
            <div className={styles.sidebarSection}>
              <div
                className={styles.sectionHeader}
                onClick={() => toggleSidebarSection("favorites")}
              >
                <div className={styles.sectionHeaderLeft}>
                  {sidebarSections.favorites ? (
                    <ChevronDown size={14} />
                  ) : (
                    <ChevronRight size={14} />
                  )}
                  <span>Favorites</span>
                </div>
              </div>
              {sidebarSections.favorites && (
                <div className={styles.sidebarList}>
                  {(function getFavorites(items) {
                    let favs = [];
                    items.forEach((item) => {
                      if (item.isFavorite) favs.push(item);
                      if (item.children)
                        favs = [...favs, ...getFavorites(item.children)];
                    });
                    return favs;
                  })(notebooks).map((item) => renderSidebarItem(item))}
                </div>
              )}
            </div>
          )}

          <div className={styles.sidebarDivider} />

          <div className={styles.sidebarSection}>
            <div
              className={styles.sectionHeader}
              onClick={() => toggleSidebarSection("private")}
            >
              <div className={styles.sectionHeaderLeft}>
                {sidebarSections.private ? (
                  <ChevronDown size={14} />
                ) : (
                  <ChevronRight size={14} />
                )}
                <span>My Notes</span>
              </div>
              <div className={styles.sectionActions}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCreateFolder("root");
                  }}
                  title="New subject"
                >
                  <Folder size={14} />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCreatePage("root");
                  }}
                  title="New page"
                >
                  <Plus size={14} />
                </button>
              </div>
            </div>
            {sidebarSections.private && (
              <>
                <div className={styles.sidebarList}>
                  {filteredNotebooks.length > 0 ? (
                    filteredNotebooks.map((item) => renderSidebarItem(item))
                  ) : (
                    <div className={styles.noResults}>No matches found</div>
                  )}
                </div>
                <button
                  className={styles.addSidebarPageBtn}
                  onClick={() => handleCreatePage("root")}
                >
                  <Plus size={14} />
                  <span>Add a page</span>
                </button>
              </>
            )}
          </div>

          <div className={styles.sidebarDivider} />

          {/* Study Tools Section */}
          <div className={styles.studyToolsSection}>
            <button
              className={styles.studyToolsHeader}
              onClick={() => setStudyToolsExpanded((v) => !v)}
            >
              <Brain size={15} className={styles.studyToolsHeaderIcon} />
              <span>Study Tools</span>
              <ChevronRight
                size={13}
                className={`${styles.studyToolsChevron} ${studyToolsExpanded ? styles.expanded : ""}`}
              />
            </button>

            {studyToolsExpanded && (
              <div className={styles.studyToolsList}>
                {STUDY_TOOLS.map((tool) => {
                  const ToolIcon = tool.icon;
                  const isExpanded = expandedStudyTool === tool.id;
                  return (
                    <div key={tool.id} className={styles.studyToolWrapper}>
                      <button
                        className={`${styles.studyToolBtn} ${isExpanded ? styles.active : ""}`}
                        onClick={() =>
                          setExpandedStudyTool(isExpanded ? null : tool.id)
                        }
                        style={{ "--tool-color": tool.color }}
                      >
                        <ToolIcon size={14} className={styles.studyToolIcon} />
                        <span>{tool.label}</span>
                        <ChevronRight
                          size={11}
                          className={`${styles.studyToolChevron} ${isExpanded ? styles.expanded : ""}`}
                        />
                      </button>

                      {isExpanded && (
                        <div className={styles.studyToolDocs}>
                          {studyDocs.length === 0 ? (
                            <div className={styles.studyNoDoc}>
                              <p>No documents uploaded yet.</p>
                              <button
                                className={styles.studyUploadBtn}
                                onClick={() => studyUploadRef.current?.click()}
                              >
                                <Plus size={12} />
                                <span>Upload</span>
                              </button>
                            </div>
                          ) : (
                            <>
                              {studyDocs.map((doc) => (
                                <button
                                  key={doc.id}
                                  className={styles.studyDocItem}
                                  disabled={isGeneratingStudy}
                                  onClick={() => {
                                    setExpandedStudyTool(null);
                                    handleStudyGenerate(tool, doc);
                                  }}
                                >
                                  <FileText size={11} />
                                  <span>
                                    {doc.name.length > 24
                                      ? doc.name.slice(0, 24) + "…"
                                      : doc.name}
                                  </span>
                                </button>
                              ))}
                              <button
                                className={`${styles.studyDocItem} ${styles.uploadRow}`}
                                onClick={() => studyUploadRef.current?.click()}
                              >
                                <Plus size={11} />
                                <span>Upload new doc</span>
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Hidden file input shared by all tools */}
                <input
                  ref={studyUploadRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.txt,.md"
                  style={{ display: "none" }}
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    await saveFileToStorage(f);
                    setStudyDocs(getAllFiles());
                    window.dispatchEvent(new CustomEvent("filesUpdated"));
                    e.target.value = "";
                  }}
                />
              </div>
            )}
          </div>

          <div className={styles.sidebarDivider} />

          <div className={styles.sidebarBottom}>
            <button
              className={styles.navItem}
              onClick={() => handleCreatePage("root")}
            >
              <Plus size={16} />
              <span>New page</span>
            </button>
            <button
              className={styles.navItem}
              onClick={() => {
                // Open general settings or show a modal
                alert("Settings coming soon in this demo!");
              }}
            >
              <Settings size={16} />
              <span>Settings</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div
        className={`${styles.main} ${isAiFloatingModal ? styles.mainAiDropdownOpen : ""}`}
      >
        {/* Top Bar */}
        <div
          className={`${styles.topBar} ${isAiFloatingModal ? styles.topBarWithAiDropdown : ""}`}
        >
          <div className={styles.topBarLeft}>
            <div className={styles.breadcrumb}>
              {!hideSidebar && sidebarCollapsed && (
                <button
                  className={styles.topBarMenuToggle}
                  onClick={() => {
                    setSidebarCollapsed(false);
                    setSidebarHovered(false);
                  }}
                  title="Open sidebar"
                >
                  <Menu size={18} />
                </button>
              )}
              {!hideSidebar && (
                <button
                  className={styles.backBtn}
                  onClick={() =>
                    onBackToChat ? onBackToChat() : navigate("/home")
                  }
                  title={onBackToChat ? "Back to Chat" : "Back to home"}
                >
                  <ArrowLeft size={18} />
                </button>
              )}
              <div className={styles.breadcrumbPath}>
                {!hideSidebar && (
                  <button
                    className={styles.breadcrumbStep}
                    onClick={() => handlePageSelect(null)}
                  >
                    <BookOpen size={14} />
                    <span>Notebooks</span>
                  </button>
                )}
                {activePage && (
                  <>
                    {!hideSidebar && (
                      <ChevronRight
                        size={12}
                        className={styles.breadcrumbSeparator}
                      />
                    )}
                    <div
                      className={`${styles.breadcrumbStep} ${styles.breadcrumbActive}`}
                      title={activePage.title || "Untitled"}
                    >
                      <FileText size={14} />
                      <span>{activePage.title || "Untitled"}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div
            ref={topBarAiHostRef}
            className={`${styles.topBarCenter} ${styles.topBarAiHost}`}
          >
            {activePage && activePage.type !== "assignment" && (
              <button
                ref={topBarAskPillRef}
                type="button"
                className={`${styles.topBarAskPill} ${showAskBar ? styles.topBarAskPillActive : ""}`}
                onClick={openNotebookAskBar}
                title="Ask AI about this page"
              >
                <Sparkles size={14} strokeWidth={2} aria-hidden />
                <span>Ask AI about this page</span>
              </button>
            )}
            {false && (
              <div
                ref={aiInlineSurfaceRef}
                className={`${styles.aiInlineSurface} ${styles.aiModalShell}`}
                role="dialog"
                aria-label="AI assistant"
              >
                <div className={styles.aiModalHeader}>
                  <div className={styles.dockTabs}>
                    <button
                      className={`${styles.dockTabBtn} ${dockTab === "docs" ? styles.active : ""}`}
                      onClick={() => setDockTab("docs")}
                      type="button"
                    >
                      <FileText size={13} />
                      <span>Documents</span>
                    </button>
                    <button
                      className={`${styles.dockTabBtn} ${dockTab === "ai" ? styles.active : ""}`}
                      onClick={() => setDockTab("ai")}
                      type="button"
                    >
                      <Sparkles size={13} />
                      <span>AI</span>
                    </button>
                    <button
                      className={`${styles.dockTabBtn} ${dockTab === "study" ? styles.active : ""}`}
                      onClick={() => setDockTab("study")}
                      type="button"
                    >
                      <Brain size={13} />
                      <span>Study</span>
                      {studyContent &&
                        studyContent.type !== "loading" &&
                        studyContent.type !== "error" && (
                          <span className={styles.dockTabDot} />
                        )}
                    </button>
                    <button
                      className={`${styles.dockTabBtn} ${dockTab === "assign" ? styles.active : ""}`}
                      onClick={() => setDockTab("assign")}
                      type="button"
                    >
                      <NotebookPen size={13} />
                      <span>Assign</span>
                    </button>
                  </div>
                  <div className={styles.dockActions}>
                    <button
                      className={styles.topBarBtn}
                      onClick={closeAiModalAnimated}
                      type="button"
                      title="Close"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
                <div
                  className={`${styles.aiDockInputArea} ${styles.aiInlineInputArea}`}
                >
                  <div className={styles.aiDockInputWrap}>
                    <textarea
                      ref={aiDockInputRef}
                      className={styles.aiDockInput}
                      placeholder="Ask AI about these notes…"
                      value={aiContextQuery}
                      rows={1}
                      onChange={(e) => {
                        setAiContextQuery(e.target.value);
                        e.target.style.height = "auto";
                        e.target.style.height =
                          Math.min(e.target.scrollHeight, 120) + "px";
                      }}
                      onKeyDown={(e) => {
                        if (
                          e.key === "Enter" &&
                          !e.shiftKey &&
                          aiContextQuery.trim() &&
                          !aiContextLoading
                        ) {
                          e.preventDefault();
                          handleContextualAI("custom", aiContextQuery);
                          setAiContextQuery("");
                          e.target.style.height = "auto";
                        }
                      }}
                      disabled={aiContextLoading}
                    />
                    <button
                      className={styles.aiDockSend}
                      disabled={!aiContextQuery.trim() || aiContextLoading}
                      onClick={() => {
                        if (aiContextQuery.trim()) {
                          handleContextualAI("custom", aiContextQuery);
                          setAiContextQuery("");
                        }
                      }}
                    >
                      <Send size={14} />
                    </button>
                  </div>
                </div>
                <div ref={aiInlineDropRef} className={styles.aiInlineDrop}>
                  <div
                    ref={aiInlineDropInnerRef}
                    className={styles.aiInlineDropInner}
                  >
                    <div className={styles.aiModalBody}>
                      <div className={styles.aiContextPane}>
                        <div className={styles.aiContextHeader}>
                          <div className={styles.aiContextPagePill}>
                            <Sparkles size={12} />
                            <span>
                              {activePage
                                ? activePage.title || "Untitled"
                                : "No page open"}
                            </span>
                          </div>
                          {aiDockMessages.length > 0 && (
                            <button
                              className={styles.aiContextClear}
                              onClick={() => setAiDockMessages([])}
                              title="Clear chat"
                            >
                              <X size={12} />
                            </button>
                          )}
                        </div>

                        <div className={styles.aiChipsRow}>
                          {[
                            { id: "summarize", label: "Summarize", icon: Zap },
                            { id: "quiz", label: "Quiz", icon: Brain },
                            { id: "explain", label: "Explain", icon: Sparkles },
                          ].map(({ id, label, icon: Icon }) => (
                            <button
                              key={id}
                              className={styles.aiChip}
                              onClick={() => handleContextualAI(id)}
                              disabled={!activePage || aiContextLoading}
                            >
                              <Icon size={11} />
                              {label}
                            </button>
                          ))}
                        </div>

                        <div
                          className={styles.aiDockThread}
                          ref={aiDockThreadRef}
                        >
                          {aiDockMessages.length === 0 && !aiContextLoading && (
                            <div className={styles.aiDockEmptyState}>
                              <Sparkles size={30} strokeWidth={1.1} />
                              <p>
                                Ask anything about your notes, or pick an
                                action above
                              </p>
                            </div>
                          )}
                          {aiDockMessages.map((msg, idx) => (
                            <div
                              key={idx}
                              className={`${styles.aiDockMsg} ${msg.role === "user" ? styles.aiDockMsgUser : styles.aiDockMsgAI}`}
                            >
                              {msg.role === "assistant" && (
                                <div className={styles.aiDockMsgAvatar}>
                                  <Sparkles size={11} />
                                </div>
                              )}
                              <div
                                className={`${styles.aiDockMsgBubble} ${msg.error ? styles.aiDockMsgError : ""}`}
                              >
                                {msg.content}
                              </div>
                              {msg.role === "assistant" && !msg.error && (
                                <div className={styles.aiDockMsgActions}>
                                  <button
                                    className={styles.aiDockMsgAction}
                                    onClick={() =>
                                      insertAssistantMarkdownIntoNotes(
                                        msg.content,
                                      )
                                    }
                                    title="Insert into page below cursor"
                                  >
                                    <Plus size={11} />
                                    Insert
                                  </button>
                                  <button
                                    className={styles.aiDockMsgAction}
                                    onClick={() =>
                                      navigator.clipboard?.writeText(
                                        msg.content,
                                      )
                                    }
                                    title="Copy"
                                  >
                                    <Copy size={11} />
                                    Copy
                                  </button>
                                </div>
                              )}
                            </div>
                          ))}
                          {aiContextLoading && (
                            <div
                              className={`${styles.aiDockMsg} ${styles.aiDockMsgAI}`}
                            >
                              <div className={styles.aiDockMsgAvatar}>
                                <Sparkles
                                  size={11}
                                  className={styles.spin}
                                />
                              </div>
                              <div className={styles.aiDockMsgBubble}>
                                <div className={styles.aiDockThinking}>
                                  <span />
                                  <span />
                                  <span />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className={styles.topBarRight}>
            {/* Mini Pomodoro Timer - Available in all modes */}
            <div className={styles.miniPomodoro}>
              <button
                className={`${styles.miniPomodoroBtn} ${studyTimerRunning ? styles.active : ""}`}
                onClick={() => setStudyTimerRunning(!studyTimerRunning)}
                title={studyTimerRunning ? "Pause timer" : "Start timer"}
              >
                {studyTimerRunning ? <Pause size={14} /> : <Play size={14} />}
              </button>
              <div className={styles.miniPomodoroTime}>
                <span
                  className={
                    pomodoroMode === "work" ? styles.workMode : styles.breakMode
                  }
                >
                  {String(Math.floor(studyTimer / 60)).padStart(2, "0")}:
                  {String(studyTimer % 60).padStart(2, "0")}
                </span>
              </div>
              <button
                className={styles.miniPomodoroBtn}
                onClick={() => {
                  setStudyTimerRunning(false);
                  setStudyTimer(pomodoroSettings.workDuration * 60);
                  setPomodoroMode("work");
                }}
                title="Reset timer"
              >
                <RotateCcw size={14} />
              </button>
              <div className={styles.miniPomodoroModes}>
                <button
                  className={`${styles.miniModeBtn} ${pomodoroMode === "work" ? styles.active : ""}`}
                  onClick={() => {
                    setPomodoroMode("work");
                    setStudyTimer(pomodoroSettings.workDuration * 60);
                    setStudyTimerRunning(false);
                  }}
                  title="Work mode (25 min)"
                >
                  W
                </button>
                <button
                  className={`${styles.miniModeBtn} ${pomodoroMode === "break" ? styles.active : ""}`}
                  onClick={() => {
                    setPomodoroMode("break");
                    setStudyTimer(pomodoroSettings.breakDuration * 60);
                    setStudyTimerRunning(false);
                  }}
                  title="Break mode (5 min)"
                >
                  B
                </button>
              </div>
            </div>

            <div className={styles.topBarDivider} />

            {/* Word count */}
            {activePage && (
              <div className={styles.wordCount} title="Word count">
                {activePage.blocks
                  .map(
                    (b) =>
                      (b.content || "").trim().split(/\s+/).filter(Boolean)
                        .length,
                  )
                  .reduce((a, b) => a + b, 0)}{" "}
                words
              </div>
            )}

            {/* Page settings dropdown */}
            {activePage && (
              <div className={styles.pageSettingsWrapper}>
                <button
                  className={`${styles.topBarBtn} ${showPageSettings ? styles.active : ""}`}
                  onClick={() => setShowPageSettings((v) => !v)}
                  title="Page style settings"
                >
                  <PenTool size={16} />
                </button>
                {showPageSettings && (
                  <div
                    className={styles.pageSettingsPanel}
                    onMouseLeave={() => setShowPageSettings(false)}
                  >
                    {/* Font family */}
                    <div className={styles.settingsGroup}>
                      <div className={styles.settingsGroupLabel}>Typeface</div>
                      <div className={styles.fontPicker}>
                        {[
                          { label: "Sans", value: "" },
                          { label: "Serif", value: "serif" },
                          { label: "Mono", value: "mono" },
                          { label: "Script", value: "cursive" },
                          { label: "Display", value: "display" },
                          { label: "Narrow", value: "narrow" },
                        ].map(({ label, value }) => (
                          <button
                            key={label}
                            className={`${styles.fontPickerBtn} ${(activePage.properties?.font || "") === value ? styles.active : ""}`}
                            onClick={() => {
                              const updated = {
                                ...activePage,
                                properties: {
                                  ...activePage.properties,
                                  font: value,
                                },
                              };
                              setActivePage(updated);
                              setNotebooks((prev) =>
                                updateItemInTree(prev, activePage.id, updated),
                              );
                            }}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Font size */}
                    <div className={styles.settingsGroup}>
                      <div className={styles.settingsGroupLabel}>Text Size</div>
                      <div className={styles.fontPicker}>
                        {[
                          { label: "S", value: "sm" },
                          { label: "M", value: "" },
                          { label: "L", value: "lg" },
                          { label: "XL", value: "xl" },
                        ].map(({ label, value }) => (
                          <button
                            key={label}
                            className={`${styles.fontPickerBtn} ${(activePage.properties?.fontSize || "") === value ? styles.active : ""}`}
                            onClick={() => {
                              const updated = {
                                ...activePage,
                                properties: {
                                  ...activePage.properties,
                                  fontSize: value,
                                },
                              };
                              setActivePage(updated);
                              setNotebooks((prev) =>
                                updateItemInTree(prev, activePage.id, updated),
                              );
                            }}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Line height */}
                    <div className={styles.settingsGroup}>
                      <div className={styles.settingsGroupLabel}>Spacing</div>
                      <div className={styles.fontPicker}>
                        {[
                          { label: "Tight", value: "tight" },
                          { label: "Normal", value: "" },
                          { label: "Relaxed", value: "relaxed" },
                          { label: "Loose", value: "loose" },
                        ].map(({ label, value }) => (
                          <button
                            key={label}
                            className={`${styles.fontPickerBtn} ${(activePage.properties?.lineHeight || "") === value ? styles.active : ""}`}
                            onClick={() => {
                              const updated = {
                                ...activePage,
                                properties: {
                                  ...activePage.properties,
                                  lineHeight: value,
                                },
                              };
                              setActivePage(updated);
                              setNotebooks((prev) =>
                                updateItemInTree(prev, activePage.id, updated),
                              );
                            }}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Page width */}
                    <div className={styles.settingsGroup}>
                      <div className={styles.settingsGroupLabel}>Width</div>
                      <div className={styles.fontPicker}>
                        {["Normal", "Wide"].map((w) => (
                          <button
                            key={w}
                            className={`${styles.fontPickerBtn} ${(activePage.properties?.width || "Normal") === w ? styles.active : ""}`}
                            onClick={() => {
                              const updated = {
                                ...activePage,
                                properties: {
                                  ...activePage.properties,
                                  width: w,
                                },
                              };
                              setActivePage(updated);
                              setNotebooks((prev) =>
                                updateItemInTree(prev, activePage.id, updated),
                              );
                            }}
                          >
                            {w}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activePage && (
              <button
                className={styles.studyPackBtn}
                onClick={handleStudyThisPage}
                title="Generate summary, quiz & flashcards from this page"
              >
                <Zap size={13} />
                Study This Page
              </button>
            )}

            <button
              className={styles.topBarBtn}
              onClick={() => handleOpenDock()}
              title="Open document panel"
            >
              <FileText
                size={18}
                style={{
                  color: showAiPanel
                    ? "var(--accent-secondary)"
                    : "currentColor",
                }}
              />
            </button>
            <div
              className={`${styles.saveIndicator} ${saveStatus === "saving" ? styles.isSaving : ""}`}
            >
              {saveStatus === "saving" ? (
                <>
                  <RefreshCw size={12} className={styles.spin} />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <CheckSquare size={12} />
                  <span>Saved</span>
                </>
              )}
            </div>
            <button
              className={styles.topBarBtn}
              onClick={() => activePage && handleToggleFavorite(activePage.id)}
              title="Add to favorites"
            >
              <Star
                size={18}
                fill={activePage?.isFavorite ? "currentColor" : "none"}
              />
            </button>
            <button
              className={styles.topBarBtn}
              onClick={toggleTheme}
              title="Toggle theme"
            >
              {darkMode ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            {activePage && (
              <button
                className={styles.topBarBtn}
                onClick={() => setShowProperties((v) => !v)}
                title="Toggle page properties"
              >
                <Info size={18} />
              </button>
            )}
            {activePage && <button className={styles.shareBtn}>Share</button>}
          </div>
        </div>

        {/* Inline Ask AI Bar - slides down below top bar */}
        <div className={`${styles.askBarWrap} ${showAskBar ? styles.askBarOpen : ""}`}>
          <div className={styles.askBar}>
            <div className={styles.askBarChips}>
              {[
                { id: "summarize", label: "Summarize" },
                { id: "explain", label: "Explain" },
                { id: "quiz", label: "Create Quiz" },
                { id: "outline", label: "Study Outline" },
                { id: "flashcards", label: "Flashcards" },
                { id: "gaps", label: "Find Gaps" },
              ].map(({ id, label }) => (
                <button
                  key={id}
                  className={styles.askBarChip}
                  onClick={() => {
                    handleContextualAI(id);
                    setShowAskBar(false);
                    toast.success(`AI is generating ${label.toLowerCase()}…`);
                  }}
                  disabled={aiContextLoading || !activePage}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className={styles.askBarInputRow}>
              <Sparkles size={15} className={styles.askBarIcon} />
              <textarea
                ref={askBarInputRef}
                className={styles.askBarInput}
                placeholder="Ask anything about this page… (Enter to send)"
                value={aiContextQuery}
                rows={1}
                onChange={(e) => {
                  setAiContextQuery(e.target.value);
                  e.target.style.height = "auto";
                  e.target.style.height = Math.min(e.target.scrollHeight, 100) + "px";
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    if (aiContextQuery.trim() && !aiContextLoading && activePage) {
                      handleContextualAI("custom", aiContextQuery);
                      setAiContextQuery("");
                      setShowAskBar(false);
                      e.target.style.height = "auto";
                      toast.success("AI is generating a response…");
                    }
                  }
                  if (e.key === "Escape") setShowAskBar(false);
                }}
                disabled={aiContextLoading}
              />
              <button
                className={styles.askBarSend}
                disabled={!aiContextQuery.trim() || aiContextLoading || !activePage}
                onClick={() => {
                  if (aiContextQuery.trim() && !aiContextLoading && activePage) {
                    handleContextualAI("custom", aiContextQuery);
                    setAiContextQuery("");
                    setShowAskBar(false);
                    toast.success("AI is generating a response…");
                  }
                }}
                title="Send (Enter)"
              >
                {aiContextLoading
                  ? <RefreshCw size={14} className={styles.spin} />
                  : <Send size={14} />
                }
              </button>
              <button
                className={styles.askBarClose}
                onClick={() => setShowAskBar(false)}
                title="Close (Esc)"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        </div>

        {/* Editor */}
        <div className={styles.editor}>
          {/* Selection Toolbar */}
          {selectedBlockIds.length > 0 && (
            <div className={styles.selectionToolbar}>
              <div className={styles.selectionCount}>
                {selectedBlockIds.length} block
                {selectedBlockIds.length > 1 ? "s" : ""} selected
              </div>
              <div className={styles.selectionDivider} />
              <button
                className={styles.selectionActionBtn}
                onClick={handleBulkDelete}
                title="Delete selected blocks"
              >
                <Trash2 size={16} />
                <span>Delete</span>
              </button>
              <div className={styles.selectionDivider} />
              <button
                className={styles.selectionActionBtn}
                onClick={() => handleBulkTransform(BLOCK_TYPES.BULLET)}
                title="Turn into bulleted list"
              >
                <List size={16} />
                <span>Bullet</span>
              </button>
              <button
                className={styles.selectionActionBtn}
                onClick={() => handleBulkTransform(BLOCK_TYPES.TODO)}
                title="Turn into to-do list"
              >
                <CheckSquare size={16} />
                <span>To-do</span>
              </button>
              <button
                className={styles.selectionActionBtn}
                onClick={() => handleBulkTransform(BLOCK_TYPES.TEXT)}
                title="Turn into text"
              >
                <Type size={16} />
                <span>Text</span>
              </button>
              <div className={styles.selectionDivider} />
              <button
                className={styles.selectionActionBtn}
                onClick={clearSelection}
                title="Clear selection"
              >
                <X size={16} />
                <span>Clear</span>
              </button>
            </div>
          )}

          {activePage ? (
            <div
              className={[
                styles.pageContent,
                activePage.properties?.font === "serif" ? styles.fontSerif : "",
                activePage.properties?.font === "mono" ? styles.fontMono : "",
                activePage.properties?.font === "cursive"
                  ? styles.fontCursive
                  : "",
                activePage.properties?.font === "display"
                  ? styles.fontDisplay
                  : "",
                activePage.properties?.font === "narrow"
                  ? styles.fontNarrow
                  : "",
                activePage.properties?.width === "Wide" ? styles.pageWide : "",
                activePage.properties?.fontSize === "sm"
                  ? styles.pageFontSm
                  : "",
                activePage.properties?.fontSize === "lg"
                  ? styles.pageFontLg
                  : "",
                activePage.properties?.fontSize === "xl"
                  ? styles.pageFontXl
                  : "",
                activePage.properties?.lineHeight === "tight"
                  ? styles.lineHeightTight
                  : "",
                activePage.properties?.lineHeight === "relaxed"
                  ? styles.lineHeightRelaxed
                  : "",
                activePage.properties?.lineHeight === "loose"
                  ? styles.lineHeightLoose
                  : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onMouseDown={handleEditorBackgroundMouseDown}
            >
              {/* Page Header - Just title */}
              <div className={styles.pageHeader}>
                {activePage.type === "assignment" && (
                  <div className={styles.assignmentBadge}>
                    <FileText size={12} />
                    <span>Assignment</span>
                  </div>
                )}
                <input
                  type="text"
                  className={styles.pageTitleInput}
                  value={activePage.title}
                  onChange={(e) => {
                    const newTitle = e.target.value;
                    setActivePage((prev) => ({ ...prev, title: newTitle }));
                    setNotebooks((prev) =>
                      updateItemInTree(prev, activePage.id, {
                        title: newTitle,
                      }),
                    );
                  }}
                  placeholder={
                    activePage.type === "assignment"
                      ? "Assignment title…"
                      : "Untitled"
                  }
                />
              </div>

              {activePage &&
                activePage.type !== "assignment" &&
                isNotebookPageSparse &&
                !showProperties && (
                  <div className={styles.pageStartWith}>
                    <div className={styles.pageStartWithLabel}>Start with</div>
                    <div className={styles.pageStartChips}>
                      {NOTEBOOK_START_CHIPS.map(
                        ({ id, label, Icon, color, mode }) => (
                          <button
                            key={id}
                            type="button"
                            className={styles.pageStartChip}
                            style={{ "--chip-accent": color }}
                            onClick={() => {
                              if (mode === "ask") openNotebookAskBar();
                              else handleContextualAI(id);
                            }}
                            disabled={aiContextLoading}
                          >
                            <span className={styles.pageStartChipIcon}>
                              <Icon size={15} strokeWidth={2} />
                            </span>
                            {label}
                          </button>
                        ),
                      )}
                    </div>
                  </div>
                )}

              {/* Assignment metadata bar */}
              {activePage.type === "assignment" && (
                <div className={styles.assignmentMeta}>
                  <div className={styles.assignmentMetaField}>
                    <label>Course</label>
                    <input
                      type="text"
                      placeholder="e.g. BIO 101"
                      value={activePage.properties?.course || ""}
                      onChange={(e) => {
                        const updated = {
                          ...activePage,
                          properties: {
                            ...activePage.properties,
                            course: e.target.value,
                          },
                        };
                        setActivePage(updated);
                        setNotebooks((prev) =>
                          updateItemInTree(prev, activePage.id, updated),
                        );
                      }}
                    />
                  </div>
                  <div className={styles.assignmentMetaField}>
                    <label>Due date</label>
                    <input
                      type="date"
                      value={activePage.properties?.dueDate || ""}
                      onChange={(e) => {
                        const updated = {
                          ...activePage,
                          properties: {
                            ...activePage.properties,
                            dueDate: e.target.value,
                          },
                        };
                        setActivePage(updated);
                        setNotebooks((prev) =>
                          updateItemInTree(prev, activePage.id, updated),
                        );
                      }}
                    />
                  </div>
                  <div className={styles.assignmentMetaField}>
                    <label>Points</label>
                    <input
                      type="number"
                      placeholder="100"
                      value={activePage.properties?.points || ""}
                      style={{ width: "64px" }}
                      onChange={(e) => {
                        const updated = {
                          ...activePage,
                          properties: {
                            ...activePage.properties,
                            points: e.target.value,
                          },
                        };
                        setActivePage(updated);
                        setNotebooks((prev) =>
                          updateItemInTree(prev, activePage.id, updated),
                        );
                      }}
                    />
                  </div>
                  <button
                    className={styles.assignmentAiBtn}
                    onClick={() => handleContextualAI("outline")}
                  >
                    <Sparkles size={13} />
                    AI Outline
                  </button>
                  <button
                    className={styles.assignmentAiBtn}
                    onClick={() => handleContextualAI("improve")}
                  >
                    <PenTool size={13} />
                    Improve Writing
                  </button>
                </div>
              )}

              {/* Page Properties */}
              {showProperties && (
                <div className={styles.pageProperties}>
                  <div className={styles.propertyRow}>
                    <div className={styles.propertyLabel}>
                      <RefreshCw size={14} />
                      <span>Status</span>
                    </div>
                    <select
                      className={styles.propertyValueSelect}
                      value={activePage.properties?.status || "Not started"}
                      onChange={(e) => {
                        const newStatus = e.target.value;
                        const updatedPage = {
                          ...activePage,
                          properties: {
                            ...activePage.properties,
                            status: newStatus,
                          },
                        };
                        setActivePage(updatedPage);
                        setNotebooks((prev) =>
                          updateItemInTree(prev, activePage.id, updatedPage),
                        );
                      }}
                    >
                      <option value="Not started">Not started</option>
                      <option value="In progress">In progress</option>
                      <option value="Completed">Done</option>
                      <option value="Archived">Archived</option>
                    </select>
                  </div>

                  <div className={styles.propertyRow}>
                    <div className={styles.propertyLabel}>
                      <Plus size={14} />
                      <span>Date</span>
                    </div>
                    <input
                      type="date"
                      className={styles.propertyValueInput}
                      value={activePage.properties?.date || ""}
                      onChange={(e) => {
                        const newDate = e.target.value;
                        const updatedPage = {
                          ...activePage,
                          properties: {
                            ...activePage.properties,
                            date: newDate,
                          },
                        };
                        setActivePage(updatedPage);
                        setNotebooks((prev) =>
                          updateItemInTree(prev, activePage.id, updatedPage),
                        );
                      }}
                    />
                  </div>

                  <div className={styles.propertyRow}>
                    <div className={styles.propertyLabel}>
                      <Hash size={14} />
                      <span>Tags</span>
                    </div>
                    <div className={styles.tagsContainer}>
                      {(activePage.properties?.tags || []).map((tag, idx) => (
                        <span key={idx} className={styles.tag}>
                          {tag}
                          <button
                            onClick={() => {
                              const newTags = activePage.properties.tags.filter(
                                (_, i) => i !== idx,
                              );
                              const updatedPage = {
                                ...activePage,
                                properties: {
                                  ...activePage.properties,
                                  tags: newTags,
                                },
                              };
                              setActivePage(updatedPage);
                              setNotebooks((prev) =>
                                updateItemInTree(
                                  prev,
                                  activePage.id,
                                  updatedPage,
                                ),
                              );
                            }}
                          >
                            <X size={10} />
                          </button>
                        </span>
                      ))}
                      <input
                        type="text"
                        className={styles.addTagInput}
                        placeholder="Add a tag..."
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && e.target.value.trim()) {
                            const newTag = e.target.value.trim();
                            if (!activePage.properties.tags.includes(newTag)) {
                              const newTags = [
                                ...(activePage.properties?.tags || []),
                                newTag,
                              ];
                              const updatedPage = {
                                ...activePage,
                                properties: {
                                  ...activePage.properties,
                                  tags: newTags,
                                },
                              };
                              setActivePage(updatedPage);
                              setNotebooks((prev) =>
                                updateItemInTree(
                                  prev,
                                  activePage.id,
                                  updatedPage,
                                ),
                              );
                            }
                            e.target.value = "";
                          }
                        }}
                      />
                    </div>
                  </div>

                  <div className={styles.addPropertyBtn}>
                    <Plus size={14} />
                    <span>Add a property</span>
                  </div>
                </div>
              )}

              <div className={styles.pageDivider} />

              {/* Tags Section */}
              <div className={styles.noteTags}>
                {(activePage.properties?.tags || []).map((tag, idx) => (
                  <span key={idx} className={styles.noteTag}>
                    <Tag size={10} />
                    {tag}
                    <button
                      className={styles.noteTagRemove}
                      onClick={() => {
                        const newTags = (
                          activePage.properties?.tags || []
                        ).filter((_, i) => i !== idx);
                        const updatedPage = {
                          ...activePage,
                          properties: {
                            ...activePage.properties,
                            tags: newTags,
                          },
                        };
                        setActivePage(updatedPage);
                        setNotebooks((prev) =>
                          updateItemInTree(prev, activePage.id, updatedPage),
                        );
                      }}
                      type="button"
                    >
                      <X size={8} />
                    </button>
                  </span>
                ))}
                {showTagInput ? (
                  <input
                    type="text"
                    className={styles.tagInput}
                    value={tagInputValue}
                    onChange={(e) => setTagInputValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && tagInputValue.trim()) {
                        const newTags = [
                          ...(activePage.properties?.tags || []),
                          tagInputValue.trim(),
                        ];
                        const updatedPage = {
                          ...activePage,
                          properties: {
                            ...activePage.properties,
                            tags: newTags,
                          },
                        };
                        setActivePage(updatedPage);
                        setNotebooks((prev) =>
                          updateItemInTree(prev, activePage.id, updatedPage),
                        );
                        setTagInputValue("");
                        setShowTagInput(false);
                      } else if (e.key === "Escape") {
                        setShowTagInput(false);
                        setTagInputValue("");
                      }
                    }}
                    onBlur={() => {
                      if (tagInputValue.trim()) {
                        const newTags = [
                          ...(activePage.properties?.tags || []),
                          tagInputValue.trim(),
                        ];
                        const updatedPage = {
                          ...activePage,
                          properties: {
                            ...activePage.properties,
                            tags: newTags,
                          },
                        };
                        setActivePage(updatedPage);
                        setNotebooks((prev) =>
                          updateItemInTree(prev, activePage.id, updatedPage),
                        );
                      }
                      setTagInputValue("");
                      setShowTagInput(false);
                    }}
                    placeholder="Add tag..."
                    autoFocus
                  />
                ) : (
                  <button
                    className={styles.addTagBtn}
                    onClick={() => setShowTagInput(true)}
                    type="button"
                  >
                    <Plus size={10} />
                    Add tag
                  </button>
                )}
              </div>

              {/* Enhanced Writing Area */}
              <div className={styles.writingArea}>
                <textarea
                  className={styles.pageTextarea}
                  value={activePage.blocks?.[0]?.content || ""}
                  onChange={(e) => {
                    if (activePage.blocks.length > 0) {
                      handleBlockChange(
                        activePage.blocks[0].id,
                        e.target.value,
                      );
                    }
                    // Check for slash command trigger
                    const val = e.target.value;
                    const cursorPos = e.target.selectionStart;
                    const lineStart = val.lastIndexOf("\n", cursorPos - 1) + 1;
                    const currentLine = val.substring(lineStart, cursorPos);

                    if (currentLine.startsWith("/")) {
                      const rect = e.target.getBoundingClientRect();
                      setSlashMenuPosition({
                        x: rect.left + 60,
                        y: rect.top + 80,
                      });
                      setShowSlashMenu(true);
                      setSlashFilter(currentLine.slice(1).toLowerCase());
                      setActiveBlockId(activePage.blocks[0]?.id);
                    } else if (showSlashMenu) {
                      setShowSlashMenu(false);
                      setSlashFilter("");
                    }
                  }}
                  ref={activeTextareaRef}
                  onKeyDown={(e) => {
                    // Handle slash menu navigation
                    if (showSlashMenu) {
                      const filtered = slashMenuOptions.filter(
                        (opt) =>
                          opt.label.toLowerCase().includes(slashFilter) ||
                          opt.shortcut.includes(slashFilter) ||
                          opt.description.toLowerCase().includes(slashFilter),
                      );

                      if (e.key === "ArrowDown") {
                        e.preventDefault();
                        setSlashMenuSelectedIndex((prev) =>
                          Math.min(prev + 1, filtered.length - 1),
                        );
                        return;
                      }
                      if (e.key === "ArrowUp") {
                        e.preventDefault();
                        setSlashMenuSelectedIndex((prev) =>
                          Math.max(prev - 1, 0),
                        );
                        return;
                      }
                      if (e.key === "Enter" || e.key === "Tab") {
                        e.preventDefault();
                        const selected =
                          filtered[slashMenuSelectedIndex] || filtered[0];
                        if (selected) {
                          const val = e.target.value;
                          const cursorPos = e.target.selectionStart;
                          const lineStart =
                            val.lastIndexOf("\n", cursorPos - 1) + 1;
                          const beforeSlash = val.substring(0, lineStart);
                          const afterCursor = val.substring(cursorPos);

                          const markdownMap = {
                            heading1: "# ",
                            heading2: "## ",
                            heading3: "### ",
                            bullet: "- ",
                            numbered: "1. ",
                            todo: "- [ ] ",
                            quote: "> ",
                            code: "```\n",
                            divider: "---\n",
                            callout: "> 💡 ",
                          };

                          const prefix = markdownMap[selected.type] || "";

                          // Handle AI types differently
                          if (
                            selected.type.startsWith("ai_") ||
                            selected.type === "ask_ai"
                          ) {
                            handleBlockChange(
                              activePage.blocks[0].id,
                              beforeSlash + afterCursor,
                            );
                            setShowAiPanel(true);
                            setDockTab("docs");
                            if (selected.type === "ai_summarize") {
                              handleAiChat("Summarize my notes");
                            } else if (selected.type === "ai_brainstorm") {
                              handleAiChat("Help me brainstorm ideas");
                            } else if (selected.type === "ai_quiz") {
                              handleAiChat("Create a quiz from my notes");
                            } else if (selected.type === "ai_flashcards") {
                              handleAiChat("Create flashcards from my notes");
                            } else {
                              // Just open AI panel for ask_ai
                            }
                          } else {
                            handleBlockChange(
                              activePage.blocks[0].id,
                              beforeSlash + prefix + afterCursor,
                            );
                          }
                        }
                        setShowSlashMenu(false);
                        setSlashFilter("");
                        setSlashMenuSelectedIndex(0);
                        return;
                      }
                      if (e.key === "Escape") {
                        setShowSlashMenu(false);
                        setSlashFilter("");
                        setSlashMenuSelectedIndex(0);
                        return;
                      }
                    }
                  }}
                  onMouseUp={(e) => {
                    const textarea = e.target;
                    const start = textarea.selectionStart;
                    const end = textarea.selectionEnd;
                    const selectedText = textarea.value.substring(start, end);
                    if (selectedText && selectedText.trim().length > 0) {
                      const rect = textarea.getBoundingClientRect();
                      setSelectionTagMenu({
                        text: selectedText,
                        x: rect.left + rect.width / 2,
                        y: Math.max(rect.top - 60, 10),
                        blockId: activePage.blocks[0]?.id,
                      });
                    } else {
                      setSelectionTagMenu(null);
                    }
                  }}
                  placeholder="Just start typing..."
                  spellCheck="true"
                  autoFocus
                />

                {/* Writing Stats & Shortcuts */}
                <div className={styles.writingStats}>
                  <div className={styles.writingStat}>
                    <Type size={12} />
                    <span>
                      {
                        (activePage.blocks?.[0]?.content || "")
                          .trim()
                          .split(/\s+/)
                          .filter((w) => w).length
                      }{" "}
                      words
                    </span>
                  </div>
                  <div className={styles.writingStat}>
                    <Clock size={12} />
                    <span>
                      {Math.max(
                        1,
                        Math.ceil(
                          (activePage.blocks?.[0]?.content || "")
                            .trim()
                            .split(/\s+/)
                            .filter((w) => w).length / 200,
                        ),
                      )}{" "}
                      min read
                    </span>
                  </div>
                  <div className={styles.writingStatSpacer} />
                  <div className={styles.writingHint}>
                    <span>**bold**</span>
                    <span>*italic*</span>
                    <span># heading</span>
                    <span>- list</span>
                  </div>
                </div>
              </div>
            </div>
          ) : selectedFolder ? (
            <SectionPanel
              folder={selectedFolder}
              notebooks={notebooks}
              onOpenPage={(page) => {
                setSelectedFolder(null);
                handlePageSelect(page);
              }}
              onGenerateKnowledgeGraph={(text, title) => {
                if (onGenerateKnowledgeGraph)
                  onGenerateKnowledgeGraph(text, title);
              }}
            />
          ) : (
            <div className={styles.homeDashboard}>
              {/* Greeting */}
              <div className={styles.dashboardGreeting}>
                <h1 className={styles.dashboardTitle}>
                  {(() => {
                    const h = new Date().getHours();
                    return h < 12
                      ? "Good morning"
                      : h < 18
                        ? "Good afternoon"
                        : "Good evening";
                  })()}
                </h1>
                <p className={styles.dashboardSubtitle}>
                  What would you like to work on today?
                </p>
              </div>

              {/* Quick action cards */}
              <div className={styles.quickActionsGrid}>
                <button
                  className={`${styles.quickActionCard} ${styles.qaAssignment}`}
                  onClick={() => {
                    const p = {
                      id: `page-${Date.now()}`,
                      title: "New Assignment",
                      type: "assignment",
                      parentId: "root",
                      blocks: [
                        {
                          id: `b-${Date.now()}`,
                          type: BLOCK_TYPES.TEXT,
                          content: "",
                        },
                      ],
                      properties: {
                        status: "In progress",
                        dueDate: "",
                        course: "",
                      },
                      createdAt: new Date().toISOString(),
                      updatedAt: new Date().toISOString(),
                      color: 0,
                    };
                    setNotebooks((prev) => addItemToFolder(prev, "root", p));
                    handlePageSelect(p);
                  }}
                >
                  <div className={styles.qaIcon}>
                    <FileText size={22} />
                  </div>
                  <div className={styles.qaText}>
                    <span className={styles.qaLabel}>New Assignment</span>
                    <span className={styles.qaDesc}>
                      Draft with AI assistance
                    </span>
                  </div>
                </button>

                <button
                  className={`${styles.quickActionCard} ${styles.qaStudy}`}
                  onClick={() => {
                    setShowAiPanel(true);
                    setDockTab("study");
                    setStudyToolsExpanded(true);
                  }}
                >
                  <div className={styles.qaIcon}>
                    <Brain size={22} />
                  </div>
                  <div className={styles.qaText}>
                    <span className={styles.qaLabel}>Study from Doc</span>
                    <span className={styles.qaDesc}>
                      Quiz, flashcards & notes
                    </span>
                  </div>
                </button>

                <button
                  className={`${styles.quickActionCard} ${styles.qaDoc}`}
                  onClick={() => {
                    setShowAiPanel(true);
                    setDockTab("docs");
                    docUploadInputRef.current?.click();
                  }}
                >
                  <div className={styles.qaIcon}>
                    <BookOpen size={22} />
                  </div>
                  <div className={styles.qaText}>
                    <span className={styles.qaLabel}>Upload Document</span>
                    <span className={styles.qaDesc}>View alongside notes</span>
                  </div>
                </button>

                <button
                  className={`${styles.quickActionCard} ${styles.qaAi}`}
                  onClick={() => {
                    handleCreatePage("root");
                  }}
                >
                  <div className={styles.qaIcon}>
                    <Plus size={22} />
                  </div>
                  <div className={styles.qaText}>
                    <span className={styles.qaLabel}>Blank Note</span>
                    <span className={styles.qaDesc}>Start writing freely</span>
                  </div>
                </button>
              </div>

              {/* Recently visited */}
              {recentlyVisitedIds.length > 0 && (
                <div className={styles.dashboardSection}>
                  <div className={styles.dashboardSectionTitle}>
                    <RefreshCw size={13} />
                    <span>Recently visited</span>
                  </div>
                  <div className={styles.dashboardGrid}>
                    {recentlyVisitedIds.slice(0, 6).map((id) => {
                      const page = findItemById(notebooks, id);
                      if (!page || page.isFolder) return null;
                      return (
                        <div
                          key={page.id}
                          className={styles.recentCard}
                          onClick={() => handlePageSelect(page)}
                        >
                          <div
                            className={styles.recentCardIcon}
                            style={{
                              color: NOTEBOOK_COLORS[page.color || 0].primary,
                            }}
                          >
                            {page.type === "assignment" ? (
                              <FileText size={18} />
                            ) : (
                              <Hash size={18} />
                            )}
                          </div>
                          <div className={styles.recentCardInfo}>
                            <span className={styles.recentCardTitle}>
                              {page.title || "Untitled"}
                            </span>
                            <span className={styles.recentCardMeta}>
                              {getRelativeTime(page.updatedAt)}
                            </span>
                          </div>
                          {page.type === "assignment" && (
                            <span className={styles.recentCardBadge}>
                              Assignment
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* All pages */}
              <div className={styles.dashboardSection}>
                <div className={styles.dashboardSectionTitle}>
                  <FileText size={13} />
                  <span>All notes</span>
                  <button
                    className={styles.dashboardSectionBtn}
                    onClick={() => handleCreatePage("root")}
                  >
                    <Plus size={12} /> New
                  </button>
                </div>
                <div className={styles.dashboardList}>
                  {(function flattenPages(items) {
                    const pages = [];
                    items.forEach((item) => {
                      if (!item.isFolder) pages.push(item);
                      if (item.children)
                        pages.push(...flattenPages(item.children));
                    });
                    return pages;
                  })(notebooks)
                    .slice(0, 8)
                    .map((page) => (
                      <div
                        key={page.id}
                        className={styles.dashboardListItem}
                        onClick={() => handlePageSelect(page)}
                      >
                        <FileText
                          size={13}
                          style={{
                            color: NOTEBOOK_COLORS[page.color || 0]?.primary,
                            flexShrink: 0,
                          }}
                        />
                        <span className={styles.dashboardListTitle}>
                          {page.title || "Untitled"}
                        </span>
                        <span className={styles.dashboardListMeta}>
                          {getRelativeTime(page.updatedAt)}
                        </span>
                        {page.type === "assignment" && (
                          <span className={styles.assignmentPill}>
                            Assignment
                          </span>
                        )}
                      </div>
                    ))}
                  {notebooks.length === 0 && (
                    <div className={styles.dashboardEmpty}>
                      <p>
                        No notes yet. Create your first page to get started.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Slash Menu with autocomplete */}
      {showSlashMenu && (
        <div
          ref={slashMenuRef}
          className={styles.slashMenu}
          style={{ left: slashMenuPosition.x, top: slashMenuPosition.y }}
        >
          {(() => {
            let globalIndex = 0;
            return Object.entries(
              filteredSlashOptions.reduce((acc, opt) => {
                if (!acc[opt.category]) acc[opt.category] = [];
                acc[opt.category].push(opt);
                return acc;
              }, {}),
            ).map(([category, options]) => (
              <div key={category} className={styles.slashMenuSection}>
                <div className={styles.slashMenuCategory}>{category}</div>
                {options.map((option) => {
                  const currentIndex = globalIndex++;
                  const isSelected = currentIndex === slashMenuSelectedIndex;
                  return (
                    <button
                      key={option.type}
                      className={`${styles.slashMenuItem} ${isSelected ? styles.slashMenuItemSelected : ""}`}
                      onClick={() => {
                        // Insert markdown for textarea
                        const markdownMap = {
                          heading1: "# ",
                          heading2: "## ",
                          heading3: "### ",
                          bullet: "- ",
                          numbered: "1. ",
                          todo: "- [ ] ",
                          quote: "> ",
                          code: "```\n",
                          divider: "---\n",
                          callout: "> 💡 ",
                        };

                        if (
                          option.type.startsWith("ai_") ||
                          option.type === "ask_ai"
                        ) {
                          setShowAiPanel(true);
                          setDockTab("docs");
                          if (option.type === "ai_summarize") {
                            handleAiChat("Summarize my notes");
                          } else if (option.type === "ai_brainstorm") {
                            handleAiChat("Help me brainstorm ideas");
                          } else if (option.type === "ai_quiz") {
                            handleAiChat("Create a quiz from my notes");
                          } else if (option.type === "ai_flashcards") {
                            handleAiChat("Create flashcards from my notes");
                          }
                        } else if (activeBlockId && activePage?.blocks?.[0]) {
                          const prefix = markdownMap[option.type] || "";
                          const currentContent =
                            activePage.blocks[0].content || "";
                          // Remove any trailing slash command
                          const cleanContent = currentContent.replace(
                            /\/\w*$/,
                            "",
                          );
                          handleBlockChange(
                            activeBlockId,
                            cleanContent + prefix,
                          );
                        }
                        setShowSlashMenu(false);
                        setSlashFilter("");
                      }}
                    >
                      <span className={styles.slashMenuIcon}>
                        {option.icon}
                      </span>
                      <div className={styles.slashMenuText}>
                        <span className={styles.slashMenuLabel}>
                          {option.label}
                        </span>
                        <span className={styles.slashMenuShortcut}>
                          /{option.shortcut}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            ));
          })()}
        </div>
      )}

      {/* Block Handle Menu (Six-dot menu) */}
      {activeMenu?.type === "handle" && (
        <div
          className={styles.handleMenu}
          style={{ left: activeMenu.pos.x, top: activeMenu.pos.y }}
        >
          <button
            className={styles.handleMenuItem}
            onClick={() => {
              handleDelete(activeMenu.blockId);
              setActiveMenu(null);
            }}
          >
            <Trash2 size={14} />
            <span>Delete</span>
          </button>
          <button
            className={styles.handleMenuItem}
            onClick={() => handleDuplicateBlock(activeMenu.blockId)}
          >
            <Copy size={14} />
            <span>Duplicate</span>
          </button>
          <div className={styles.handleMenuDivider} />
          <button
            className={`${styles.handleMenuItem} ${styles.ai}`}
            onClick={() => {
              const block = (function findBlock(blocks, id) {
                for (const b of blocks) {
                  if (b.id === id) return b;
                  if (b.children) {
                    const found = findBlock(b.children, id);
                    if (found) return found;
                  }
                }
                return null;
              })(activePage.blocks, activeMenu.blockId);
              if (block) handleAiAction("Improve", block.content);
              setActiveMenu(null);
            }}
          >
            <Sparkles size={14} />
            <span>AI Improve</span>
          </button>
          <button
            className={`${styles.handleMenuItem} ${styles.ai}`}
            onClick={() => {
              const block = (function findBlock(blocks, id) {
                for (const b of blocks) {
                  if (b.id === id) return b;
                  if (b.children) {
                    const found = findBlock(b.children, id);
                    if (found) return found;
                  }
                }
                return null;
              })(activePage.blocks, activeMenu.blockId);
              if (block) handleAiAction("Explain", block.content);
              setActiveMenu(null);
            }}
          >
            <Sparkles size={14} />
            <span>AI Explain</span>
          </button>
          <div className={styles.handleMenuDivider} />
          <button
            className={styles.handleMenuItem}
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const pos = calculateMenuPosition(
                {
                  left: rect.right + 5,
                  top: rect.top,
                  bottom: rect.bottom,
                  right: rect.right + 225,
                },
                220,
                400,
              );
              setActiveMenu({
                type: "blockColor",
                pos,
                blockId: activeMenu.blockId,
              });
            }}
          >
            <Type size={14} />
            <span>Color</span>
            <ChevronRight size={14} style={{ marginLeft: "auto" }} />
          </button>
          <div className={styles.handleMenuDivider} />
          <button
            className={styles.handleMenuItem}
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const pos = calculateMenuPosition(
                {
                  left: rect.right + 5,
                  top: rect.top,
                  bottom: rect.bottom,
                  right: rect.right + 305,
                },
                300,
                400,
              );
              setActiveMenu({
                type: "convert",
                pos,
                blockId: activeMenu.blockId,
              });
            }}
          >
            <RefreshCw size={14} />
            <span>Turn into</span>
            <ChevronRight size={14} style={{ marginLeft: "auto" }} />
          </button>
        </div>
      )}

      {/* Block Color Menu */}
      {activeMenu?.type === "blockColor" && (
        <div
          className={styles.blockColorMenu}
          style={{ left: activeMenu.pos.x, top: activeMenu.pos.y }}
        >
          <div className={styles.slashMenuHeader}>Color</div>
          {BLOCK_COLORS.map((color) => (
            <button
              key={color.name}
              className={styles.slashMenuItem}
              onClick={() => handleBlockColorSelect("color", color.value)}
            >
              <div
                className={styles.colorSample}
                style={{ color: color.value }}
              >
                A
              </div>
              <span className={styles.slashMenuLabel}>{color.name}</span>
            </button>
          ))}
          <div className={styles.handleMenuDivider} />
          <div className={styles.slashMenuHeader}>Background</div>
          {BLOCK_BG_COLORS.map((bg) => (
            <button
              key={bg.name}
              className={styles.slashMenuItem}
              onClick={() =>
                handleBlockColorSelect("background", bg.value, true)
              }
            >
              <div
                className={styles.colorSample}
                style={{ backgroundColor: bg.value }}
              >
                A
              </div>
              <span className={styles.slashMenuLabel}>{bg.name}</span>
            </button>
          ))}
        </div>
      )}

      {/* Convert Menu (Turn into) */}
      {activeMenu?.type === "convert" && (
        <div
          className={styles.slashMenu}
          style={{ left: activeMenu.pos.x, top: activeMenu.pos.y }}
          onMouseLeave={() => setActiveMenu(null)}
        >
          <div className={styles.slashMenuHeader}>Turn into</div>
          {slashMenuOptions
            .filter((opt) => opt.type !== "ai")
            .map((option) => (
              <button
                key={option.type}
                className={styles.slashMenuItem}
                onClick={() => handleConvertMenuSelect(option.type)}
              >
                <span className={styles.slashMenuIcon}>{option.icon}</span>
                <div className={styles.slashMenuText}>
                  <span className={styles.slashMenuLabel}>{option.label}</span>
                </div>
              </button>
            ))}
        </div>
      )}

      {/* Context Menu */}
      {contextMenu && (
        <div
          className={styles.contextMenu}
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          {!contextMenu.item.isFolder && (
            <button
              className={styles.contextMenuItem}
              onClick={() => handleToggleFavorite(contextMenu.item.id)}
            >
              <Star size={14} />
              <span>
                {contextMenu.item.isFavorite
                  ? "Remove from favorites"
                  : "Add to favorites"}
              </span>
            </button>
          )}
          {contextMenu.item.isFolder && (
            <>
              <button
                className={styles.contextMenuItem}
                onClick={() => {
                  handleCreatePage(contextMenu.item.id);
                  setContextMenu(null);
                }}
              >
                <Plus size={14} />
                <span>New page</span>
              </button>
              <div className={styles.contextMenuDivider} />
              <div className={styles.contextMenuLabel}>Create folder</div>
              <button
                className={styles.contextMenuItem}
                onClick={() => {
                  const folder = createFolder(
                    "New Subject",
                    contextMenu.item.id,
                    FOLDER_TYPES.SUBJECT,
                  );
                  setNotebooks((prev) =>
                    addItemToFolder(prev, contextMenu.item.id, {
                      ...folder,
                      children: [],
                    }),
                  );
                  setContextMenu(null);
                }}
              >
                <span className={styles.folderIcon}>
                  {FOLDER_ICONS.subject}
                </span>
                <span>Subject</span>
              </button>
              <button
                className={styles.contextMenuItem}
                onClick={() => {
                  const folder = createFolder(
                    "New Class",
                    contextMenu.item.id,
                    FOLDER_TYPES.CLASS,
                  );
                  setNotebooks((prev) =>
                    addItemToFolder(prev, contextMenu.item.id, {
                      ...folder,
                      children: [],
                    }),
                  );
                  setContextMenu(null);
                }}
              >
                <span className={styles.folderIcon}>{FOLDER_ICONS.class}</span>
                <span>Class</span>
              </button>
              <button
                className={styles.contextMenuItem}
                onClick={() => {
                  const folder = createFolder(
                    "New Unit",
                    contextMenu.item.id,
                    FOLDER_TYPES.UNIT,
                  );
                  setNotebooks((prev) =>
                    addItemToFolder(prev, contextMenu.item.id, {
                      ...folder,
                      children: [],
                    }),
                  );
                  setContextMenu(null);
                }}
              >
                <span className={styles.folderIcon}>{FOLDER_ICONS.unit}</span>
                <span>Unit</span>
              </button>
              <button
                className={styles.contextMenuItem}
                onClick={() => {
                  handleCreateFolder(contextMenu.item.id);
                  setContextMenu(null);
                }}
              >
                <span className={styles.folderIcon}>{FOLDER_ICONS.folder}</span>
                <span>Folder</span>
              </button>
              <div className={styles.contextMenuDivider} />
            </>
          )}
          <button
            className={styles.contextMenuItem}
            onClick={() => {
              setEditingTitle(contextMenu.item.id);
              setContextMenu(null);
            }}
          >
            <Edit3 size={14} />
            <span>Rename</span>
          </button>
          <button
            className={styles.contextMenuItem}
            onClick={() => {
              const item = contextMenu.item;
              const copy = item.isFolder
                ? {
                    ...createFolder(item.name + " (copy)", item.parentId),
                    children: [...(item.children || [])],
                  }
                : createPage(item.title + " (copy)", item.parentId);
              setNotebooks((prev) =>
                addItemToFolder(prev, item.parentId || "root", copy),
              );
              setContextMenu(null);
            }}
          >
            <Copy size={14} />
            <span>Duplicate</span>
          </button>
          <div className={styles.contextMenuDivider} />
          <button
            className={`${styles.contextMenuItem} ${styles.danger}`}
            onClick={() => handleDelete(contextMenu.item.id)}
          >
            <Trash2 size={14} />
            <span>Delete{contextMenu.item.isFolder ? " folder" : ""}</span>
          </button>
        </div>
      )}
      {/* Trash Modal */}
      {showTrash && (
        <div className={styles.trashOverlay}>
          <div className={styles.trashModal}>
            <div className={styles.modalHeader}>
              <h3>Trash</h3>
              <div className={styles.modalActions}>
                <button
                  className={styles.emptyTrashBtn}
                  onClick={handleEmptyTrash}
                  disabled={trash.length === 0}
                >
                  Empty Trash
                </button>
                <button onClick={() => setShowTrash(false)}>
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className={styles.trashList}>
              {trash.length > 0 ? (
                trash.map((item) => (
                  <div key={item.id} className={styles.trashItem}>
                    <div className={styles.trashItemInfo}>
                      <div
                        className={styles.trashItemIcon}
                        style={{
                          color: NOTEBOOK_COLORS[item.color || 0].primary,
                        }}
                      >
                        {item.isFolder ? (
                          <Folder size={16} />
                        ) : (
                          <FileText size={16} />
                        )}
                      </div>
                      <div className={styles.trashItemText}>
                        <span className={styles.trashItemTitle}>
                          {item.isFolder ? item.name : item.title}
                        </span>
                        <span className={styles.trashItemMeta}>
                          Deleted {getRelativeTime(item.deletedAt)}
                        </span>
                      </div>
                    </div>
                    <div className={styles.trashItemActions}>
                      <button
                        className={styles.restoreBtn}
                        onClick={() => handleRestore(item.id)}
                        title="Restore"
                      >
                        <RefreshCw size={14} />
                      </button>
                      <button
                        className={styles.deletePermanentlyBtn}
                        onClick={() => handlePermanentDelete(item.id)}
                        title="Delete permanently"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className={styles.noResults}>Trash is empty</div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Quick Switcher (Cmd+K) */}
      {showQuickSwitcher && (
        <div className={styles.switcherOverlay}>
          <div className={styles.switcherModal}>
            <div className={styles.switcherSearch}>
              <Search size={18} />
              <input
                autoFocus
                type="text"
                placeholder="Search pages..."
                value={switcherSearchQuery}
                onChange={(e) => setSwitcherSearchQuery(e.target.value)}
              />
            </div>

            <div className={styles.switcherResults}>
              {switcherSearchQuery === "" && recentSearches.length > 0 && (
                <div className={styles.switcherSection}>
                  <div className={styles.switcherSectionHeader}>
                    Recently searched
                  </div>
                  {recentSearches.map((page) => (
                    <button
                      key={`recent-${page.id}`}
                      className={styles.switcherItem}
                      onClick={() => {
                        handlePageSelect(page);
                        setShowQuickSwitcher(false);
                        setRecentSearches((prev) => {
                          const filtered = prev.filter((p) => p.id !== page.id);
                          return [page, ...filtered].slice(0, 5);
                        });
                      }}
                    >
                      <div
                        className={styles.switcherItemIcon}
                        style={{
                          color: NOTEBOOK_COLORS[page.color || 0].primary,
                        }}
                      >
                        {page.emoji ? (
                          <span className={styles.switcherEmoji}>
                            {page.emoji}
                          </span>
                        ) : (
                          <FileText size={16} />
                        )}
                      </div>
                      <div className={styles.switcherItemInfo}>
                        <span className={styles.switcherItemTitle}>
                          {page.title}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {switcherSearchQuery !== "" &&
                (function getFilteredPages(items, query) {
                  let results = [];
                  const search = query.toLowerCase();

                  function searchInItems(list) {
                    list.forEach((item) => {
                      if (
                        !item.isFolder &&
                        item.title.toLowerCase().includes(search)
                      ) {
                        results.push(item);
                      }
                      if (item.children) searchInItems(item.children);
                    });
                  }

                  searchInItems(items);
                  return results;
                })(notebooks, switcherSearchQuery).map((page) => (
                  <button
                    key={page.id}
                    className={styles.switcherItem}
                    onClick={() => {
                      handlePageSelect(page);
                      setShowQuickSwitcher(false);
                      setRecentSearches((prev) => {
                        const filtered = prev.filter((p) => p.id !== page.id);
                        return [page, ...filtered].slice(0, 5);
                      });
                    }}
                  >
                    <div
                      className={styles.switcherItemIcon}
                      style={{
                        color: NOTEBOOK_COLORS[page.color || 0].primary,
                      }}
                    >
                      {page.emoji ? (
                        <span className={styles.switcherEmoji}>
                          {page.emoji}
                        </span>
                      ) : (
                        <FileText size={16} />
                      )}
                    </div>
                    <div className={styles.switcherItemInfo}>
                      <span className={styles.switcherItemTitle}>
                        {page.title}
                      </span>
                      <span className={styles.switcherItemPath}>
                        {(function getPath(items, targetId, currentPath = []) {
                          for (const item of items) {
                            if (item.id === targetId) return currentPath;
                            if (item.children) {
                              const path = getPath(item.children, targetId, [
                                ...currentPath,
                                item.name || item.title,
                              ]);
                              if (path) return path;
                            }
                          }
                          return null;
                        })(notebooks, page.id)?.join(" / ")}
                      </span>
                    </div>
                  </button>
                ))}
              {switcherSearchQuery !== "" &&
                (function getFilteredPagesCount(items, query) {
                  let count = 0;
                  const search = query.toLowerCase();
                  function countItems(list) {
                    list.forEach((item) => {
                      if (
                        !item.isFolder &&
                        item.title.toLowerCase().includes(search)
                      )
                        count++;
                      if (item.children) countItems(item.children);
                    });
                  }
                  countItems(items);
                  return count;
                })(notebooks, switcherSearchQuery) === 0 && (
                  <div className={styles.noResults}>No pages found</div>
                )}
            </div>

            <div className={styles.switcherFooter}>
              <div className={styles.switcherHint}>
                <span>Enter</span> to select
              </div>
              <div className={styles.switcherHint}>
                <span>Esc</span> to close
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Right Dock - Document Reader */}
      <div
        ref={dockRef}
        className={`${styles.dock} ${!dockSideOpen ? styles.dockCollapsed : ""}`}
        style={dockSideOpen ? { width: `${dockWidth}px` } : undefined}
      >
        {dockSideOpen && (
          <div
            className={styles.dockResizeHandle}
            onMouseDown={handleDockResizeStart}
          />
        )}
        <input
          ref={docUploadInputRef}
          type="file"
          style={{ display: "none" }}
          accept=".pdf,.doc,.docx,.txt,.md"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleDocUpload(f);
            e.target.value = "";
          }}
        />
        {dockSideOpen && dockTab !== "docs" && (
          <div className={styles.dockHeader}>
            <div className={styles.dockTabs}>
              <button
                className={`${styles.dockTabBtn} ${dockTab === "docs" ? styles.active : ""}`}
                onClick={() => setDockTab("docs")}
                type="button"
              >
                <FileText size={13} />
                <span>Documents</span>
              </button>
              <button
                className={`${styles.dockTabBtn} ${dockTab === "ai" ? styles.active : ""}`}
                onClick={() => setDockTab("ai")}
                type="button"
              >
                <Sparkles size={13} />
                <span>AI</span>
              </button>
              <button
                className={`${styles.dockTabBtn} ${dockTab === "study" ? styles.active : ""}`}
                onClick={() => setDockTab("study")}
                type="button"
              >
                <Brain size={13} />
                <span>Study</span>
                {studyContent &&
                  studyContent.type !== "loading" &&
                  studyContent.type !== "error" && (
                    <span className={styles.dockTabDot} />
                  )}
              </button>
              <button
                className={`${styles.dockTabBtn} ${dockTab === "assign" ? styles.active : ""}`}
                onClick={() => setDockTab("assign")}
                type="button"
              >
                <NotebookPen size={13} />
                <span>Assign</span>
              </button>
            </div>
            <div className={styles.dockActions}>
              {dockTab === "docs" && (
                <button
                  className={styles.topBarBtn}
                  onClick={() => docUploadInputRef.current?.click()}
                  type="button"
                  title="Upload document"
                >
                  <Plus size={16} />
                </button>
              )}
              <button
                className={styles.topBarBtn}
                onClick={() => setShowAiPanel(false)}
                type="button"
                title="Close"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Study Content Panel */}
        {dockTab === "study" && (
          <div className={styles.studyPane}>
            {!studyContent ? (
              <div className={styles.studyPaneEmpty}>
                <Brain
                  size={40}
                  strokeWidth={1.2}
                  className={styles.studyPaneEmptyIcon}
                />
                <h3>Study Tools</h3>
                <p>
                  Open a study tool from the sidebar to generate quizzes,
                  flashcards, notes, and more from your documents.
                </p>
              </div>
            ) : studyContent.type === "loading" ? (
              <div className={styles.studyPaneLoading}>
                <div className={styles.studySpinner} />
                <p>{studyContent.title}</p>
              </div>
            ) : studyContent.type === "error" ? (
              <div className={styles.studyPaneError}>
                <h3>{studyContent.title}</h3>
                <p>{studyContent.message}</p>
                <button
                  onClick={() => setStudyContent(null)}
                  className={styles.studyRetryBtn}
                >
                  Dismiss
                </button>
              </div>
            ) : studyContent.type === "studypack" ? (
              <StudyPackPanel
                data={studyContent.data}
                title={studyContent.title}
                onClose={() => setStudyContent(null)}
              />
            ) : studyContent.type === "quiz" || studyContent.type === "test" ? (
              <div className={styles.studyPaneContent}>
                <div className={styles.studyPaneTitle}>
                  {studyContent.title}
                </div>
                {studyContent.data?.questions ? (
                  <EnhancedQuizDeck
                    quiz={studyContent.data}
                    onExit={() => setStudyContent(null)}
                  />
                ) : (
                  <div className={styles.studyRawContent}>
                    <pre>{JSON.stringify(studyContent.data, null, 2)}</pre>
                  </div>
                )}
              </div>
            ) : studyContent.type === "flashcards" ? (
              <div className={styles.studyPaneContent}>
                <div className={styles.studyPaneTitle}>
                  {studyContent.title}
                </div>
                <NotebookFlashcards
                  cards={studyContent.data?.cards || []}
                  onDone={() => setStudyContent(null)}
                />
              </div>
            ) : (
              <div className={styles.studyPaneContent}>
                <div className={styles.studyPaneTitle}>
                  {studyContent.title}
                </div>
                <div className={styles.studyNotesContent}>
                  {(
                    studyContent.data?.content ||
                    studyContent.data?.notes ||
                    studyContent.data?.quotes ||
                    []
                  ).map((item, i) => (
                    <div key={i} className={styles.studyNotesItem}>
                      {typeof item === "string"
                        ? item
                        : item.text ||
                          item.quote ||
                          item.content ||
                          JSON.stringify(item)}
                    </div>
                  ))}
                  {typeof studyContent.data === "string" && (
                    <div className={styles.studyNotesRaw}>
                      {studyContent.data}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Assignment Automation Panel */}
        {dockTab === "assign" && (
          <NotebookAssignPanel
            activePage={activePage}
            onInsertContent={(text) => {
              if (!activePage) return;
              const newBlocks = text
                .split("\n")
                .filter((line) => line.trim())
                .map((line) => ({
                  id: `assign-${Date.now()}-${Math.random().toString(36).slice(2)}`,
                  type: BLOCK_TYPES.TEXT,
                  content: line,
                  children: [],
                }));
              const updatedPage = {
                ...activePage,
                blocks: [...activePage.blocks, ...newBlocks],
                updatedAt: new Date().toISOString(),
              };
              setActivePage(updatedPage);
              setNotebooks((prev) =>
                updateItemInTree(prev, activePage.id, updatedPage),
              );
            }}
          />
        )}

        {/* Document Viewer */}
        <div
          className={styles.docsPane}
          style={{ display: dockTab !== "docs" ? "none" : undefined }}
        >
          <div className={styles.docsChrome}>
            <div className={styles.dockHeaderInDocs}>
              <div className={styles.dockTabs}>
                <button
                  className={`${styles.dockTabBtn} ${dockTab === "docs" ? styles.active : ""}`}
                  onClick={() => setDockTab("docs")}
                  type="button"
                >
                  <FileText size={13} />
                  <span>Documents</span>
                </button>
                <button
                  className={`${styles.dockTabBtn} ${dockTab === "ai" ? styles.active : ""}`}
                  onClick={() => setDockTab("ai")}
                  type="button"
                >
                  <Sparkles size={13} />
                  <span>AI</span>
                </button>
                <button
                  className={`${styles.dockTabBtn} ${dockTab === "study" ? styles.active : ""}`}
                  onClick={() => setDockTab("study")}
                  type="button"
                >
                  <Brain size={13} />
                  <span>Study</span>
                  {studyContent &&
                    studyContent.type !== "loading" &&
                    studyContent.type !== "error" && (
                      <span className={styles.dockTabDot} />
                    )}
                </button>
                <button
                  className={`${styles.dockTabBtn} ${dockTab === "assign" ? styles.active : ""}`}
                  onClick={() => setDockTab("assign")}
                  type="button"
                >
                  <NotebookPen size={13} />
                  <span>Assign</span>
                </button>
              </div>
              <div className={styles.dockActions}>
                <button
                  className={styles.topBarBtn}
                  onClick={() => docUploadInputRef.current?.click()}
                  type="button"
                  title="Upload document"
                >
                  <Plus size={16} />
                </button>
                <button
                  className={styles.topBarBtn}
                  onClick={() => setShowAiPanel(false)}
                  type="button"
                  title="Close"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
            {/* Document Tabs - Only show if documents exist */}
            {dockDocuments.length > 0 && (
              <div className={styles.docToolbar}>
                <div className={styles.docTabs}>
                  {dockDocuments.map((doc) => (
                    <button
                      key={doc.id}
                      className={`${styles.docTab} ${activeDockDocId === doc.id ? styles.active : ""}`}
                      onClick={() => setActiveDockDocId(doc.id)}
                      type="button"
                    >
                      <FileText size={12} />
                      <span>
                        {doc.name.length > 20
                          ? doc.name.slice(0, 20) + "..."
                          : doc.name}
                      </span>
                      <button
                        className={styles.docTabClose}
                        onClick={(e) => {
                          e.stopPropagation();
                          const remaining = dockDocuments.filter(
                            (d) => d.id !== doc.id,
                          );
                          setDockDocuments(remaining);
                          if (activeDockDocId === doc.id) {
                            setActiveDockDocId(
                              remaining.length > 0 ? remaining[0].id : null,
                            );
                          }
                        }}
                        type="button"
                        title="Close"
                      >
                        <X size={10} />
                      </button>
                    </button>
                  ))}
                </div>
                <div className={styles.docToolbarActions}>
                  {docSearchOpen ? (
                    <div className={styles.docSearchWrap}>
                      <Search size={14} className={styles.docSearchIcon} />
                      <input
                        ref={docSearchInputRef}
                        className={styles.docSearchInput}
                        value={docSearchQuery}
                        onChange={(e) => setDocSearchQuery(e.target.value)}
                        placeholder="Search documents…"
                        onKeyDown={(e) => {
                          if (e.key === "Escape") {
                            setDocSearchOpen(false);
                            setDocSearchQuery("");
                          }
                          if (e.key === "Enter") {
                            const first = sortedDockDocuments[0];
                            if (first) setActiveDockDocId(first.id);
                          }
                        }}
                      />
                      <button
                        type="button"
                        className={styles.docSearchClose}
                        onClick={() => {
                          setDocSearchOpen(false);
                          setDocSearchQuery("");
                        }}
                        title="Close search"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className={styles.docToolbarIconBtn}
                      onClick={() => setDocSearchOpen(true)}
                      title="Search documents"
                    >
                      <Search size={16} />
                    </button>
                  )}
                </div>
                {/* Annotations toggle */}
                {activeDockDocId && (
                  <button
                    type="button"
                    className={`${styles.docToolbarIconBtn} ${showAnnotations ? styles.docToolbarIconBtnActive : ""}`}
                    onClick={() => setShowAnnotations((v) => !v)}
                    title={`${currentDocAnnotations.length} annotation${currentDocAnnotations.length !== 1 ? "s" : ""}`}
                    style={{ position: "relative" }}
                  >
                    <MessageSquare size={15} />
                    {currentDocAnnotations.length > 0 && (
                      <span
                        style={{
                          position: "absolute",
                          top: "-3px",
                          right: "-3px",
                          background: "#9333ea",
                          color: "#fff",
                          borderRadius: "50%",
                          width: "14px",
                          height: "14px",
                          fontSize: "9px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 700,
                          lineHeight: 1,
                        }}
                      >
                        {currentDocAnnotations.length}
                      </span>
                    )}
                  </button>
                )}
                <div className={styles.docZoomControls}>
                  <button
                    className={styles.docZoomBtn}
                    onClick={() =>
                      setDocZoom((prev) => Math.max(50, prev - 25))
                    }
                    type="button"
                    title="Zoom out"
                  >
                    <ZoomOut size={14} />
                  </button>
                  <span className={styles.docZoomLevel}>{docZoom}%</span>
                  <button
                    className={styles.docZoomBtn}
                    onClick={() =>
                      setDocZoom((prev) => Math.min(200, prev + 25))
                    }
                    type="button"
                    title="Zoom in"
                  >
                    <ZoomIn size={14} />
                  </button>
                  <button
                    className={styles.docZoomBtn}
                    onClick={() => setDocZoom(100)}
                    type="button"
                    title="Reset zoom"
                  >
                    <Maximize size={14} />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Document viewer */}
          <div className={styles.docViewer}>
            {dockDocuments.length > 0 && (
              <div
                className={styles.docList}
                style={{ display: docSearchOpen ? undefined : "none" }}
              >
                <div className={styles.docListHeader}>
                  <div className={styles.docListHeaderLeft}>
                    <span className={styles.docListTitle}>Documents</span>
                    <span className={styles.docListCount}>
                      {sortedDockDocuments.length}
                    </span>
                  </div>
                  <div className={styles.docListHeaderRight}>
                    <button
                      type="button"
                      className={`${styles.docListSortBtn} ${docSortMode === "recent" ? styles.active : ""}`}
                      onClick={() => setDocSortMode("recent")}
                      title="Sort by recently opened"
                    >
                      Recent
                    </button>
                    <button
                      type="button"
                      className={`${styles.docListSortBtn} ${docSortMode === "az" ? styles.active : ""}`}
                      onClick={() => setDocSortMode("az")}
                      title="Sort A–Z"
                    >
                      A–Z
                    </button>
                  </div>
                </div>
                {sortedDockDocuments.length === 0 ? (
                  <div className={styles.docListEmpty}>No documents found</div>
                ) : (
                  sortedDockDocuments.map((doc) => (
                    <div
                      key={doc.id}
                      className={`${styles.docListItem} ${activeDockDocId === doc.id ? styles.active : ""}`}
                    >
                      <button
                        type="button"
                        className={styles.docListMain}
                        onClick={() => setActiveDockDocId(doc.id)}
                        title={doc.name}
                      >
                        <FileText size={14} />
                        <span className={styles.docListName}>{doc.name}</span>
                        {doc?.type && (
                          <span className={styles.docListMeta}>
                            {String(doc.type).toUpperCase()}
                          </span>
                        )}
                      </button>
                      <button
                        type="button"
                        className={styles.docListClose}
                        onClick={() => {
                          const remaining = dockDocuments.filter(
                            (d) => d.id !== doc.id,
                          );
                          setDockDocuments(remaining);
                          if (activeDockDocId === doc.id) {
                            setActiveDockDocId(
                              remaining.length > 0 ? remaining[0].id : null,
                            );
                          }
                        }}
                        title="Close"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}
            {activeDockDocId && activeDockDocUrl ? (
              <div
                style={{
                  display: "flex",
                  flex: 1,
                  minHeight: 0,
                  position: "relative",
                }}
              >
                {/* Proper PDF viewer with text selection, highlight, and note actions */}
                <div
                  className={`${styles.docPdfColumn} ${showAnnotations ? styles.docPdfColumnWithAnnot : ""}`}
                >
                  {(() => {
                    const activeDoc = dockDocuments.find(
                      (d) => d.id === activeDockDocId,
                    );
                    if (activeDoc?.content) {
                      return (
                        <PdfHighlightReader
                          key={activeDockDocId}
                          doc={activeDoc}
                          onAddToNotes={handlePdfAddToNotes}
                          onOpenLumiChat={onOpenLumiWithMessage || null}
                          onQueueLumiDraft={onQueueLumiDraft || null}
                          onAnnotate={handlePdfAnnotate}
                          annotations={currentDocAnnotations}
                        />
                      );
                    }
                    // Fallback for non-base64 docs (e.g. external URL)
                    return (
                      <iframe
                        key={`${activeDockDocId}-${docZoom}`}
                        ref={docIframeRef}
                        className={styles.docIframe}
                        src={`/pdfjs/web/viewer.html?file=${encodeURIComponent(activeDockDocUrl)}#zoom=${docZoom}`}
                        title="Document viewer"
                      />
                    );
                  })()}
                </div>
                {showAnnotations && (
                  <aside className={styles.pdfAnnotPanel}>
                    <div className={styles.pdfAnnotHeader}>
                      <span className={styles.pdfAnnotTitle}>
                        Clips · {currentDocAnnotations.length}
                      </span>
                      <button
                        type="button"
                        className={styles.pdfAnnotAddBtn}
                        onClick={() => {
                          setAddingAnnotation(true);
                          setNewAnnotation({ quote: "", note: "" });
                        }}
                      >
                        + Add
                      </button>
                    </div>
                    {addingAnnotation && (
                      <div className={styles.pdfAnnotForm}>
                        <div className={styles.pdfAnnotLabel}>Quote</div>
                        <textarea
                          className={styles.pdfAnnotInput}
                          value={newAnnotation.quote}
                          onChange={(e) =>
                            setNewAnnotation((a) => ({
                              ...a,
                              quote: e.target.value,
                            }))
                          }
                          placeholder="Paste text from the PDF…"
                          rows={2}
                        />
                        <div
                          className={styles.pdfAnnotLabel}
                          style={{ marginTop: 8 }}
                        >
                          Your note
                        </div>
                        <textarea
                          className={styles.pdfAnnotInput}
                          value={newAnnotation.note}
                          onChange={(e) =>
                            setNewAnnotation((a) => ({
                              ...a,
                              note: e.target.value,
                            }))
                          }
                          placeholder="Why this matters…"
                          rows={3}
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && (e.metaKey || e.ctrlKey))
                              saveAnnotation();
                            if (e.key === "Escape") setAddingAnnotation(false);
                          }}
                        />
                        <div className={styles.pdfAnnotActions}>
                          <button
                            type="button"
                            className={styles.pdfAnnotSaveBtn}
                            onClick={saveAnnotation}
                          >
                            Save clip
                          </button>
                          <button
                            type="button"
                            className={styles.pdfAnnotCancelBtn}
                            onClick={() => setAddingAnnotation(false)}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                    <div className={styles.pdfAnnotList}>
                      {currentDocAnnotations.length === 0 &&
                      !addingAnnotation ? (
                        <div className={styles.pdfAnnotEmpty}>
                          <MessageSquare
                            size={22}
                            strokeWidth={1.25}
                            style={{ marginBottom: 8, opacity: 0.45 }}
                          />
                          <p style={{ fontSize: 12, margin: 0 }}>
                            No clips yet.
                          </p>
                          <p
                            style={{
                              fontSize: 11,
                              margin: "6px 0 0",
                              opacity: 0.7,
                            }}
                          >
                            Use “Clip” on a selection, or add manually.
                          </p>
                        </div>
                      ) : (
                        [...currentDocAnnotations].reverse().map((ann) => (
                          <div key={ann.id} className={styles.pdfAnnotCard}>
                            {ann.quote && (
                              <div className={styles.pdfAnnotQuote}>
                                “{ann.quote}”
                              </div>
                            )}
                            {ann.note ? (
                              <p className={styles.pdfAnnotNoteText}>
                                {ann.note}
                              </p>
                            ) : null}
                            <div className={styles.pdfAnnotCardFooter}>
                              <span className={styles.pdfAnnotMeta}>
                                {new Date(ann.createdAt).toLocaleDateString(
                                  "en-US",
                                  {
                                    month: "short",
                                    day: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  },
                                )}
                              </span>
                              <div className={styles.pdfAnnotCardActions}>
                                <button
                                  type="button"
                                  className={styles.pdfAnnotToNoteBtn}
                                  disabled={!activePage}
                                  onClick={() =>
                                    handleAnnotationInsertToNote(ann)
                                  }
                                  title={
                                    activePage
                                      ? "Insert into open note"
                                      : "Open a note first"
                                  }
                                >
                                  <PenTool
                                    size={11}
                                    style={{
                                      marginRight: 4,
                                      verticalAlign: "middle",
                                    }}
                                  />
                                  To note
                                </button>
                                <button
                                  type="button"
                                  className={styles.pdfAnnotIconBtn}
                                  onClick={() => deleteAnnotation(ann.id)}
                                  title="Delete"
                                >
                                  <X size={12} />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    {currentDocAnnotations.length > 0 && (
                      <div className={styles.pdfAnnotFooter}>
                        {onOpenLumiWithMessage && (
                          <button
                            type="button"
                            className={styles.pdfAnnotFooterBtn}
                            onClick={() => {
                              const text = currentDocAnnotations
                                .map(
                                  (a, i) =>
                                    `Clip ${i + 1}:\n${a.quote ? `Quote: "${a.quote}"\n` : ""}${a.note ? `Note: ${a.note}` : ""}`,
                                )
                                .join("\n\n");
                              onOpenLumiWithMessage(
                                `Here are clips from my PDF. Help me synthesize themes and what to study.\n\n${text}`,
                              );
                            }}
                          >
                            Open Lumi &amp; send
                          </button>
                        )}
                        {onQueueLumiDraft && (
                          <button
                            type="button"
                            className={`${styles.pdfAnnotFooterBtn} ${styles.pdfAnnotFooterBtnSecondary}`}
                            onClick={() => {
                              const text = currentDocAnnotations
                                .map(
                                  (a, i) =>
                                    `Clip ${i + 1}:\n${a.quote ? `Quote: "${a.quote}"\n` : ""}${a.note ? `Note: ${a.note}` : ""}`,
                                )
                                .join("\n\n");
                              onQueueLumiDraft(
                                `[PDF clips — please synthesize and suggest study focus]\n\n${text}`,
                              );
                            }}
                          >
                            Queue in Lumi draft
                          </button>
                        )}
                        {!onOpenLumiWithMessage && onBackToChat && (
                          <button
                            type="button"
                            className={styles.pdfAnnotFooterBtn}
                            onClick={() => {
                              const text = currentDocAnnotations
                                .map(
                                  (a, i) =>
                                    `Clip ${i + 1}:\n${a.quote ? `Quote: "${a.quote}"\n` : ""}${a.note ? `Note: ${a.note}` : ""}`,
                                )
                                .join("\n\n");
                              onBackToChat(`PDF clips:\n\n${text}`);
                            }}
                          >
                            Back to chat
                          </button>
                        )}
                      </div>
                    )}
                  </aside>
                )}
              </div>
            ) : (
              <div className={styles.docEmptyState}>
                <FileText size={48} strokeWidth={1} />
                <h3>No document open</h3>
                <p>Upload a PDF or document to view it alongside your notes.</p>
                <button
                  type="button"
                  className={styles.docUploadBtn}
                  onClick={() => docUploadInputRef.current?.click()}
                >
                  <Plus size={16} />
                  <span>Upload Document</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Inline AI - Morphing pill/bar (single persistent element) */}
      <div
        className={`${styles.inlineAiBar} ${aiBarExpanded || aiPreview ? styles.aiBarOpen : ''} ${!selectionTagMenu ? styles.aiBarHidden : ''}`}
        onClick={!aiBarExpanded && !aiPreview ? () => setAiBarExpanded(true) : undefined}
        role={!aiBarExpanded && !aiPreview ? 'button' : undefined}
        tabIndex={!aiBarExpanded && !aiPreview ? 0 : undefined}
        onKeyDown={!aiBarExpanded && !aiPreview ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setAiBarExpanded(true); }
        } : undefined}
      >
        {/* Icon — spinner when loading, sparkle otherwise */}
        {isAiLoading
          ? <RefreshCw size={16} className={`${styles.inlineAiIcon} ${styles.spin}`} />
          : <Sparkles size={16} className={styles.inlineAiIcon} />
        }

        {/* Collapsed pill label */}
        {!aiBarExpanded && !aiPreview && (
          <span className={styles.aiBarPillLabel}>Ask AI</span>
        )}

        {/* Expanded input + actions — fade in after container has expanded */}
        {aiBarExpanded && !aiPreview && (
          <div className={styles.aiBarExpandedContent}>
            <input
              type="text"
              className={styles.inlineAiInput}
              placeholder="Ask AI to write, explain, brainstorm..."
              value={aiInput}
              autoFocus
              onChange={(e) => setAiInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') { setAiBarExpanded(false); setAiInput(''); }
                if (e.key === 'Enter' && aiInput.trim() && !isAiLoading) {
                  e.preventDefault();
                  const userPrompt = aiInput.trim();
                  setAiInput('');
                  setIsAiLoading(true);
                  setTimeout(() => {
                    const response = `Here's what I found for "${userPrompt}":\n\n• This is a simulated AI response\n• In production, this would be generated by your AI model\n• You can accept to insert this into your notes, or reject to try again`;
                    setAiPreview({ prompt: userPrompt, response });
                    setIsAiLoading(false);
                  }, 1500);
                }
              }}
            />
            <div className={styles.aiQuickActions}>
              <button type="button" className={styles.aiQuickAction} title="Generate Quiz"
                onClick={() => { setIsAiLoading(true); setTimeout(() => { setAiPreview({ prompt: 'quiz', response: `**Quiz: Test Your Knowledge**\n\n1. What is the main concept discussed?\n   a) Option A\n   b) Option B\n   c) Option C\n   d) Option D\n\n2. Which statement is true?\n   a) Statement 1\n   b) Statement 2\n   c) Statement 3\n\n*Answers: 1-b, 2-c*` }); setIsAiLoading(false); }, 1200); }}>
                <CheckSquare size={14} />
              </button>
              <button type="button" className={styles.aiQuickAction} title="Generate Flashcards"
                onClick={() => { setIsAiLoading(true); setTimeout(() => { setAiPreview({ prompt: 'flashcards', response: `**Flashcards**\n\n**Card 1**\nQ: What is the key concept?\nA: The key concept is...\n\n**Card 2**\nQ: Define the main term\nA: It refers to...` }); setIsAiLoading(false); }, 1200); }}>
                <Copy size={14} />
              </button>
              <button type="button" className={styles.aiQuickAction} title="Generate Test"
                onClick={() => { setIsAiLoading(true); setTimeout(() => { setAiPreview({ prompt: 'test', response: `**Practice Test**\n\n**Section A: Multiple Choice (10 pts)**\n1. Question about main topic...\n\n**Section B: Short Answer (15 pts)**\n2. Explain the relationship between...` }); setIsAiLoading(false); }, 1200); }}>
                <FileText size={14} />
              </button>
              <button type="button" className={styles.aiQuickAction} title="Generate Quote"
                onClick={() => { setIsAiLoading(true); setTimeout(() => { setAiPreview({ prompt: 'quote', response: `> "The only way to do great work is to love what you do."\n> — Steve Jobs` }); setIsAiLoading(false); }, 800); }}>
                <Quote size={14} />
              </button>
            </div>
            <button type="button" className={styles.inlineAiSend} disabled={!aiInput.trim()}
              onClick={() => {
                if (!aiInput.trim()) return;
                const userPrompt = aiInput.trim();
                setAiInput('');
                setIsAiLoading(true);
                setTimeout(() => {
                  const response = `Here's what I found for "${userPrompt}":\n\n• This is a simulated AI response\n• In production, this would be generated by your AI model\n• You can accept to insert this into your notes, or reject to try again`;
                  setAiPreview({ prompt: userPrompt, response });
                  setIsAiLoading(false);
                }, 1500);
              }}>
              <Sparkles size={16} />
            </button>
            <button type="button" className={styles.aiBarClose}
              onClick={() => { setAiBarExpanded(false); setAiInput(''); }}>
              <X size={16} />
            </button>
          </div>
        )}

        {/* Preview: accept / reject AI response */}
        {aiPreview && (
          <div className={styles.aiBarExpandedContent}>
            <div className={styles.aiPreviewCard}>
              <div className={styles.aiPreviewHeader}>
                <span className={styles.aiPreviewTitle}>AI Generated</span>
                <span className={styles.aiPreviewPrompt}>"{aiPreview.prompt}"</span>
              </div>
              <div className={styles.aiPreviewContent}>{aiPreview.response}</div>
              <div className={styles.aiPreviewActions}>
                <button type="button" className={styles.aiRejectBtn}
                  onClick={() => { setAiPreview(null); setAiBarExpanded(false); }}>
                  <X size={16} /><span>Reject</span>
                </button>
                <button type="button" className={styles.aiAcceptBtn}
                  onClick={() => {
                    if (!activePage) return;
                    const aiBlocks = buildAiBlocks(aiPreview.response);
                    const updatedBlocks = insertBlocksAfterTarget(activePage.blocks, activeBlockId, aiBlocks);
                    const updatedPage = { ...activePage, blocks: updatedBlocks };
                    setActivePage(updatedPage);
                    setNotebooks(prev => updateItemInTree(prev, activePage.id, updatedPage));
                    setAiPreview(null);
                    setAiBarExpanded(false);
                    const focusBlock = aiBlocks[aiBlocks.length - 1];
                    setTimeout(() => { const el = blockRefs.current[focusBlock?.id]; if (el) el.focus(); }, 50);
                  }}>
                  <Check size={16} /><span>Insert into Notes</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Immersive Focus Mode - Study Workspace */}
      {studyMode && (
        <div className={styles.focusModeOverlay}>
          <div className={styles.focusModeWindow}>
            {/* Top Bar with Tabs */}
            <div className={styles.focusModeTopBar}>
              <div className={styles.focusModeTopLeft}>
                <button className={styles.focusModeSidebarBtn} type="button">
                  <Menu size={18} />
                </button>
                <div className={styles.focusModeFileTab}>
                  <FileText size={14} />
                  <span>{activePage?.title || "Notes"}</span>
                </div>
              </div>
              <div className={styles.focusModeTopCenter}>
                <span className={styles.focusModeTitle}>Focus Mode</span>
              </div>
              <div className={styles.focusModeTopActions}>
                <button
                  className={styles.focusModeTopBtn}
                  onClick={() => {
                    if (document.fullscreenElement) {
                      document.exitFullscreen();
                    } else {
                      document.documentElement.requestFullscreen();
                    }
                  }}
                  type="button"
                  title={
                    document.fullscreenElement
                      ? "Exit Fullscreen"
                      : "Fullscreen"
                  }
                >
                  {document.fullscreenElement ? (
                    <Minimize2 size={16} />
                  ) : (
                    <Maximize2 size={16} />
                  )}
                </button>
                <button
                  className={styles.focusModeExitBtn}
                  onClick={() => {
                    setStudyMode(false);
                    setStudyTimerRunning(false);
                    if (document.fullscreenElement) {
                      document.exitFullscreen();
                    }
                  }}
                  type="button"
                  title="Exit Focus Mode"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Split Content Area */}
            <div className={styles.focusModeContent}>
              {/* Left: Notes Editor (Dark) */}
              <div className={styles.focusModeEditor}>
                <div className={styles.focusModeEditorScroll}>
                  <textarea
                    className={styles.focusModeWritingArea}
                    value={activePage?.blocks?.[0]?.content || ""}
                    onChange={(e) => {
                      if (activePage && activePage.blocks.length > 0) {
                        handleBlockChange(
                          activePage.blocks[0].id,
                          e.target.value,
                        );
                      }
                    }}
                    onMouseUp={(e) => {
                      const textarea = e.target;
                      const start = textarea.selectionStart;
                      const end = textarea.selectionEnd;
                      const selectedText = textarea.value
                        .substring(start, end)
                        .trim();
                      if (selectedText && selectedText.length > 0) {
                        setSelectionTagMenu({
                          text: selectedText,
                          fullText: textarea.value,
                          x: Math.min(e.clientX, window.innerWidth - 400),
                          y: Math.max(e.clientY - 50, 10),
                          start,
                          end,
                          blockId: activePage?.blocks[0]?.id,
                        });
                      } else {
                        setSelectionTagMenu(null);
                      }
                    }}
                    placeholder="Just start typing..."
                    spellCheck="true"
                    autoFocus
                  />
                </div>

                {/* Writing Stats */}
                <div className={styles.focusModeStats}>
                  <div className={styles.focusModeStat}>
                    <Type size={12} />
                    <span>
                      {
                        (activePage?.blocks?.[0]?.content || "")
                          .trim()
                          .split(/\s+/)
                          .filter((w) => w).length
                      }{" "}
                      words
                    </span>
                  </div>
                  <div className={styles.focusModeStat}>
                    <Clock size={12} />
                    <span>
                      {Math.max(
                        1,
                        Math.ceil(
                          (activePage?.blocks?.[0]?.content || "")
                            .trim()
                            .split(/\s+/)
                            .filter((w) => w).length / 200,
                        ),
                      )}{" "}
                      min read
                    </span>
                  </div>
                  <div className={styles.focusModeStatSpacer} />
                  <div className={styles.focusModeHint}>
                    <span>ESC to exit</span>
                  </div>
                </div>
              </div>

              {/* Right: Document Viewer (White/Light) */}
              <div className={styles.focusModeDocument}>
                <div className={styles.focusModeDocTopBar}>
                  <div className={styles.focusModeDocTabs}>
                    {dockDocuments.map((doc) => (
                      <button
                        key={doc.id}
                        className={`${styles.focusModeDocTab} ${activeDockDocId === doc.id ? styles.active : ""}`}
                        type="button"
                        onClick={() => setActiveDockDocId(doc.id)}
                      >
                        <FileText size={14} />
                        <span>
                          {doc.name.length > 20
                            ? doc.name.slice(0, 20) + "..."
                            : doc.name}
                        </span>
                        <button
                          className={styles.focusModeDocTabClose}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (activeDockDocId === doc.id) {
                              const remaining = dockDocuments.filter(
                                (d) => d.id !== doc.id,
                              );
                              setActiveDockDocId(
                                remaining.length > 0 ? remaining[0].id : null,
                              );
                            }
                          }}
                          type="button"
                        >
                          <X size={12} />
                        </button>
                      </button>
                    ))}
                    <button
                      className={styles.focusModeDocTabAdd}
                      onClick={() => docUploadInputRef.current?.click()}
                      type="button"
                      title="Add document"
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                  <div className={styles.focusModeDocActions}>
                    <span className={styles.focusModeDocZoom}>Zoom to Fit</span>
                  </div>
                </div>
                <div className={styles.focusModeDocContent}>
                  {activeDockDocUrl ? (
                    <iframe
                      src={activeDockDocUrl}
                      className={styles.focusModeIframe}
                      title="Document viewer"
                    />
                  ) : (
                    <div className={styles.focusModeDocEmpty}>
                      <FileText size={40} strokeWidth={1} />
                      <h3>No document loaded</h3>
                      <p>
                        Upload a PDF or document to view alongside your notes
                      </p>
                      <button
                        className={styles.focusModeUploadBtn}
                        onClick={() => docUploadInputRef.current?.click()}
                        type="button"
                      >
                        <Plus size={16} />
                        <span>Upload Document</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Pomodoro Timer Panel */}
              {showPomodoro && (
                <div className={styles.pomodoroPanel}>
                  <div className={styles.pomodoroHeader}>
                    <span className={styles.pomodoroTitle}>
                      {pomodoroMode === "work"
                        ? "🎯 Focus"
                        : pomodoroMode === "break"
                          ? "☕ Break"
                          : "🌴 Long Break"}
                    </span>
                    <button
                      className={styles.pomodoroClose}
                      onClick={() => setShowPomodoro(false)}
                      type="button"
                      title="Hide timer"
                    >
                      <X size={14} />
                    </button>
                  </div>

                  <div className={styles.pomodoroTimer}>
                    <div className={styles.pomodoroCircle}>
                      <svg viewBox="0 0 100 100" className={styles.pomodoroSvg}>
                        <circle
                          cx="50"
                          cy="50"
                          r="45"
                          fill="none"
                          stroke="rgba(255,255,255,0.1)"
                          strokeWidth="6"
                        />
                        <circle
                          cx="50"
                          cy="50"
                          r="45"
                          fill="none"
                          stroke={
                            pomodoroMode === "work" ? "#a855f7" : "#22c55e"
                          }
                          strokeWidth="6"
                          strokeLinecap="round"
                          strokeDasharray={283}
                          strokeDashoffset={
                            283 -
                            283 *
                              (1 -
                                studyTimer /
                                  (pomodoroMode === "work"
                                    ? pomodoroSettings.workDuration * 60
                                    : pomodoroMode === "break"
                                      ? pomodoroSettings.breakDuration * 60
                                      : pomodoroSettings.longBreakDuration *
                                        60))
                          }
                          transform="rotate(-90 50 50)"
                          style={{ transition: "stroke-dashoffset 1s linear" }}
                        />
                      </svg>
                      <div className={styles.pomodoroTime}>
                        {String(Math.floor(studyTimer / 60)).padStart(2, "0")}:
                        {String(studyTimer % 60).padStart(2, "0")}
                      </div>
                    </div>
                  </div>

                  <div className={styles.pomodoroControls}>
                    <button
                      className={styles.pomodoroBtn}
                      onClick={() => setStudyTimerRunning(!studyTimerRunning)}
                      type="button"
                    >
                      {studyTimerRunning ? (
                        <Pause size={18} />
                      ) : (
                        <Play size={18} />
                      )}
                    </button>
                    <button
                      className={styles.pomodoroBtn}
                      onClick={() => {
                        setStudyTimerRunning(false);
                        setStudyTimer(pomodoroSettings.workDuration * 60);
                        setPomodoroMode("work");
                      }}
                      type="button"
                      title="Reset"
                    >
                      <RotateCcw size={16} />
                    </button>
                  </div>

                  <div className={styles.pomodoroSessions}>
                    <span>
                      Session {pomodoroSession} of{" "}
                      {pomodoroSettings.sessionsBeforeLongBreak}
                    </span>
                    <div className={styles.pomodoroDots}>
                      {[...Array(pomodoroSettings.sessionsBeforeLongBreak)].map(
                        (_, i) => (
                          <div
                            key={i}
                            className={`${styles.pomodoroDot} ${i < ((pomodoroSession - 1) % pomodoroSettings.sessionsBeforeLongBreak) + (pomodoroMode !== "work" ? 1 : 0) ? styles.completed : ""}`}
                          />
                        ),
                      )}
                    </div>
                  </div>

                  <div className={styles.pomodoroModes}>
                    <button
                      className={`${styles.pomodoroModeBtn} ${pomodoroMode === "work" ? styles.active : ""}`}
                      onClick={() => {
                        setPomodoroMode("work");
                        setStudyTimer(pomodoroSettings.workDuration * 60);
                        setStudyTimerRunning(false);
                      }}
                      type="button"
                    >
                      Focus
                    </button>
                    <button
                      className={`${styles.pomodoroModeBtn} ${pomodoroMode === "break" ? styles.active : ""}`}
                      onClick={() => {
                        setPomodoroMode("break");
                        setStudyTimer(pomodoroSettings.breakDuration * 60);
                        setStudyTimerRunning(false);
                      }}
                      type="button"
                    >
                      Break
                    </button>
                    <button
                      className={`${styles.pomodoroModeBtn} ${pomodoroMode === "longBreak" ? styles.active : ""}`}
                      onClick={() => {
                        setPomodoroMode("longBreak");
                        setStudyTimer(pomodoroSettings.longBreakDuration * 60);
                        setStudyTimerRunning(false);
                      }}
                      type="button"
                    >
                      Long
                    </button>
                  </div>
                </div>
              )}

              {/* Pomodoro Toggle (when hidden) */}
              {!showPomodoro && (
                <button
                  className={styles.pomodoroToggle}
                  onClick={() => setShowPomodoro(true)}
                  type="button"
                  title="Show Pomodoro Timer"
                >
                  <Clock size={18} />
                </button>
              )}
            </div>

            {/* AI Chat — same thread as dock (Notebook AI tab) */}
            {aiDockMessages.length > 0 && (
              <div className={styles.focusModeAiChat}>
                {aiDockMessages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`${styles.focusModeAiMessage} ${styles[msg.role]}`}
                  >
                    <div className={styles.focusModeAiMessageContent}>
                      {msg.role === "assistant" && <Sparkles size={14} />}
                      <span>{msg.content}</span>
                    </div>
                  </div>
                ))}
                {isAiLoading && (
                  <div
                    className={`${styles.focusModeAiMessage} ${styles.assistant}`}
                  >
                    <div className={styles.focusModeAiMessageContent}>
                      <Sparkles size={14} className={styles.spin} />
                      <span>Thinking...</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Bottom AI Bar - Like Prism */}
            <div className={styles.focusModeAiBar}>
              <input
                type="text"
                placeholder="Ask anything..."
                className={styles.focusModeAiInput}
                value={aiInput}
                onChange={(e) => setAiInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && aiInput.trim()) {
                    e.preventDefault();
                    handleAiChat(aiInput);
                    setAiInput("");
                  }
                }}
              />
              <div className={styles.focusModeAiActions}>
                <button
                  className={styles.focusModeAiBtn}
                  type="button"
                  title="Attach"
                >
                  <Plus size={18} />
                </button>
                <button
                  className={styles.focusModeAiBtn}
                  type="button"
                  title="Voice"
                >
                  <Zap size={18} />
                </button>
                <button
                  className={styles.focusModeAiSend}
                  type="button"
                  title="Send"
                  onClick={() => {
                    if (aiInput.trim()) {
                      handleAiChat(aiInput);
                      setAiInput("");
                    }
                  }}
                >
                  <Send size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Command Palette (⌘+K) */}
      {showCommandPalette && (
        <div
          className={styles.commandPaletteOverlay}
          onClick={() => setShowCommandPalette(false)}
        >
          <div
            className={styles.commandPalette}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.commandPaletteHeader}>
              <Search size={18} />
              <input
                ref={commandInputRef}
                type="text"
                className={styles.commandPaletteInput}
                placeholder="Search notes, actions, or type a command..."
                value={commandQuery}
                onChange={(e) => setCommandQuery(e.target.value)}
                autoFocus
              />
              <kbd className={styles.commandPaletteKbd}>ESC</kbd>
            </div>
            <div className={styles.commandPaletteContent}>
              <div className={styles.commandPaletteSection}>
                <div className={styles.commandPaletteSectionTitle}>
                  Quick Actions
                </div>
                <button
                  className={styles.commandPaletteItem}
                  onClick={() => {
                    handleCreatePage("root");
                    setShowCommandPalette(false);
                  }}
                >
                  <Plus size={16} />
                  <span>New note</span>
                  <kbd>⌘N</kbd>
                </button>
                <button
                  className={styles.commandPaletteItem}
                  onClick={() => {
                    if (activePage) {
                      setStudyMode(true);
                    }
                    setShowCommandPalette(false);
                  }}
                >
                  <Maximize2 size={16} />
                  <span>Enter focus mode</span>
                  <kbd>⌘⇧F</kbd>
                </button>
                <button
                  className={styles.commandPaletteItem}
                  onClick={() => {
                    toggleTheme();
                    setShowCommandPalette(false);
                  }}
                >
                  {darkMode ? <Sun size={16} /> : <Moon size={16} />}
                  <span>Toggle theme</span>
                  <kbd>⌘D</kbd>
                </button>
              </div>
              {commandQuery && (
                <div className={styles.commandPaletteSection}>
                  <div className={styles.commandPaletteSectionTitle}>Notes</div>
                  {notebooks
                    .flatMap((n) => n.children || [])
                    .filter((p) =>
                      p.title
                        .toLowerCase()
                        .includes(commandQuery.toLowerCase()),
                    )
                    .slice(0, 5)
                    .map((page) => (
                      <button
                        key={page.id}
                        className={styles.commandPaletteItem}
                        onClick={() => {
                          handlePageSelect(page);
                          setShowCommandPalette(false);
                        }}
                      >
                        <FileText size={16} />
                        <span>{page.title}</span>
                      </button>
                    ))}
                </div>
              )}
            </div>
            <div className={styles.commandPaletteFooter}>
              <span>↑↓ Navigate</span>
              <span>↵ Select</span>
              <span>ESC Close</span>
            </div>
          </div>
        </div>
      )}

      {/* Selection Toolbar (Global) */}
      {selectionTagMenu && (
        <div
          className={styles.selectionToolbar}
          style={{ 
            left: selectionTagMenu.x,
            top: selectionTagMenu.y - 50
          }}
          onMouseDown={(e) => e.preventDefault()}
        >
          {/* Simplified Formatting - essential options for students */}
          <div className={styles.selectionToolbarSection}>
            <button
              className={styles.selectionToolbarBtn}
              onClick={() => applyTextFormat("bold")}
              title="Bold"
            >
              <strong>B</strong>
            </button>
            <button
              className={styles.selectionToolbarBtn}
              onClick={() => applyTextFormat("italic")}
              title="Italic"
            >
              <em>I</em>
            </button>
            <button
              className={styles.selectionToolbarBtn}
              onClick={() => applyTextFormat("highlight-yellow")}
              title="Highlight"
            >
              <Highlighter size={14} />
            </button>
          </div>
          <div className={styles.selectionToolbarDivider} />

          {/* AI Actions - simplified for students */}
          <div className={styles.selectionToolbarSection}>
            <button
              className={`${styles.selectionToolbarBtn} ${styles.aiBtn}`}
              onClick={() => {
                handleAiChat(`Explain this simply: "${selectionTagMenu.text}"`);
                setShowAiPanel(true);
                setSelectionTagMenu(null);
              }}
              title="Explain"
            >
              <Sparkles size={14} />
              <span>Explain</span>
            </button>
            <button
              className={`${styles.selectionToolbarBtn} ${styles.aiBtn}`}
              onClick={() => {
                handleAiChat(`Summarize this: "${selectionTagMenu.text}"`);
                setShowAiPanel(true);
                setSelectionTagMenu(null);
              }}
              title="Summarize"
            >
              <Sparkles size={14} />
              <span>Summarize</span>
            </button>
          </div>
          <div className={styles.selectionToolbarDivider} />

          {/* Essential Actions */}
          <div className={styles.selectionToolbarSection}>
            <button
              className={styles.selectionToolbarBtn}
              onClick={() => {
                navigator.clipboard.writeText(selectionTagMenu.text);
                setSelectionTagMenu(null);
              }}
              title="Copy"
            >
              <Copy size={14} />
            </button>
          </div>
          <button
            className={styles.selectionToolbarClose}
            onClick={() => setSelectionTagMenu(null)}
          >
            <X size={12} />
          </button>
        </div>
      )}
    </div>
  );
};

export default NotebookPage;
